import * as i1$1 from '@ngrx/store';
import { createAction, props, createReducer, on, createFeatureSelector, createSelector, StoreModule } from '@ngrx/store';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, APP_INITIALIZER, NgModule, Optional } from '@angular/core';
import * as i1 from '@panamax/browser-storage';
import { MessageTypes } from '@panamax/browser-storage';
import { combineLatest, throwError, of, Observable } from 'rxjs';
import { filter, switchMap, take, catchError, map, withLatestFrom, concatMap, mergeMap, distinctUntilChanged, first, tap, finalize } from 'rxjs/operators';
import * as i3 from '@panamax/app-state';
import { selectCustomer, selectLoggedInUser, retryBackoff } from '@panamax/app-state';
import { createEntityAdapter } from '@ngrx/entity';
import { ServiceRequestType, SousStatus, ListMessageStrings, ListMessageType, ListEventActions, ListGroupEventActions, ListItemEventActions } from '@usf/list-types';
import * as i1$2 from '@angular/common/http';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import * as i1$3 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import * as i1$4 from '@panamax/websocket';
import dayjs from 'dayjs';

const LIST_ACTIONS = {
    // SYNCHRONOUS ACTIONS
    loadFromStorage: createAction('[NgRxList - List] Load lists from storage'),
    loadFromStorageSuccess: createAction('[NgRxList - List] Load from storage succeeded', props()),
    loadFromStorageFail: createAction('[NgRxList - List] Load from storage failed', props()),
    writeToOfflineStorage: createAction('[NgRxList - List] Write to offline storage'),
    load: createAction('[NgRxList - List] Load lists'),
    loadSuccess: createAction('[NgRxList - List] Load lists succeeded', props()),
    loadFail: createAction('[NgRxList - List] Load lists failed', props()),
    loadChanges: createAction('[NgRxList - List] Load "My Lists" changes'),
    loadChangesSuccess: createAction('[NgRxList - List] Load "My Lists" changes succeeded'),
    loadChangesFail: createAction('[NgRxList - List] Load "My Lists" changes failed', props()),
    loadProductIds: createAction('[NgRxList - List] Load product IDs', props()),
    loadProductIdsSuccess: createAction('[NgRxList - List] Load product IDs succeeded', props()),
    resetStore: createAction('[NgRxList - List] Reset lists in store'),
    domainCallSuccess: createAction('[NgRxList - List] Domain API call succeeded', props()),
    domainCallFail: createAction('[NgRxList - List] Domain API Call failed', props()),
    create: createAction('[NGRXTrace- List] Create list', props()),
    createSuccess: createAction('[NGRXTrace - List] Create list succeeded', props()),
    createFail: createAction('[NGRXTrace - List] Create list failed', props()),
    delete: createAction('[NGRXTrace - List] Delete list', props()),
    deleteSuccess: createAction('[NgRxList - List] Delete list succeeded', props()),
    deleteFail: createAction('[NgRxList - List] Delete list failed', props()),
    removeNegativeIdLists: createAction('[NgRxList - List] Remove negative ID lists', props()),
    editListType: createAction('[NGRXTrace - List] Edit list type', props()),
    editListTypeSuccess: createAction('[NGRXTrace - List] Edit list type succeeded', props()),
    editListTypeFail: createAction('[NGRXTrace - List] Edit list type failed', props()),
    editListName: createAction('[NGRXTrace - List] Edit list name', props()),
    editListNameSuccess: createAction('[NGRXTrace - List] Edit list name succeeded', props()),
    editListNameFail: createAction('[NGRXTrace - List] Edit list name failed', props()),
    updateLastSelectedList: createAction('[NgRxList - List] Update last selected list', props()),
    updateList: createAction('[NgRxList - List] Upsert List', props()),
    updateListOfListsSort: createAction('[NgRxList - List] Update sort direction of selected header and reset other headers to default values in given listOfListsSort section', props()),
    resetListOfListsSort: createAction('[NgRxInventory - Inventory] Resets listOfListsSort to its default value'),
    editGroupSequence: createAction('[NGRXTrace - List] Edit group sequence', props()),
    editGroupSequenceSuccess: createAction('[NGRXTrace - List] Edit group sequence succeeded', props()),
    editGroupSequenceFail: createAction('[NGRXTrace - List] Edit group sequence failed', props()),
    deleteGroupAndDelete: createAction('[NGRXTrace - List] Delete group and all items in it', props()),
    deleteGroupAndDeleteSuccess: createAction('[NgRxList - List] Delete group and all items in it succeeded', props()),
    deleteGroupAndMove: createAction('[NGRXTrace - List] Delete group and move all items to unassigned group', props()),
    deleteGroupAndMoveSuccess: createAction('[NGRXTrace - List] Delete group and move all items to unassigned group succeeded', props()),
    deleteGroupFail: createAction('[NGRXTrace - List] Delete group failed', props()),
    changeSelectedGroup: createAction('[NgRxList Change Selected Group] Update currently selected group in store', props()),
    selectAList: createAction('[NgRxList Select A List] Set is selected for a list', props()),
    productsFailedToLoad: createAction('[NgRxTrace - List] Products failed to load on list', props()),
    downloadList: createAction('[NgRxTrace - List] Download List', props()),
    copy: createAction('[NgRxTrace - List] Copy List', props()),
    copySuccess: createAction('[NgRxTrace - List] Copy List Success', props()),
    copyFail: createAction('[NgRxTrace - List] Copy List Fail', props()),
    massDelete: createAction('[NgRxTrace - List] Mass Delete', props()),
    massDeleteSuccess: createAction('[NgRxTrace - List] Mass Delete Success', props()),
    massDeleteFail: createAction('[NgRxTrace - List] Mass Delete Fail', props()),
    // END OF SYNCHRONOUS ACTIONS
    /**
     * WEBSOCKET/ASYNC ACTIONS
     *
     * @Remarks
     * The following actions are in response to CDC Events or GetMyChanges
     */
    ecomR4UpdateListsInStore: createAction('[NGRXTrace - List] Update lists from r4 websocket event', props()),
    createdAsync: createAction('[NgRxList - List] Create list asynchronously', props()),
    updatedAsync: createAction('[NgRxList - List] Update list asynchronously', props()),
    deletedAsync: createAction('[NGRXTrace- List] Delete list asynchronously', props()),
    deletedAsyncSuccess: createAction('[NGRXTrace - List] Delete list asynchronously succeeded', props()),
    deletedAsyncFail: createAction('[NGRXTrace - List] Delete Lists Event Failed', props()),
    updateInStore: createAction('[NGRXTrace - List] Update lists in store', props()),
    updateInStorage: createAction('[NgRxList - List] Update lists in offline storage', props()),
    updateMany: createAction('[NgRxList - List] Generic upsert many', props()),
    initializeStorage: createAction('[NgRxList - List] Initialize offline storage'),
    listsPageLoad: createAction('[NgRxList - List] Lists page load'),
    searchInList: createAction('[NGRXTrace - List] Search in list', props()),
    searchInListSuccess: createAction('[NgRxTrace - List] Search in list succeeded', props()),
    sortAndFilterList: createAction('[NgRxList - List] Sort and filter in list', props()),
    endListAction: createAction('[NGRXTrace - List] End current list action', props()),
    websocketMessageReceivedAction: createAction('[NGRXTrace - List] Receive websocked message action', props()),
    displayListDetails: createAction('[NGRXTrace - List] Display list details', props()),
    displayListDetailsSuccess: createAction('[NGRXTrace - List] Display list details succeeded', props()),
    displayListDetailsFailure: createAction('[NGRXTrace - List] Display list details failure', props()),
    betterBuyAccept: createAction('[NgRxList - List] Accept Better Buy Swap', props()),
    betterBuyAcceptSuccess: createAction('[NgRxList - List] Accept Better Buy Swap Success', props()),
    betterBuyAcceptFail: createAction('[NgRxList - List] Accept Better Buy Swap Fail', props()),
    betterBuyDecline: createAction('[NgRxList - List] Decline Better Buy Swap', props()),
    betterBuyDeclineSuccess: createAction('[NgRxList - List] Decline Better Buy Swap Success', props()),
    betterBuyDeclineFail: createAction('[NgRxList - List] Decline Better Buy Swap Fail', props()),
    requestDownloadTemplate: createAction('[NgRxList - List] Template Request'),
    importList: createAction('[NgRxList - List] Import List', props()),
    importListSuccess: createAction('[NgRxList - List] Import List Success', props()),
    importListFail: createAction('[NgRxList - List] Import List Failure', props()),
    removeImportError: createAction('[NgRxList - List] Import List Error Removal', props()),
    getCustomersWithListName: createAction('[NgRxList - List] Find Customers Using A Shared List Name', props()),
    getCustomersWithListNameSuccess: createAction('[NgRxList - List] Find Customers With The Same List Success', props()),
    getCustomersWithListNameFail: createAction('[NgRxList - List] Find Customers With The Same List Fail', props()),
};

const LIST_GROUP_ACTIONS = {
    loadFromStorage: createAction('[NgRxList - ListGroups] Load ListGroups From Storage'),
    loadFromStorageSuccess: createAction('[NgRxList - ListGroup] Loaded From Storage Successfully', props()),
    loadFromStorageFail: createAction('[NgRxList - ListGroup] Failed to Load From Browser Storage', props()),
    writeToOfflineStorage: createAction('[NgRxList - ListGroup] Write To Offline Storage'),
    load: createAction('[NgRxList - ListGroup] Load List Groups'),
    loadSuccess: createAction('[NgRxList - ListGroup] All Groups Loaded', props()),
    loadFail: createAction('[NgRxList - ListGroup] Loading List Groups Failed', props()),
    loadChanges: createAction('[NgRxList - ListGroup] Get My List Group Changes'),
    loadChangesSuccess: createAction('[NgRxList - ListGroup] Get My List Group Changes Success'),
    loadChangesFail: createAction('[NgRxList - ListGroup] Get My List Group Changes Failed', props()),
    resetStore: createAction('[NgRxList - ListGroup] Reset listgroups in store'),
    create: createAction('[NGRXTrace - ListGroup] Create group', props()),
    createSuccess: createAction('[NGRXTrace - ListGroup] Create group success', props()),
    createFail: createAction('[NGRXTrace - ListGroup] Create group fail', props()),
    update: createAction('[NgRxList - ListGroup] Update Group', props()),
    delete: createAction('[NgRxList - ListGroup] Delete Group', props()),
    removeNegativeIdGroups: createAction('[NgRxList - ListGroup] Remove negative id groups', props()),
    moveItemsWithinGroupFromLineNumber: createAction('[NGRXTrace - ListGroup] - Move item within group from line number', props()),
    moveItemsWithinGroupFromDragAndDrop: createAction('[NGRXTrace - ListGroup] - Move item within group from drag and drop', props()),
    moveItemsWithinGroupSuccess: createAction('[NGRXTrace - ListGroup] - Move item within group success', props()),
    moveItemsWithinGroupFail: createAction('[NGRXTrace - ListGroup] - Move item within group fail', props()),
    moveItemsToAnotherGroupFromLineNumber: createAction('[NGRXTrace - ListGroup] - Move items to another group from line number', props()),
    moveItemsToAnotherGroupFromDragAndDrop: createAction('[NGRXTrace - ListGroup] - Move items to another group from drag and drop', props()),
    moveItemsToAnotherGroupFromModal: createAction('[NGRXTrace - ListGroup] - Move items to another group from modal', props()),
    moveItemsToAnotherGroupSuccess: createAction('[NGRXTrace - ListGroup] - Move items to another group success', props()),
    moveItemsToAnotherGroupFail: createAction('[NGRXTrace - ListGroup] - Move items to another group fail', props()),
    editGroupName: createAction('[NGRXTrace - ListGroup] - Edit group name', props()),
    editGroupNameSuccess: createAction('[NGRXTrace - ListGroup] - Edit group name success', props()),
    editGroupNameFail: createAction('[NGRXTrace - ListGroup] - Edit group name fail', props()),
    updateMany: createAction('[NgRxList - ListGroup] - Update many groups', props()),
    // END OF SYNCHRONOUS ACTIONS
    // ASYNC ACTIONS
    // CDC events, EcomR4 Events, GetMyChanges
    createdAsync: createAction('[NgRxList - ListGroup] Create', props()),
    updatedAsync: createAction('[NgRxList - ListGroup] Update', props()),
    deletedAsync: createAction('[NgRxList - ListGroup] - Delete', props()),
    deletedAsyncSuccess: createAction('[NGRXTrace - ListGroup] Deleted Success', props()),
    deletedAsyncFail: createAction('[NGRXTrace - ListGroup] Delete List Groups Event Failed', props()),
    updateInStore: createAction('[NGRXTrace - List Groups] Update List Groups in Store', props()),
    updateInStorage: createAction('[NGRXTrace - ListGroup] Update List Groups in Offline Storage', props()),
    updateListItemKeys: createAction('[NgRxList - List Groups] Update ListItemKeys for a group in Store', props()),
    removeListItemKeys: createAction('[NgRxList - List Groups] Remove ListItemKeys for a group in Store', props()),
};

const LIST_ITEM_ACTIONS = {
    loadFromStorage: createAction('[NgRxList - ListItem] Load ListItems From Storage'),
    loadFromStorageSuccess: createAction('[NgRxList - ListItem] ListItems Loaded From Storage Successfully', props()),
    loadFromStorageFail: createAction('[NgRxList - ListItem] Failed to Load From Browser Storage', props()),
    writeToOfflineStorage: createAction('[NgRxList - ListItem] Write To Offline Storage'),
    load: createAction('[NgRxList - ListItem] Load List Items'),
    loadSuccess: createAction('[NgRxList - ListItem] All List Items Loaded', props()),
    loadFail: createAction('[NgRxList - ListItem] Load List Items Failed', props()),
    loadChanges: createAction('[NgRxList - ListItem] Load My List Item Changes'),
    loadChangesSuccess: createAction('[NgRxList - ListItem] Load My List Item Changes Success'),
    loadChangesFail: createAction('[NgRxList - ListItem] Load List Item Changes Failed', props()),
    resetStore: createAction('[NgRxList - ListItem] Reset ListItems in store'),
    setIsSelected: createAction('[NgRxList - ListItem] Set isSelected attribute on List Items', props()),
    update: createAction('[NgRxList - ListItem] Set group key to list items new group', props()),
    deleteInStore: createAction('[NgRxList - ListItem] Remove list items from store', props()),
    replaceProductInAList: createAction('[NGRXTrace - ListItem] Replace product number for list item', props()),
    replaceProductAcrossAllLists: createAction('[NGRXTrace - ListItem] Replace product number for list item across lists', props()),
    replaceProductSuccess: createAction('[NGRXTrace - ListItem] Replace product number for list item success', props()),
    replaceProductFail: createAction('[NGRXTrace - ListItem] Replace product number in list items fail', props()),
    create: createAction('[NGRXTrace - ListItem] Create listItem(s) on list', props()),
    createSuccess: createAction('[NGRXTrace - ListItem] Create ListItem(s) on list success', props()),
    createFail: createAction('[NGRXTrace - ListItem] Create ListItem(s) on list fail', props()),
    removeItems: createAction('[NgRxList - ListItem] Remove ListItems from state', props()),
    delete: createAction('[NGRXTrace - ListItem] Delete listItems', props()),
    deleteSuccess: createAction('[NGRXTrace - ListItem] Delete listitems success', props()),
    deleteFail: createAction('[NGRXTrace - ListItem] Delete listitems fail', props()),
    copy: createAction('[NGRXTrace - ListItem] Copy listitem(s)', props()),
    copySuccess: createAction('[NGRXTrace - ListItem] Copy listItem(s) success', props()),
    copyFail: createAction('[NGRXTrace - ListItem] Copy listItem(s) fail', props()),
    createNote: createAction('[NgRxList - ListItem] Create note', props()),
    createNoteSuccess: createAction('[NgRxList - ListItem] Create note success', props()),
    createNoteFail: createAction('[NgRxList - ListItem] Create note fail', props()),
    updateNote: createAction('[NgRxList - ListItem] Update note', props()),
    updateNoteSuccess: createAction('[NgRxList - ListItem] Update note success', props()),
    updateNoteFail: createAction('[NgRxList - ListItem] Update note fail', props()),
    deleteNote: createAction('[NgRxList - ListItem] Delete note', props()),
    deleteNoteSuccess: createAction('[NgRxList - ListItem] Delete note success', props()),
    deleteNoteFail: createAction('[NgRxList - ListItem] Delete note fail', props()),
    createTempItem: createAction('[NgRxList - ListItem] Inserts a temporary item in the state', props()),
    saveProductMap: createAction('[NgRxList - ListItem] Saves map of product numbers for list items', props()),
    /**
     * WEBSOCKET ACTIONS
     *
     * @Remarks
     * Responds to CDC Events GetMyChanges
     */
    updateInStore: createAction('[NGRXTrace - ListItem] Update in Store', props()),
    updateInStorage: createAction('[NGRXTrace - ListItem] Update in Offline Storage', props()),
    createdAsync: createAction('[NgRxList - ListItem] Create Lists', props()),
    updatedAsync: createAction('[NgRxList - ListItem] Update Lists', props()),
    deletedAsync: createAction('[NgRxList - ListItem] Delete', props()),
};

const LIST_UPDATES_ACTIONS = {
    load: createAction('[NgRxList - List Updates] Load List Updates data from Domain Api'),
    loadSuccess: createAction('[NgRxList - List Updates] List Updates data from Domain Api loaded successfully', props()),
    loadFail: createAction('[NgRxList - List Updates] Failed to load List Updates data from Domain Api', props()),
};

const RECENT_PURCHASE_ACTIONS = {
    loadFromStorage: createAction('[NgRxList - RecentPurchase] Load From Storage'),
    loadFromStorageSuccess: createAction('[NgRxList - RecentPurchase] Loaded From Storage Successfully', props()),
    loadFromStorageFail: createAction('[NgRxList - RecentPurchase] Failed to Load From Storage', props()),
    loadRecentPurchase: createAction('[NgRxList - RecentPurchase] Load From Domain Api'),
    loadRecentPurchaseSuccess: createAction('[NgRxList - RecentPurchase] Loaded From Domain Api Successfully', props()),
    loadRecentPurchaseFail: createAction('[NgRxList - RecentPurchase] Failed to Load From Domain Api', props()),
    writeToOfflineStorage: createAction('[NgRxList - RecentPurchase] Write To Offline Storage'),
    resetStore: createAction('[NgRxList - RecentPurchase] Reset recent purchase in store'),
    searchInRecentPurchase: createAction('[NgRxList - RecentPurchase] - Search In Recent Purchase', props()),
    sortAndFilterRecentPurchase: createAction('[NgRxList - RecentPurchase] - Sort and Filter in Recent Purchase', props()),
    setIsSelectedForARecentPurchase: createAction('[NgRxList - RecentPurchase] - Set is selected for a recent purchase product', props()),
    setIsSelectedForMultipleRecentPurchases: createAction('[NgRxList - RecentPurchase] - Set is selected for a multiple recent purchase products', props()),
    setIsSelectedForAllRecentPurchases: createAction('[NgRxList - RecentPurchase] - Set is selected for all recent purchase products', props()),
    selectAList: createAction('[NgRxList - RecentPurchase] Set is selected for the recent-purchase-list', props()),
    downloadList: createAction('[NgRxTrace - RecentPurchase] Download List', props()),
    delete: createAction('[NgRxTrace - RecentPurchase] Delete products', props()),
    deleteSuccess: createAction('[NgRxTrace - RecentPurchase] Delete products success', props()),
    deleteFail: createAction('[NgRxTrace - RecentPurchase] Delete products failure', props()),
};

const SELLER_RECENT_PURCHASE_ACTIONS = {
    loadRecentPurchase: createAction('[NgRxList - Seller Recent Purchase] Load Recent Purchase From Domain Api', props()),
    loadRecentPurchaseSuccess: createAction('[NgRxList - Seller Recent Purchase] Recent Purchase From Domain Api Loaded Successfully', props()),
    loadRecentPurchaseFail: createAction('[NgRxList - Seller Recent Purchase] Failed to Load Recent Purchase From Domain Api', props()),
};

const SERVICE_REQUEST_ACTIONS = {
    load: createAction('[NgRxList - Service Request] Load Service Request data from Domain Api'),
    loadSuccess: createAction('[NgRxList - Service Request] Service Request data from Domain Api loaded successfully', props()),
    loadFail: createAction('[NgRxList - Service Request] Failed to load Service Request data from Domain Api', props()),
    submitRequestCredit: createAction('[NgRxList - Service Request] Submit Request Credit to Domain Api', props()),
    submitRequestCreditSuccess: createAction('[NGRXTrace - Service Request] Successfully submitted Request Credit to Domain Api', props()),
    submitRequestCreditFailure: createAction('[NGRXTrace - Service Request] Failed to submit Request Credit', props()),
    submitPickupRequest: createAction('[NgRxList - Pickup Request] Submit Pickup Request to Domain Api', props()),
    submitPickupRequestSuccess: createAction('[NGRXTrace - Pickup Request] Successfully submitted Pickup Request to Domain Api', props()),
    submitPickupRequestFailure: createAction('[NGRXTrace - Pickup Request] Failed to submit Pickup Request', props()),
    resubmit: createAction('[NgRxList - Service Request] resubmit for serviceRequest', props()),
    resubmitSuccess: createAction('[NgRxList - Service Requeest] resubmit success for serviceRequest', props()),
    resubmitFail: createAction('[NgRxList - Service Request] resubmit fail for serviceRequest', props()),
    submitRecoveryOrder: createAction('[NgRxList - Service Request] Submit Recovery Order Request to Domain Api', props()),
    submitRecoveryOrderSuccess: createAction('[NgRxList - Service Request] Successfully submitted Recovery Order Request to Domain Api', props()),
    submitRecoveryOrderFailure: createAction('[NgRxList - Service Request] Failed to submit Recovery Order Request', props()),
};

const MASTER_LIST_ACTIONS = {
    load: createAction('[NgRxList - Master List] Load From Domain Api'),
    loadSuccess: createAction('[NgRxList - Master List] Load From Domain Api Success', props()),
    loadFail: createAction('[NgRxList - Master List] Load From Domain Api Fail', props()),
    updateMany: createAction('[NGRXList - Master List] Update Many Lists', props()),
    resetStore: createAction('[NgRxList - Master List] Reset Master List State'),
    searchInMasterList: createAction('[NGRXTrace - Master List] - Search In Master List', props()),
    searchInMasterListSuccess: createAction('[NgRxTrace - Master List] - Search In Master List Success', props()),
    sortAndFilterMasterList: createAction('[NgRxList - Master List] - Sort and Filter in Master List', props()),
    selectAList: createAction('[NgRxList Select A Master List] Set is selected for a master list', props()),
    downloadMasterList: createAction('[NgRxTrace - Master List] Download Master List', props()),
};

const MASTER_LIST_GROUP_ACTIONS = {
    load: createAction('[NgRxList - MasterListGroups] Load From Domain Api'),
    loadSuccess: createAction('[NgRxList - Master MasterListGroups] Load From Domain Api Success', props()),
    loadFail: createAction('[NgRxList - MasterListGroups] Load From Domain Api Fail', props()),
    resetStore: createAction('[NgRxList - MasterListGroups] Reset Master List Group State'),
};

const MASTER_LIST_ITEM_ACTIONS = {
    load: createAction('[NgRxList - MasterListItemProducts] Load From Domain Api'),
    loadSuccess: createAction('[NgRxList - MasterListItemProducts] Load From Domain Api Success', props()),
    loadFail: createAction('[NgRxList - MasterListItemProducts] Load From Domain Api Fail', props()),
    resetStore: createAction('[NgRxList - MasterListItemProducts] Reset Master List Product State'),
    setIsShowingAlternates: createAction('[NgRxList - MasterListItemProducts] Set isShowingAlternate for listItemProduct', props()),
    updateItemKeyProductKeysDict: createAction('[NgRxList - MasterListItemProducts] Update itemKeyProductKeysDict in State', props()),
    saveProductMap: createAction('[NgRxList - MasterListItemProducts] Saves map of product numbers for master list items', props()),
    setLoadedAndLoading: createAction('[NgRxList - MasterListItemProducts] Set loaded and loading, is done after client does primary/secondary calculations', props()),
    setIsComparingWithAlternates: createAction('[NgRxList - MasterListItemProducts] Sets isComparingWithAlternates for list item', props()),
    cancelComparingWithAlternates: createAction('[NgRxList - MasterListItemProducts] Cancel isComparingWithAlternates for all list items'),
    setIsSelected: createAction('[NgRxList - MasterListItemProducts] Set selection for a list item', props()),
    resetSelections: createAction('[NgRxList - MasterListItemProducts] Reset selections for all list items'),
};

const ORDER_GUIDE_ACTIONS = {
    load: createAction('[NgRxList - OrderGuide] Load OrderGuides'),
    loadSuccess: createAction('[NgRxList - OrderGuide] Load OrderGuides success', props()),
    loadFail: createAction('[NgRxList - OrderGuide] Load OrderGuides fail', props()),
    resetStore: createAction('[NgRxList - OrderGuide] Reset order guides in store'),
    selectAList: createAction('[NgRxList - OrderGuide] Set is selected for an orderguide', props()),
};

const ORDER_GUIDE_GROUP_ACTIONS = {
    load: createAction('[NgRxList - OrderGuideGroup] Load OrderGuide Groups'),
    loadSuccess: createAction('[NgRxList - OrderGuideGroup] Load OrderGuide Groups success', props()),
    loadFail: createAction('[NgRxList - OrderGuideGroup] Load OrderGuide Groups fail', props()),
    resetStore: createAction('[NgRxList - OrderGuideGroup] Reset order guide groups in store'),
};

const ORDER_GUIDE_ITEM_ACTIONS = {
    load: createAction('[NgRxList - OrderGuideItem] Load OrderGuide Items'),
    loadSuccess: createAction('[NgRxList - OrderGuideItem] Load OrderGuide Items success', props()),
    loadFail: createAction('[NgRxList - OrderGuideItem] Load OrderGuide Items fail', props()),
    resetStore: createAction('[NgRxList - OrderGuideItem] Reset order guide items in store'),
    saveProductMap: createAction('[NgRxList - OrderGuideItem] Saves map of product numbers for order guide items', props()),
};

const SUPPORT_CENTER_ACTIONS = {
    load: createAction('[NgRxList - Support Center] Load Support Center data from Domain Api'),
    loadSuccess: createAction('[NgRxList - Support Center] Support Center data from Domain Api loaded successfully', props()),
    loadFail: createAction('[NgRxList - Support Center] Failed to load Support Center data from Domain Api', props()),
    updateFilter: createAction('[NgRxList - Support Center] Update filter for cards', props()),
    resetStore: createAction('[NgRxList - Support Center] reset support center store'),
    updateSort: createAction('[NgRxList - Support Center] Update the selected sort for the support center page', props()),
    submitSupportRequest: createAction('[NgRxList - Support Center] Call API to submit a new support request', props()),
    submitSupportRequestSuccess: createAction('[NgRxList - Support Center] Successful support request submission', props()),
    submitSupportRequestFailure: createAction('[NgRxList - Support Center] API call to create a support request failed', props()),
};

const ACCESSIBLE_LIST_ACTIONS = {
    load: createAction('[NgRxList - Accessible] Load lists for user', props()),
    loadSuccess: createAction('[NgRxList - Accessible] Load for user succeeded', props()),
    loadFailure: createAction('[NgRxList - Accessible] Load for user failed', props()),
    copyToMultiple: createAction('[NgRxList - Accessible] copy to multiple lists', props()),
    copyToMultipleSuccess: createAction('[NgRxList - Accessible] copy to multiple lists succeeded', props()),
    copyToMultipleFailure: createAction('[NgRxList - Accessible] copy to multiple lists failed', props()),
};

const LIST_VIEW_ACTIONS = {
    selectList: createAction('[List View] Set Selected List Id and Type', props()),
    changeListViewMode: createAction('[List View] Change List View Mode', props()),
    setSelectedListItemKey: createAction('[List View] Set Selected List Item Key', props()),
    setFridayToggle: createAction('[List View] Toggle Friday Toggle', props()),
    setPmeSortsAndFilters: createAction('[List View] Set PME Sorts and Filters', props()),
    resetListView: createAction('[List View] Reset List View'),
};

/* eslint-disable no-shadow */
var ListConstants;
(function (ListConstants) {
    ListConstants["normalListTypeId"] = "SL";
    ListConstants["orderGuideListType"] = "OG";
    ListConstants["masterListTypeId"] = "ML";
    ListConstants["recentPurchase"] = "Recently Purchased";
    ListConstants["orderGuide"] = "Order Guide";
    ListConstants["listTypeId"] = "listTypeId";
    ListConstants["beginningOfTimeWatermark"] = "1995-08-26T15:28:17.854Z";
    ListConstants["watermark"] = "watermark";
    ListConstants["updateDtm"] = "updateDtm";
    ListConstants["internal"] = "INTERNAL";
    ListConstants["private"] = "PRIVATE";
    ListConstants["splitOrderGuideEnpointFlag"] = "split_global_order_guide_filter";
    ListConstants["splitMyChangesWatermarkFlag"] = "split_global_mychanges_watermark";
    ListConstants["splitMasterListFlag"] = "split_global_master_lists";
    ListConstants["splitListUpdatesFlag"] = "split_global_forced_update_notification";
    // Sort Options
    ListConstants["asc"] = "asc";
    ListConstants["desc"] = "desc";
    // List of Lists Sort Options
    ListConstants["listName"] = "listName";
    ListConstants["lastUpdatedBy"] = "updatedByUser";
    ListConstants["numProducts"] = "listItemCount";
    ListConstants["numDiscontinued"] = "discontinuedCount";
    // List Sort Options
    ListConstants["groupLine"] = "Group (Line #)";
    ListConstants["groupAlpha"] = "Group (Alpha)";
    ListConstants["usFoodsClass"] = "US Foods Class";
    ListConstants["alphabetical"] = "Alphabetical";
    ListConstants["date"] = "Sort By Date";
    // List Filter Options
    ListConstants["recentlyPurchased"] = "Recently Purchased";
    ListConstants["discontinued"] = "Discontinued";
    // List Group Options
    ListConstants["allGroups"] = "allGroups";
    ListConstants["showProductsFromPast"] = "Show Products From Past";
    ListConstants["unassignedGroup"] = "Unassigned Group";
})(ListConstants || (ListConstants = {}));
var PmeSortOptions;
(function (PmeSortOptions) {
    PmeSortOptions["customerAz"] = "Customer (A-Z)";
    PmeSortOptions["gpd"] = "GPD / GP %";
    PmeSortOptions["marketChange"] = "Market Change";
    PmeSortOptions["priceChange"] = "Price Change";
    PmeSortOptions["wwp"] = "WWP";
})(PmeSortOptions || (PmeSortOptions = {}));
var PmeFilterOptions;
(function (PmeFilterOptions) {
    PmeFilterOptions["cprAutoAssist"] = "CPR Auto Assist";
    PmeFilterOptions["noCprEstablished"] = "No CPR Established";
    PmeFilterOptions["inFlrAndTgtRange"] = "In FLR and TGT Range";
    PmeFilterOptions["notInOptimizedRange"] = "Not in Optimized Range";
    PmeFilterOptions["productErrors"] = "Product Errors";
    PmeFilterOptions["showAllMarketChange"] = "Show All Market Change";
    PmeFilterOptions["showAllPriceChange"] = "Show All Price Changed";
    PmeFilterOptions["manualCpr"] = "Manual CPR";
})(PmeFilterOptions || (PmeFilterOptions = {}));
const listOfListsHeaders = {
    [ListConstants.listName]: {
        sortDirection: ListConstants.asc,
        displayText: 'List Name',
    },
    [ListConstants.lastUpdatedBy]: {
        sortDirection: ListConstants.desc,
        displayText: 'Last Updated By',
    },
    [ListConstants.numProducts]: {
        sortDirection: ListConstants.asc,
        displayText: 'Products',
    },
    [ListConstants.numDiscontinued]: {
        sortDirection: ListConstants.asc,
        displayText: 'Discontinued',
    },
};
const initialListOfListsSort = {
    recentlyViewedLists: {
        selectedHeader: undefined,
        headers: listOfListsHeaders,
    },
    publicLists: {
        selectedHeader: ListConstants.listName,
        headers: listOfListsHeaders,
    },
    internalLists: {
        selectedHeader: ListConstants.listName,
        headers: listOfListsHeaders,
    },
    privateLists: {
        selectedHeader: ListConstants.listName,
        headers: listOfListsHeaders,
    },
    managedByUsfLists: {
        selectedHeader: ListConstants.listName,
        headers: listOfListsHeaders,
    },
};
const initialImportErrors = {};
const initialCustomersPerList = {};

// TODO:  Make a dynamic offline storage key
// Ex.) LIST_STORAGE_KEY based on the logged in user and selected customer
const LIST_KEY = 'lists';
const LIST_GROUP_KEY = 'list-groups';
const LIST_ITEM_KEY = 'list-items';
const ORDER_GUIDE_KEY = 'order-guides';
const ORDER_GUIDE_GROUP_KEY = 'order-guide-groups';
const ORDER_GUIDE_ITEM_KEY = 'order-guide-items';
const RECENT_PURCHASE_KEY = 'recent-purchase';
const LIST_DOMAIN_NAME = 'lists';
const SELLER_RECENT_PURCHASE_KEY = 'seller-recent-purchase';
const SERVICE_REQUEST_KEY = 'service-request';
const MASTER_LIST_KEY = 'master-lists';
const MASTER_LIST_GROUP_KEY = 'master-list-groups';
const MASTER_LIST_ITEM_KEY = 'master-list-items';
const USER_STATE_KEY = 'user-state';
const SUPPORT_CENTER_KEY = 'support-center';
const LIST_UPDATES_KEY = 'list-updates';
const ACCESSIBLE_LIST_KEY = 'accessible-list';
const LIST_VIEW_KEY = 'list-view';
const LIST_STORAGE_KEYS = [
    LIST_KEY,
    LIST_GROUP_KEY,
    LIST_ITEM_KEY,
    ORDER_GUIDE_KEY,
    ORDER_GUIDE_GROUP_KEY,
    ORDER_GUIDE_ITEM_KEY,
    RECENT_PURCHASE_KEY,
    SELLER_RECENT_PURCHASE_KEY,
    SERVICE_REQUEST_KEY,
    MASTER_LIST_KEY,
    MASTER_LIST_GROUP_KEY,
    MASTER_LIST_ITEM_KEY,
    USER_STATE_KEY,
    SUPPORT_CENTER_KEY,
    LIST_UPDATES_KEY,
    ACCESSIBLE_LIST_KEY,
    LIST_VIEW_KEY,
];

const NGRX_LIST_LIB_CONFIG = new InjectionToken('NGRX_LIST_LIB_CONFIG');

/* eslint-disable max-len */
/* eslint-disable no-shadow */
var ListTracingConstants;
(function (ListTracingConstants) {
    // Trace Contexts
    ListTracingConstants["requestCreditContext"] = "requestCredit";
    ListTracingConstants["pickupRequestContext"] = "pickupRequest";
    ListTracingConstants["listMaintenance"] = "listMaintenance";
    ListTracingConstants["searchInList"] = "searchInList";
    ListTracingConstants["displayListDetails"] = "displayListDetails";
    // Events
    ListTracingConstants["submitRequestCreditEvent"] = "submit request credit";
    ListTracingConstants["submitRequestCreditEventWithUploadFailureEvent"] = "submit request credit with upload failure";
    ListTracingConstants["submitPickupRequestEvent"] = "submit pickup request";
    ListTracingConstants["submitPickupRequestEventWithUploadFailureEvent"] = "submit pickup request with upload failure";
    ListTracingConstants["addToListEvent"] = "add to list";
    ListTracingConstants["searchWithinListEvent"] = "search within list";
    ListTracingConstants["searchWithinListEndEvent"] = "search within list end";
    ListTracingConstants["displayListDetailsEvent"] = "display list details";
    ListTracingConstants["deleteListGroupEvent"] = "delete list group";
    ListTracingConstants["deleteListGroupAndMoveEvent"] = "delete list group and move items to unassigned group";
    ListTracingConstants["editListNameEvent"] = "edit list name";
    ListTracingConstants["editListTypeEvent"] = "edit list type";
    ListTracingConstants["createListEvent"] = "create list";
    ListTracingConstants["createGroupEvent"] = "create group";
    ListTracingConstants["deleteListEvent"] = "delete list";
    ListTracingConstants["copyItemsEvent"] = "copy items";
    ListTracingConstants["deleteItemsEvent"] = "delete items";
    ListTracingConstants["editGroupNameEvent"] = "edit group name";
    ListTracingConstants["resequenceGroupsEvent"] = "resequence groups";
    ListTracingConstants["replaceItemOnListEvent"] = "replace item on a list";
    ListTracingConstants["replaceItemOnListsEvent"] = "replace item on all lists";
    ListTracingConstants["moveItemWithinGroupFromLineNumberEvent"] = "move item within group from line number";
    ListTracingConstants["moveItemWithinGroupFromDragAndDropEvent"] = "move items within group from drag and drop";
    ListTracingConstants["moveItemToAnotherGroupFromLineNumberEvent"] = "move item to another group from line number";
    ListTracingConstants["moveItemToAnotherGroupFromDragAndDropEvent"] = "move items to another group from drag and drop";
    ListTracingConstants["moveItemToAnotherGroupFromModalEvent"] = "move items to another group from modal";
    // Span Names
    ListTracingConstants["submitRequestCreditSpan"] = "Submit Request Credit";
    ListTracingConstants["submitPickupRequestSpan"] = "Submit Pickup Request";
    ListTracingConstants["addToListSpan"] = "Add to List";
    ListTracingConstants["requestCreditSubmitEventsWithUploadFailureSpan"] = "Submit Request Credit with Upload Failure";
    ListTracingConstants["pickupRequestSubmitEventsWithUploadFailureSpan"] = "Submit Pickup Request with Upload Failure";
    ListTracingConstants["deleteListGroupSpan"] = "Delete List Group";
    ListTracingConstants["deleteListGroupAndMoveSpan"] = "Delete List Group and Move Items to Unassigned Group";
    ListTracingConstants["searchWithinListSpan"] = "Search Within List";
    ListTracingConstants["displayListDetailsSpan"] = "Display List Details";
    ListTracingConstants["editListNameSpan"] = "Edit List Name";
    ListTracingConstants["editListTypeSpan"] = "Edit List Type";
    ListTracingConstants["createListSpan"] = "Create List";
    ListTracingConstants["createGroupSpan"] = "Create Group";
    ListTracingConstants["deleteListSpan"] = "Delete List";
    ListTracingConstants["copyItemsSpan"] = "Copy Items";
    ListTracingConstants["deleteItemsSpan"] = "Delete Items";
    ListTracingConstants["editGroupNameSpan"] = "Edit Group Name";
    ListTracingConstants["resequenceGroupsSpan"] = "Resequence Groups";
    ListTracingConstants["replaceItemOnListSpan"] = "Replace Item on a List";
    ListTracingConstants["replaceItemOnListsSpan"] = "Replace Item on All Lists";
    ListTracingConstants["moveItemWithinGroupFromLineNumberSpan"] = "Move Item Within Group from Line Number";
    ListTracingConstants["moveItemWithinGroupFromDragAndDropSpan"] = "Move Items Within Group from Drag and Drop";
    ListTracingConstants["moveItemToAnotherGroupFromLineNumberSpan"] = "Move Item to Another Group from Line Number";
    ListTracingConstants["moveItemToAnotherGroupFromDragAndDropSpan"] = "Move Items to Another Group from Drag and Drop";
    ListTracingConstants["moveItemToAnotherGroupFromModalSpan"] = "Move Items to Another Group from Modal";
    // Attributes
    ListTracingConstants["searchWithin"] = "search within";
    ListTracingConstants["recentPurchaseListType"] = "recentlyPurchased";
    // Descriptions
    ListTracingConstants["requestCreditSuccessWithUploadFailureDesc"] = "Message has been posted to SOUS successfully, but attachments uploading/linking have failed.";
    ListTracingConstants["eventSuccess"] = " success";
    ListTracingConstants["eventFailure"] = " failure";
    ListTracingConstants["spanSuccess"] = " Success";
    ListTracingConstants["spanFailure"] = " Failure";
})(ListTracingConstants || (ListTracingConstants = {}));

// eslint-disable-next-line no-shadow
var ListState;
(function (ListState) {
    ListState["public"] = "PUBLIC";
    ListState["private"] = "PRIVATE";
    ListState["internal"] = "INTERNAL";
})(ListState || (ListState = {}));

// eslint-disable-next-line no-shadow
var ListType;
(function (ListType) {
    ListType["normal"] = "SL";
    ListType["orderGuide"] = "OG";
    ListType["masterList"] = "ML";
    ListType["recentPurchase"] = "Recently Purchased";
})(ListType || (ListType = {}));

/* eslint-disable no-shadow */
var SupportCenterConstants;
(function (SupportCenterConstants) {
    // sort directions
    SupportCenterConstants["asc"] = "asc";
    SupportCenterConstants["desc"] = "desc";
})(SupportCenterConstants || (SupportCenterConstants = {}));
var SupportCenterSortOptions;
(function (SupportCenterSortOptions) {
    // sort headers
    SupportCenterSortOptions["product"] = "product";
    SupportCenterSortOptions["request"] = "request";
    SupportCenterSortOptions["status"] = "status";
    SupportCenterSortOptions["invoice"] = "invoice";
    SupportCenterSortOptions["order"] = "order";
    SupportCenterSortOptions["date"] = "date";
    SupportCenterSortOptions["username"] = "username";
})(SupportCenterSortOptions || (SupportCenterSortOptions = {}));
const supportCenterHeaders = {
    [SupportCenterSortOptions.product]: {
        sortDirection: SupportCenterConstants.desc,
        displayText: 'Product',
    },
    [SupportCenterSortOptions.request]: {
        sortDirection: SupportCenterConstants.desc,
        displayText: 'Request #',
    },
    [SupportCenterSortOptions.status]: {
        sortDirection: SupportCenterConstants.asc,
        displayText: 'Status',
    },
    [SupportCenterSortOptions.invoice]: {
        sortDirection: SupportCenterConstants.desc,
        displayText: 'Invoice #',
    },
    [SupportCenterSortOptions.order]: {
        sortDirection: SupportCenterConstants.desc,
        displayText: 'Order #',
    },
    [SupportCenterSortOptions.date]: {
        sortDirection: SupportCenterConstants.desc,
        displayText: 'Date',
    },
    [SupportCenterSortOptions.username]: {
        sortDirection: SupportCenterConstants.desc,
        displayText: 'Username',
    },
};
const initialSupportCenterSort = {
    selectedHeader: SupportCenterSortOptions.status,
    headers: supportCenterHeaders,
};

const SousStatusLookUp = {
    FAILED: 0,
    NEW: 1,
    IN_PROGRESS: 2,
    CREATED: 3,
    PENDING_CDMR_DECISION: 3,
    APPROVED: 4,
    CLOSED: 5,
};
const mapSousStatusToNumber = (status) => SousStatusLookUp[status];

// eslint-disable-next-line no-shadow
var ListViewMode;
(function (ListViewMode) {
    ListViewMode["ordering"] = "ordering";
    ListViewMode["management"] = "management";
    ListViewMode["pricing"] = "pricing";
})(ListViewMode || (ListViewMode = {}));

/* eslint-disable max-len */
/* eslint-disable no-shadow */
var AnalyticMessages;
(function (AnalyticMessages) {
    // Capabilities
    AnalyticMessages["capability"] = "lists";
    AnalyticMessages["capabilityProduct"] = "product discovery";
    AnalyticMessages["capibilityServiceRequest"] = "service requests";
    // Page Names
    AnalyticMessages["listOfListsPageName"] = "us foods:r4:lists";
    AnalyticMessages["listDetailsPageName"] = "us foods:r4:list details";
    AnalyticMessages["listManagementPageName"] = "us foods:r4:list management";
    AnalyticMessages["requestCreditPageName"] = "us foods:r4:request credit";
    AnalyticMessages["requestCreditConfirmationPageName"] = "us foods:r4:request credit confirmation";
    AnalyticMessages["searchByProductNumberPageName"] = "us foods:r4:search product numbers";
    AnalyticMessages["supportCenterPageName"] = "us foods:r4:support center";
    AnalyticMessages["pricingPageName"] = "us foods:r4:list details:pricing";
    // Events
    AnalyticMessages["addToOrderEvent"] = "Add Product To Order";
    AnalyticMessages["filtersAppliedEvent"] = "filters applied";
    AnalyticMessages["pageLoadedEvent"] = "page loaded";
    AnalyticMessages["startRequestCreditEvent"] = "start return";
    AnalyticMessages["modalOpen"] = "modal open";
    AnalyticMessages["modalClosed"] = "modal closed";
    AnalyticMessages["downloadPrint"] = "share print download";
    AnalyticMessages["searchSubsEvent"] = "search submit";
    AnalyticMessages["addToList"] = "add to list";
    AnalyticMessages["copyListEvent"] = "copy list";
    AnalyticMessages["buttonClickEvent"] = "button click";
    AnalyticMessages["resubmitRequestEvent"] = "resubmit request";
    AnalyticMessages["submitSupportRequestEvent"] = "submit form";
    AnalyticMessages["productClickEvent"] = "product click";
    // Miscellaneous
    AnalyticMessages["masterListType"] = "master list";
    AnalyticMessages["quickView"] = "product quick view";
    AnalyticMessages["searchSubs"] = "search for subs";
    AnalyticMessages["submitPickUpRequest"] = "submit pickup request";
    AnalyticMessages["generateAIList"] = "generate ai list";
    AnalyticMessages["creditRequest"] = "credit request";
    AnalyticMessages["recoveryOrder"] = "recovery order";
    AnalyticMessages["pickUpRequest"] = "pickup request";
    AnalyticMessages["supportRequest"] = "support request";
    AnalyticMessages["aiListBuilder"] = "AI list builder";
    AnalyticMessages["fridayPricingToggleOn"] = "friday pricing toggle on";
    AnalyticMessages["fridayPricingToggleOff"] = "friday pricing toggle off";
})(AnalyticMessages || (AnalyticMessages = {}));

const orderGuideAdapter = createEntityAdapter({
    selectId: (orderGuide) => orderGuide?.listKey?.listTypeId + '-' + orderGuide?.listKey?.listId,
});
const initialOrderGuideState = orderGuideAdapter.getInitialState({
    loaded: false,
    loading: false,
    error: null,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _orderGuideReducer = createReducer(initialOrderGuideState, on(ORDER_GUIDE_ACTIONS.load, (state) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(ORDER_GUIDE_ACTIONS.loadSuccess, (state, action) => orderGuideAdapter.upsertMany(action.orderGuides, {
    ...state,
    loaded: true,
    loading: false,
})), on(ORDER_GUIDE_ACTIONS.loadFail, (state, action) => ({
    ...state,
    loading: false,
    loaded: true,
    error: action.error?.message,
})), on(ORDER_GUIDE_ACTIONS.selectAList, (state, action) => {
    const updatedLists = [];
    state.ids.forEach((id) => {
        if (id === action.key) {
            const updatedOrderGuide = {
                ...state.entities[id],
                isSelected: action.value,
            };
            updatedLists.push(updatedOrderGuide);
        }
        else {
            const updatedOrderGuide = { ...state.entities[id], isSelected: false };
            updatedLists.push(updatedOrderGuide);
        }
    });
    return orderGuideAdapter.upsertMany(updatedLists, state);
}), on(ORDER_GUIDE_ACTIONS.resetStore, (state, action) => initialOrderGuideState));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function orderGuideReducer(state, action) {
    return _orderGuideReducer(state, action);
}

const selectOrderGuideState = createFeatureSelector(ORDER_GUIDE_KEY);
const orderGuidesLoaded = () => createSelector(selectOrderGuideState, (orderGuideState) => orderGuideState.loaded);
const selectOrderGuideById = (listKey) => createSelector(selectOrderGuideState, (orderGuideState) => orderGuideState.entities[listKey.listTypeId + '-' + listKey.listId]);
const shouldShowOrderGuides = createSelector(selectCustomer(), selectLoggedInUser(), (selectedCustomer, userState) => {
    const userProfile = userState?.profiles?.find((userProf) => userProf.key === 'HIDE_OG');
    return (!userProfile ||
        userProfile?.value !== 'TRUE' ||
        selectedCustomer?.restrictToOG === 'Y');
});
const { selectIds: selectOrderGuideIds, selectEntities: selectOrderGuideEntities, selectAll: selectAllOrderGuides, selectTotal: selectTotalOrderGuides, } = orderGuideAdapter.getSelectors(selectOrderGuideState);

class ListStateService {
    constructor(browserStorageService, store, panAppState, config) {
        this.browserStorageService = browserStorageService;
        this.store = store;
        this.panAppState = panAppState;
        this.config = config;
        this.shouldShowOrderGuides$ = this.store.select(shouldShowOrderGuides);
        this.listDataInitialization$ = this.panAppState.session$.pipe(filter((sessionState) => !sessionState.isGuest), switchMap((sessionState) => combineLatest([
            this.panAppState.user$,
            this.panAppState.customer$,
        ]).pipe(filter(([userState, selectedCustomer]) => !!userState?.profiles &&
            Number(selectedCustomer?.customerNumber) ===
                Number(sessionState.selectedCustomerId) &&
            Number(selectedCustomer?.divisionNumber) ===
                Number(sessionState.division) &&
            Number(selectedCustomer?.departmentNumber) ===
                Number(sessionState.department)), take(1))), switchMap(() => this.shouldShowOrderGuides.pipe(take(1))));
    }
    get shouldShowOrderGuides() {
        return this.shouldShowOrderGuides$;
    }
    get listDataInitialization() {
        return this.listDataInitialization$;
    }
    initializeStorage(user, customer) {
        const dbInfo = [];
        dbInfo.push({
            dbName: `${user.userId}_${customer.divisionNumber}_${customer.customerNumber}_${customer.departmentNumber}_${LIST_DOMAIN_NAME}`,
            objectStoreNames: LIST_STORAGE_KEYS,
        });
        return this.browserStorageService.initialize({ dbInfo });
    }
    /**
     * @Remarks
     * Fetch new data on customer switch
     */
    initialize() {
        this.listDataInitialization.subscribe((shouldShowOG) => {
            this.resetData();
            this.initializeServiceRequestData();
            this.initializeListData();
            this.initializeMasterListData();
            if (shouldShowOG) {
                this.initializeOrderGuideData();
            }
            else {
                this.setOrderGuideStatesToLoadedTrue();
            }
            this.initializeListUpdatesData();
        });
    }
    initializeServiceRequestData() {
        this.store.dispatch(SERVICE_REQUEST_ACTIONS.load());
    }
    initializeListData() {
        this.store.dispatch(LIST_ACTIONS.initializeStorage());
    }
    async initializeMasterListData() {
        const shouldShowMasterLists = await this.panAppState.featureEnabled(ListConstants.splitMasterListFlag);
        if (shouldShowMasterLists) {
            this.store.dispatch(MASTER_LIST_ACTIONS.load());
            this.store.dispatch(MASTER_LIST_GROUP_ACTIONS.load());
            this.store.dispatch(MASTER_LIST_ITEM_ACTIONS.load());
        }
    }
    async initializeListUpdatesData() {
        const shouldShowListUpdates = await this.panAppState.featureEnabled(ListConstants.splitListUpdatesFlag);
        if (shouldShowListUpdates) {
            this.store.dispatch(LIST_UPDATES_ACTIONS.load());
        }
    }
    async initializeOrderGuideData() {
        const orderGuideFeatureFlag = await this.panAppState.featureEnabled(ListConstants.splitOrderGuideEnpointFlag);
        if (orderGuideFeatureFlag) {
            this.store.dispatch(ORDER_GUIDE_ACTIONS.load());
            this.store.dispatch(ORDER_GUIDE_GROUP_ACTIONS.load());
            this.store.dispatch(ORDER_GUIDE_ITEM_ACTIONS.load());
        }
    }
    setOrderGuideStatesToLoadedTrue() {
        this.store.dispatch(ORDER_GUIDE_ACTIONS.loadSuccess({ orderGuides: [] }));
        this.store.dispatch(ORDER_GUIDE_GROUP_ACTIONS.loadSuccess({ orderGuideGroups: [] }));
        this.store.dispatch(ORDER_GUIDE_ITEM_ACTIONS.loadSuccess({ orderGuideItems: [] }));
    }
    getFromOfflineStorage(user, customer, storeEntityName) {
        // eslint-disable-next-line max-len
        const dbName = `${user?.userId}_${customer?.divisionNumber}_${customer?.customerNumber}_${customer?.departmentNumber}_${LIST_DOMAIN_NAME}`;
        return this.browserStorageService.loadFromOfflineStorage(dbName, storeEntityName);
    }
    writeToOfflineStorage(user, customer, storeEntityName, state) {
        const dbName = `${user?.userId}_${customer?.divisionNumber}_${customer?.customerNumber}_${customer?.departmentNumber}_lists`;
        return this.browserStorageService.indexDB(dbName, storeEntityName, '0', state, MessageTypes.saveBrowserStorage);
    }
    resetData() {
        this.store.dispatch(LIST_ACTIONS.resetStore());
        this.store.dispatch(LIST_ITEM_ACTIONS.resetStore());
        this.store.dispatch(LIST_GROUP_ACTIONS.resetStore());
        this.store.dispatch(RECENT_PURCHASE_ACTIONS.resetStore());
        this.store.dispatch(MASTER_LIST_ACTIONS.resetStore());
        this.store.dispatch(MASTER_LIST_GROUP_ACTIONS.resetStore());
        this.store.dispatch(MASTER_LIST_ITEM_ACTIONS.resetStore());
        this.store.dispatch(ORDER_GUIDE_ACTIONS.resetStore());
        this.store.dispatch(ORDER_GUIDE_GROUP_ACTIONS.resetStore());
        this.store.dispatch(ORDER_GUIDE_ITEM_ACTIONS.resetStore());
    }
}
ListStateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListStateService, deps: [{ token: i1.BrowserStorageService }, { token: i1$1.Store }, { token: i3.PanAppState }, { token: NGRX_LIST_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
ListStateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListStateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListStateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.BrowserStorageService }, { type: i1$1.Store }, { type: i3.PanAppState }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_LIST_LIB_CONFIG]
                }] }]; } });

/* eslint-disable no-shadow */
const listAdapter = createEntityAdapter({
    selectId: (list) => list?.listKey?.listTypeId + '-' + list?.listKey?.listId,
});
const initialListState = listAdapter.getInitialState({
    // add additional entity state properties here if needed
    loaded: false,
    watermark: ListConstants.beginningOfTimeWatermark,
    searchSortFilterOptions: {
        searchKey: '',
        sortType: ListConstants.groupLine,
        filters: [],
        groupToSort: ListConstants.allGroups,
    },
    listOfListsSort: initialListOfListsSort,
    importErrors: initialImportErrors,
    customersPerList: initialCustomersPerList,
    isMassDeleteLoading: false,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _listReducer = createReducer(initialListState, on(LIST_ACTIONS.loadFromStorageSuccess, (state, action) => {
    const listEntities = {};
    for (const key in action.lists.entities) {
        if (action.lists.entities.hasOwnProperty(key)) {
            listEntities[key] = {
                ...action.lists.entities[key],
                selectedGroup: undefined,
            };
        }
    }
    const newListState = {
        ...action.lists,
        entities: listEntities,
        listOfListsSort: initialListOfListsSort,
    };
    return {
        ...state,
        ...newListState,
        searchSortFilterOptions: {
            searchKey: '',
            sortType: ListConstants.groupLine,
            filters: [],
            groupToSort: ListConstants.allGroups,
        },
        loading: false,
        loaded: true,
    };
}), on(LIST_ACTIONS.load, (state) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(LIST_ACTIONS.loadSuccess, (state, action) => {
    let stateWatermark = ListConstants.beginningOfTimeWatermark;
    const listsState = action.lists.map((list) => {
        if (list !== null) {
            const listState = list;
            if (new Date(listState.watermark) > new Date(stateWatermark)) {
                stateWatermark = listState.watermark;
            }
            return {
                ...list,
                selectedGroup: undefined,
                loading: false,
                loaded: true,
                isLastSelected: false,
                isSelected: false,
            };
        }
    });
    return listAdapter.upsertMany(listsState, {
        ...state,
        loading: false,
        loaded: true,
        watermark: stateWatermark,
    });
}), on(LIST_ACTIONS.loadFail, (state, action) => ({
    ...state,
    loading: false,
    loaded: true,
    error: action.error,
})), on(LIST_ACTIONS.resetStore, () => {
    console.log('LIST_ACTIONS.resetStore: resetting lists state');
    return initialListState;
}), on(LIST_ACTIONS.domainCallSuccess, (state, action) => listAdapter.upsertOne(action.list, state)), on(LIST_ACTIONS.domainCallFail, (state, action) => listAdapter.upsertOne(action.list, state)), on(LIST_ACTIONS.create, (state, action) => {
    const listEntity = action.list;
    state = listAdapter.addOne(listEntity, state);
    return {
        ...state,
        loading: true,
        loaded: false,
    };
}), on(LIST_ACTIONS.createSuccess, (state) => ({
    ...state,
    loading: false,
    loaded: true,
})), on(LIST_ACTIONS.createFail, (state, action) => {
    const key = action.list.listKey.listTypeId + '-' + action.list.listKey.listId;
    state = listAdapter.removeOne(key, state);
    return {
        ...state,
        loading: false,
        loaded: true,
        error: action.error,
    };
}), on(LIST_ACTIONS.delete, (state, action) => {
    const listKey = action.list.listKey.listTypeId + '-' + action.list.listKey.listId;
    state = listAdapter.removeOne(listKey, state);
    return {
        ...state,
    };
}), on(LIST_ACTIONS.deleteSuccess, (state) => ({
    ...state,
    loading: false,
    loaded: true,
})), on(LIST_ACTIONS.deleteFail, (state, action) => listAdapter.upsertOne(action.list, state)), on(LIST_ACTIONS.removeNegativeIdLists, (state, action) => listAdapter.removeMany(action.keys, state)), on(LIST_ACTIONS.editListType, (state, action) => listAdapter.upsertOne({ ...action.list, listState: action.newListType }, state)), on(LIST_ACTIONS.editListName, (state, action) => listAdapter.upsertOne({
    ...action.list,
    listName: action.newListName,
}, state)), on(LIST_ACTIONS.editListTypeFail, LIST_ACTIONS.editListNameFail, (state, action) => listAdapter.upsertOne(action.list, state)), on(LIST_ACTIONS.updateLastSelectedList, (state, action) => {
    const listState = [];
    for (const key in state.entities) {
        if (state.entities.hasOwnProperty(key)) {
            if (state.entities[key]?.listKey === action.lastSelectedList?.listKey) {
                listState.push({
                    ...state.entities[key],
                    isLastSelected: true,
                });
            }
            else {
                listState.push({
                    ...state.entities[key],
                    isLastSelected: false,
                });
            }
        }
    }
    state = listAdapter.upsertMany(listState, {
        ...state,
        loaded: true,
        loading: false,
    });
    return { ...state };
}), on(LIST_ACTIONS.updateList, (state, action) => listAdapter.upsertOne(action.list, state)), on(LIST_ACTIONS.updateListOfListsSort, (state, action) => {
    // If selected header is NOT listName (default), must use 'desc' for listName sort direction instead of initial value
    // b/c first click sets sort direction to opposite value in client logic
    const updatedHeaders = action.selectedHeader === ListConstants.listName
        ? {
            ...initialListOfListsSort[action.listSection].headers,
            [action.selectedHeader]: {
                ...state.listOfListsSort[action.listSection].headers[action.selectedHeader],
                sortDirection: action.sortDirection,
            },
        }
        : {
            ...initialListOfListsSort[action.listSection].headers,
            [ListConstants.listName]: {
                ...state.listOfListsSort[action.listSection].headers[ListConstants.listName],
                sortDirection: ListConstants.desc,
            },
            [action.selectedHeader]: {
                ...state.listOfListsSort[action.listSection].headers[action.selectedHeader],
                sortDirection: action.sortDirection,
            },
        };
    return {
        ...state,
        listOfListsSort: {
            ...state.listOfListsSort,
            [action.listSection]: {
                ...state.listOfListsSort[action.listSection],
                selectedHeader: action.selectedHeader,
                headers: updatedHeaders,
            },
        },
    };
}), on(LIST_ACTIONS.resetListOfListsSort, (state) => ({
    ...state,
    listOfListsSort: initialListOfListsSort,
})), on(LIST_ACTIONS.editGroupSequence, (state, action) => {
    const listToUpdate = { ...action.list, listGroupKeys: action.groupKeys };
    return listAdapter.upsertOne(listToUpdate, state);
}), on(LIST_ACTIONS.editGroupSequenceFail, (state, action) => listAdapter.upsertOne(action.list, state)), on(LIST_ACTIONS.deleteGroupAndDelete, LIST_ACTIONS.deleteGroupAndMove, (state, action) => {
    const newList = {
        ...action.list,
        listGroupKeys: action.updatedGroupKeys,
    };
    return listAdapter.upsertOne(newList, state);
}), on(LIST_ACTIONS.deleteGroupFail, (state, action) => listAdapter.upsertOne(action.list, {
    ...state,
    loading: false,
    loaded: true,
})), on(LIST_ACTIONS.changeSelectedGroup, (state, action) => {
    const newListState = {
        ...state.entities[action.key],
        selectedGroup: action.newSelectedGroup,
    };
    return listAdapter.upsertOne(newListState, {
        ...state,
        loaded: true,
        loading: false,
    });
}), on(LIST_ACTIONS.selectAList, (state, action) => {
    const updatedLists = [];
    state.ids.forEach((id) => {
        if (id === action.key) {
            const updatedList = { ...state.entities[id], isSelected: action.value };
            updatedLists.push(updatedList);
        }
        else {
            const updatedList = { ...state.entities[id], isSelected: false };
            updatedLists.push(updatedList);
        }
    });
    return listAdapter.upsertMany(updatedLists, state);
}), on(LIST_ACTIONS.ecomR4UpdateListsInStore, (state, action) => {
    const listsToUpdate = [];
    action.lists.forEach((list) => {
        const currentState = state.entities[list?.listKey?.listTypeId + '-' + list?.listKey?.listId];
        const watermark = new Date(action.watermark);
        const listEntity = list;
        // Check to see if list already exists
        if (!currentState) {
            // Insert new list, update old list with new ID so ui knows to subscribe to new list
            const negativeList = generateNegativeList(list, state);
            listsToUpdate.push(negativeList);
            listsToUpdate.push({ ...list, watermark: watermark.toISOString() });
        }
        else if (!watermarkIsOld(listEntity?.watermark, list, state)) {
            // If list does exist and the new watermark is newer, upsert state with new list
            listsToUpdate.push({ ...list, watermark: watermark.toISOString() });
        }
    });
    const watermark = action.watermark;
    const version = action.version;
    return listAdapter.upsertMany(listsToUpdate, {
        ...state,
        watermark,
        loaded: true,
        loading: false,
        version,
    });
}), on(LIST_ACTIONS.deletedAsyncSuccess, (state, action) => listAdapter.removeMany(action.keys, {
    ...state,
    watermark: action.watermark,
    loaded: true,
    loading: false,
    version: action.version,
})), on(LIST_ACTIONS.updateInStore, (state, action) => {
    const watermark = new Date(action.watermark);
    const version = action.version;
    const listsToUpdate = [];
    action.lists?.forEach((list) => {
        const currentState = state?.entities[list?.listKey.listTypeId + '-' + list?.listKey.listId] || null;
        if (currentState) {
            const currentWatermark = new Date(currentState?.watermark);
            if (watermark > currentWatermark) {
                listsToUpdate.push({ ...list, watermark: watermark.toISOString() });
            }
        }
        else {
            listsToUpdate.push({ ...list, watermark: watermark.toISOString() });
        }
    });
    return listAdapter.upsertMany(listsToUpdate, {
        ...state,
        watermark: watermark.toISOString(),
        loaded: true,
        loading: false,
        version,
    });
}), on(LIST_ACTIONS.updateMany, (state, action) => listAdapter.upsertMany(action.lists, state)), on(LIST_ACTIONS.searchInList, (state, action) => {
    let newState = state;
    const startOfSearchOrSearchReset = action.tracking?.tracing.data.isStartOfTrace || !action.tracking;
    if (startOfSearchOrSearchReset) {
        newState = {
            ...state,
            searchSortFilterOptions: {
                ...state.searchSortFilterOptions,
                searchKey: action.searchKey,
            },
        };
    }
    return newState;
}), on(LIST_ACTIONS.sortAndFilterList, (state, action) => ({
    ...state,
    searchSortFilterOptions: {
        ...state.searchSortFilterOptions,
        sortType: action.sortType,
        groupToSort: action.groupToSort,
        filters: action.filters,
        sortDirection: action.sortDirection ?? state.searchSortFilterOptions.sortDirection,
    },
})), on(LIST_ACTIONS.betterBuyAcceptSuccess, (state, action) => listAdapter.upsertMany(action.lists, state)), on(LIST_ACTIONS.importListSuccess, (state, action) => {
    const stateToExport = { ...state, importErrors: { ...state.importErrors } };
    // eslint-disable-next-line max-len
    stateToExport.importErrors[action.importedListContents.list.listKey.listTypeId +
        '-' +
        action.importedListContents.list.listKey.listId] = { errors: action.errorDetails };
    return stateToExport;
}), on(LIST_ACTIONS.removeImportError, (state, action) => {
    const stateToExport = { ...state };
    // eslint-disable-next-line max-len
    delete stateToExport.importErrors[action.listKey.listTypeId + '-' + action.listKey.listId];
    return stateToExport;
}), on(LIST_ACTIONS.getCustomersWithListName, (state, action) => {
    const stateToExport = {
        ...state,
        isMassDeleteLoading: true,
    };
    return stateToExport;
}), on(LIST_ACTIONS.getCustomersWithListNameFail, (state, action) => {
    const stateToExport = {
        ...state,
        isMassDeleteLoading: false,
    };
    return stateToExport;
}), on(LIST_ACTIONS.getCustomersWithListNameSuccess, (state, action) => {
    const stateToExport = {
        ...state,
        customersPerList: { ...state.customersPerList },
        isMassDeleteLoading: false,
    };
    stateToExport.customersPerList[action.listName] = {
        custInfo: action.customerList,
    };
    return stateToExport;
}), on(LIST_ACTIONS.massDelete, (state, action) => ({
    ...state,
    isMassDeleteLoading: true,
})), on(LIST_ACTIONS.massDeleteSuccess, (state, action) => {
    let listKeyToRemove;
    for (const listKey of action.listKeys) {
        const key = listKey.listTypeId + '-' + listKey.listId;
        const list = state.entities[key];
        if (list) {
            listKeyToRemove = key;
        }
    }
    if (listKeyToRemove) {
        const newState = listAdapter.removeOne(listKeyToRemove, state);
        return { ...newState, isMassDeleteLoading: false };
    }
    else {
        return { ...state, isMassDeleteLoading: false };
    }
}), on(LIST_ACTIONS.massDeleteFail, (state, action) => ({
    ...state,
    error: action.error,
    isMassDeleteLoading: false,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function listReducer(state, action) {
    return _listReducer(state, action);
}
const createListKey = (listKey) => [listKey.listTypeId, listKey.listId].join('-');
const generateNegativeList = (list, state) => {
    const newList = {
        listKey: {
            listId: Number(list.r4TempListId),
            listTypeId: list.listKey.listTypeId,
        },
        swapListId: list.listKey.listId,
    };
    state = listAdapter.upsertOne(newList, state);
    return newList;
};
const watermarkIsOld = (newWatermark, list, state) => {
    const currentList = state.entities[list.listKey.listTypeId + '-' + list.listKey.listId];
    const currentWatermark = currentList?.watermark;
    const newDate = new Date(newWatermark);
    const currentlySavedDate = new Date(currentWatermark);
    return newDate < currentlySavedDate;
};

/* eslint-disable @typescript-eslint/dot-notation */
const listGroupAdapter = createEntityAdapter({
    selectId: (listGroupState) => listGroupState?.listKey?.listTypeId +
        '-' +
        listGroupState?.listKey?.listId +
        '-' +
        listGroupState?.listGroupKey?.listGroupId,
});
const initialListGroupState = listGroupAdapter.getInitialState({
    // additional entity state properties
    loaded: false,
    watermark: ListConstants.beginningOfTimeWatermark,
});
// create view model state from entity state (api models)
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _listGroupReducer = createReducer(initialListGroupState, on(LIST_GROUP_ACTIONS.load, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(LIST_GROUP_ACTIONS.loadSuccess, (state, action) => {
    let stateWatermark = ListConstants.beginningOfTimeWatermark;
    const listGroupState = action.listGroups.map((listGroup) => {
        if (listGroup !== null) {
            const groupState = listGroup;
            if (new Date(groupState[ListConstants.updateDtm]) >
                new Date(stateWatermark)) {
                stateWatermark = groupState[ListConstants.updateDtm];
            }
            return {
                ...{
                    ...listGroup,
                },
                loaded: true,
                loading: false,
            };
        }
    });
    return listGroupAdapter.upsertMany(listGroupState, {
        ...state,
        loaded: true,
        loading: false,
        watermark: stateWatermark,
    });
}), on(LIST_GROUP_ACTIONS.resetStore, (state, action) => initialListGroupState), on(LIST_GROUP_ACTIONS.create, (state, action) => listGroupAdapter.addOne(action.listGroup, {
    ...state,
    loaded: false,
    loading: true,
})), on(LIST_GROUP_ACTIONS.createSuccess, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
})), on(LIST_GROUP_ACTIONS.createFail, (state, action) => {
    const listGroup = action.listGroup;
    const key = createListGroupKey(listGroup.listKey, listGroup.listGroupKey);
    state = listGroupAdapter.removeOne(key, state);
    return { ...state, loaded: true, loading: false };
}), on(LIST_GROUP_ACTIONS.updateInStore, (state, action) => {
    const watermark = new Date(action.watermark);
    const currentStateWatermark = new Date(state.watermark);
    const version = action.version;
    const listGroupsToUpdate = [];
    if (watermark > currentStateWatermark) {
        action.listGroups?.forEach((group) => {
            const currentState = state?.entities[group.listKey.listTypeId +
                '-' +
                group.listKey.listId +
                '-' +
                group.listGroupKey.listGroupId] || null;
            if (currentState) {
                const currentWatermark = new Date(currentState[ListConstants.updateDtm]);
                if (watermark > currentWatermark) {
                    listGroupsToUpdate.push({
                        ...group,
                        watermark: watermark.toISOString(),
                    });
                }
            }
            else {
                listGroupsToUpdate.push({
                    ...group,
                    watermark: watermark.toISOString(),
                });
            }
        });
        return listGroupAdapter.upsertMany(listGroupsToUpdate, {
            ...state,
            watermark: watermark.toISOString(),
            loaded: true,
            loading: false,
            version,
        });
    }
    else {
        return state;
    }
}), on(LIST_GROUP_ACTIONS.deletedAsyncSuccess, (state, action) => listGroupAdapter.removeMany(action.keys, {
    ...state,
    watermark: action.watermark,
    loaded: true,
    loading: false,
    version: action.version,
})), on(LIST_GROUP_ACTIONS.moveItemsWithinGroupFromDragAndDrop, LIST_GROUP_ACTIONS.moveItemsWithinGroupFromLineNumber, (state, action) => listGroupAdapter.upsertOne({
    ...action.group,
    listItemKeys: action.listItemKeys,
}, { ...state, loaded: false, loading: true })), on(LIST_GROUP_ACTIONS.moveItemsWithinGroupSuccess, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
})), on(LIST_GROUP_ACTIONS.moveItemsWithinGroupFail, (state, action) => listGroupAdapter.upsertOne(action.group, {
    ...state,
    loaded: true,
    loading: false,
})), on(LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFromDragAndDrop, LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFromLineNumber, LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFromModal, (state, action) => {
    const newGroupState = [];
    const newSourceGroup = {
        ...action.stateToChange.sourceGroup,
        listItemKeys: action.stateToChange.updatedSourceGroupItemKeys,
    };
    newGroupState.push(newSourceGroup);
    const newTargetGroup = {
        ...action.stateToChange.targetGroup,
        listItemKeys: action.stateToChange.updatedTargetGroupItemKeys,
    };
    newGroupState.push(newTargetGroup);
    return listGroupAdapter.upsertMany(newGroupState, {
        ...state,
    });
}), on(LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFail, (state, action) => listGroupAdapter.upsertMany([action.stateToRevert.sourceGroup, action.stateToRevert.targetGroup], {
    ...state,
    loaded: true,
    loading: false,
})), on(LIST_GROUP_ACTIONS.editGroupName, (state, action) => listGroupAdapter.upsertOne({ ...action.group, groupName: action.newGroupName }, { ...state, loaded: false, loading: true })), on(LIST_GROUP_ACTIONS.editGroupNameSuccess, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
})), on(LIST_GROUP_ACTIONS.editGroupNameFail, (state, action) => listGroupAdapter.upsertOne(action.group, {
    ...state,
    loading: false,
    loaded: true,
})), on(LIST_GROUP_ACTIONS.update, (state, action) => listGroupAdapter.upsertOne(action.group, state)), on(LIST_GROUP_ACTIONS.delete, (state, action) => listGroupAdapter.removeOne(createListGroupKey(action.group.listKey, action.group.listGroupKey), state)), on(LIST_GROUP_ACTIONS.removeNegativeIdGroups, (state, action) => listGroupAdapter.removeMany(action.keys, state)), on(LIST_GROUP_ACTIONS.updateListItemKeys, (state, action) => {
    const listItemKeys = action.listGroup?.listItemKeys?.slice() || [];
    if (action.atGroupLocation && action.atGroupLocation === 'TOP') {
        action.listItems.forEach((item) => {
            listItemKeys.unshift(item.listItemKey);
        });
    }
    else {
        action.listItems.forEach((item) => {
            listItemKeys.push(item.listItemKey);
        });
    }
    const groupState = { ...action.listGroup, listItemKeys };
    return listGroupAdapter.upsertOne(groupState, state);
}), on(LIST_GROUP_ACTIONS.removeListItemKeys, (state, action) => {
    let listItemKeys = [];
    const groupStates = [];
    const listItemKeysToRemove = action.listItemsToRemove.map((item) => item?.listItemKey?.listItemId);
    action.listGroups.forEach((group) => {
        group.listItemKeys.forEach((itemKey) => {
            if (!listItemKeysToRemove.includes(itemKey?.listItemId)) {
                listItemKeys.push(itemKey);
            }
        });
        groupStates.push({ ...group, listItemKeys });
        listItemKeys = [];
    });
    return listGroupAdapter.upsertMany(groupStates, state);
}), on(LIST_GROUP_ACTIONS.updateMany, (state, action) => listGroupAdapter.upsertMany(action.groups, state)), on(LIST_ITEM_ACTIONS.delete, (state, action) => listGroupAdapter.upsertMany(action.updatedGroups, state)), on(LIST_ITEM_ACTIONS.deleteFail, (state, action) => listGroupAdapter.upsertMany(action.listGroups, state)), on(LIST_GROUP_ACTIONS.loadFromStorageSuccess, (state, action) => ({
    ...state,
    ...action.listGroups,
    loading: false,
    loaded: true,
})), on(LIST_ACTIONS.deleteGroupAndDelete, LIST_ACTIONS.deleteGroupAndMove, (state, action) => listGroupAdapter.removeOne(createListGroupKey(action.groupToDelete.listKey, action.groupToDelete.listGroupKey), state)), on(LIST_ACTIONS.deleteGroupAndMove, (state, action) => listGroupAdapter.upsertOne({ ...action.unassignedGroup, listItemKeys: action.updatedListItemKeys }, state)), on(LIST_ACTIONS.deleteGroupFail, (state, action) => listGroupAdapter.upsertMany(action.groupsToReinsert, state)), on(LIST_ACTIONS.betterBuyAcceptSuccess, (state, action) => listGroupAdapter.upsertMany(action.groups, state)));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function listGroupReducer(state, action) {
    return _listGroupReducer(state, action);
}
const createListGroupKey = (listKey, listGroupKey) => [listKey.listTypeId, listKey.listId, listGroupKey.listGroupId].join('-');

const orderGuideGroupAdapter = createEntityAdapter({
    selectId: (orderGuideGroup) => orderGuideGroup?.listKey?.listTypeId +
        '-' +
        orderGuideGroup?.listKey?.listId +
        '-' +
        orderGuideGroup?.listGroupKey?.listGroupId,
});
const initialOrderGuideGroupState = orderGuideGroupAdapter.getInitialState({
    loaded: false,
    loading: false,
    error: null,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _orderGuideGroupReducer = createReducer(initialOrderGuideGroupState, on(ORDER_GUIDE_GROUP_ACTIONS.load, (state) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(ORDER_GUIDE_GROUP_ACTIONS.loadSuccess, (state, action) => orderGuideGroupAdapter.upsertMany(action.orderGuideGroups, {
    ...state,
    loaded: true,
    loading: false,
})), on(ORDER_GUIDE_GROUP_ACTIONS.loadFail, (state, action) => ({
    ...state,
    loading: false,
    loaded: true,
    error: action.error?.message,
})), on(ORDER_GUIDE_GROUP_ACTIONS.resetStore, (state, action) => initialOrderGuideGroupState));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function orderGuideGroupReducer(state, action) {
    return _orderGuideGroupReducer(state, action);
}

const selectOrderGuideGroupState = createFeatureSelector(ORDER_GUIDE_GROUP_KEY);
const orderGuideGroupsLoaded = () => createSelector(selectOrderGuideGroupState, (orderGuideGroupState) => orderGuideGroupState.loaded);
const { selectIds: selectOrderGuideGroupIds, selectEntities: selectOrderGuideGroupEntities, selectAll: selectAllOrderGuideGroups, selectTotal: selectTotalOrderGuideGroups, } = orderGuideGroupAdapter.getSelectors(selectOrderGuideGroupState);

const selectListGroupsState = createFeatureSelector(LIST_GROUP_KEY);
const listGroupsLoaded = () => createSelector(selectListGroupsState, (groups) => groups.loaded);
const listGroupsWatermark = () => createSelector(selectListGroupsState, (listGroups) => listGroups?.watermark);
const selectCombinedListAndOgGroups = () => createSelector(listGroupAdapter.getSelectors(selectListGroupsState).selectEntities, orderGuideGroupAdapter.getSelectors(selectOrderGuideGroupState)
    .selectEntities, (listGroups, orderGuideGroups) => Object.assign({}, listGroups, orderGuideGroups));
const { selectIds: selectListGroupIds, selectEntities: selectListGroupEntities, selectAll: selectAllListGroups, selectTotal: selectTotalListGroups, } = listGroupAdapter.getSelectors(selectListGroupsState);

const selectListsState = createFeatureSelector(LIST_KEY);
const listsLoaded = () => createSelector(selectListsState, (lists) => lists.loaded);
const allLists = () => createSelector(selectListsState, (lists) => Object.values(lists));
const selectListSearchSortFilterOptions = createSelector(selectListsState, (listState) => listState.searchSortFilterOptions);
const selectListImportErrors = (listKey) => createSelector(selectListsState, (lists) => lists.importErrors[listKey.listTypeId + '-' + listKey.listId]);
const selectCustomersWithListName = (listName) => createSelector(selectListsState, (lists) => lists.customersPerList[listName]);
const selectListById = (listKey) => createSelector(selectListsState, (lists) => lists?.entities[listKey.listTypeId + '-' + listKey.listId]);
const listsWatermark = () => createSelector(selectListsState, (lists) => lists.watermark);
const selectListsWithUpdatedCounts = createSelector(selectListsState, selectListGroupsState, (iListState, iListGroupState) => {
    const dictionaryOfLists = {};
    for (const key in iListState.entities) {
        if (iListState.entities.hasOwnProperty(key)) {
            let count = 0;
            const list = iListState.entities[key];
            const listGroupKeys = list?.listGroupKeys;
            if (listGroupKeys) {
                for (const listGroupKey of listGroupKeys) {
                    const groupKeyLookup = createListGroupKey(list.listKey, listGroupKey);
                    const group = iListGroupState.entities[groupKeyLookup];
                    if (group?.listItemKeys) {
                        count += group.listItemKeys.length;
                    }
                }
                const listKey = createListKey(list.listKey);
                dictionaryOfLists[listKey] = { ...list, listItemCount: count };
            }
        }
    }
    return dictionaryOfLists;
});
const selectCombinedListAndOgHeaders = () => createSelector(selectListsWithUpdatedCounts, orderGuideAdapter.getSelectors(selectOrderGuideState).selectEntities, (lists, orderGuides) => Object.assign({}, lists, orderGuides));
const selectMassDeleteLoading = () => createSelector(selectListsState, (listsState) => listsState?.isMassDeleteLoading);
const { selectIds: selectListIds, selectEntities: selectListEntities, selectAll: selectAllLists, selectTotal: selectTotalLists, } = listAdapter.getSelectors(selectListsState);
const selectFavoritesList = createSelector(selectAllLists, (lists) => lists?.find((list) => list.favorite === true));

class ListFacadeService {
    constructor(store) {
        this.store = store;
        this.allLists$ = this.store.select(selectAllLists);
        this.allListEntities$ = this.store.select(selectListEntities);
        this.listState$ = this.store.select(selectListsState);
        this.listsLoaded$ = this.store.select(listsLoaded());
    }
    loadLists() {
        this.store.dispatch(LIST_ACTIONS.loadFromStorage());
    }
}
ListFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListFacadeService, deps: [{ token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ListFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Store }]; } });

class ListGroupFacadeService {
    constructor(browserStorageService, store) {
        this.browserStorageService = browserStorageService;
        this.store = store;
        this.allListGroups$ = this.store.select(selectAllListGroups);
        this.allListGroupEntities$ = this.store.select(selectListGroupEntities);
        this.listGroupsLoaded$ = this.store.select(listGroupsLoaded());
    }
    loadListGroups(watermark) {
        this.store.dispatch(LIST_GROUP_ACTIONS.loadFromStorage());
    }
}
ListGroupFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListGroupFacadeService, deps: [{ token: i1.BrowserStorageService }, { token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ListGroupFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListGroupFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListGroupFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.BrowserStorageService }, { type: i1$1.Store }]; } });

const listItemAdapter = createEntityAdapter({
    selectId: (listItemState) => listItemState.listKey.listTypeId +
        '-' +
        listItemState.listKey.listId +
        '-' +
        listItemState.listGroupKey.listGroupId +
        '-' +
        listItemState.listItemKey.listItemId,
});
const initialListItemState = listItemAdapter.getInitialState({
    // additional entity state properties
    loaded: false,
    watermark: ListConstants.beginningOfTimeWatermark,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _listItemReducer = createReducer(initialListItemState, on(LIST_ITEM_ACTIONS.load, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(LIST_ITEM_ACTIONS.loadSuccess, (state, action) => {
    let stateWatermark = ListConstants.beginningOfTimeWatermark;
    const listItemsState = action.listItems.map((item) => {
        if (item !== null) {
            const listItem = item;
            if (new Date(listItem[ListConstants.updateDtm]) > new Date(stateWatermark)) {
                stateWatermark = listItem[ListConstants.updateDtm];
            }
            return {
                ...item,
                loaded: true,
                loading: false,
                isSelected: false,
            };
        }
    });
    return listItemAdapter.upsertMany(listItemsState, {
        ...state,
        loaded: true,
        loading: false,
        watermark: stateWatermark,
    });
}), on(LIST_ITEM_ACTIONS.loadFail, (state, action) => ({
    ...state,
    loading: false,
    loaded: false,
    error: action.error,
})), on(LIST_ITEM_ACTIONS.loadFromStorageSuccess, (state, action) => {
    const listItemsState = {};
    for (const key in action.listItems.entities) {
        if (action.listItems.entities.hasOwnProperty(key)) {
            listItemsState[key] = {
                ...action.listItems.entities[key],
                isSelected: false,
                loaded: true,
            };
        }
    }
    const newListItemsState = {
        ...action.listItems,
        entities: listItemsState,
    };
    return { ...state, ...newListItemsState, loaded: true, loading: false };
}), on(LIST_ITEM_ACTIONS.resetStore, (state, action) => initialListItemState), on(LIST_ITEM_ACTIONS.updateInStore, (state, action) => {
    const watermark = new Date(action.watermark);
    const currentStateWatermark = new Date(state.watermark);
    const version = action.version;
    const listItemsToUpdate = [];
    if (watermark >= currentStateWatermark) {
        action.listItems?.forEach((item) => {
            const currentState = state?.entities[item.listKey.listTypeId +
                '-' +
                item.listKey.listId.toString() +
                '-' +
                item.listGroupKey.listGroupId +
                '-' +
                item.listItemKey.listItemId] || null;
            if (currentState) {
                const currentWatermark = new Date(currentState[ListConstants.updateDtm]);
                if (watermark >= currentWatermark) {
                    listItemsToUpdate.push({
                        ...item,
                        watermark: watermark.toISOString(),
                    });
                }
            }
            else {
                listItemsToUpdate.push({
                    ...item,
                    watermark: watermark.toISOString(),
                });
            }
        });
        return listItemAdapter.upsertMany(listItemsToUpdate, {
            ...state,
            watermark: watermark.toISOString(),
            loaded: true,
            loading: false,
            version,
        });
    }
    else {
        return state;
    }
}), on(LIST_ITEM_ACTIONS.setIsSelected, (state, action) => {
    const listItemsState = [];
    if (!action.key) {
        for (const key in state.entities) {
            if (state.entities.hasOwnProperty(key) && !action.listGroupKey) {
                listItemsState.push({
                    ...state.entities[key],
                    isSelected: action.value,
                });
            }
            else if (state.entities.hasOwnProperty(key) &&
                JSON.stringify(state.entities[key].listGroupKey) ===
                    JSON.stringify(action.listGroupKey)) {
                listItemsState.push({
                    ...state.entities[key],
                    isSelected: action.value,
                });
            }
        }
    }
    else {
        const listItem = {
            ...state.entities[action.key],
            isSelected: action.value,
        };
        listItemsState.push(listItem);
    }
    return listItemAdapter.upsertMany(listItemsState, {
        ...state,
        loaded: true,
        loading: false,
    });
}), on(LIST_ITEM_ACTIONS.update, (state, action) => listItemAdapter.upsertMany(action.listItems, {
    ...state,
})), on(LIST_ITEM_ACTIONS.delete, (state, action) => listItemAdapter.removeMany(action.listItemKeysToRemove, {
    ...state,
    loaded: false,
    loading: true,
})), on(LIST_ITEM_ACTIONS.deleteSuccess, (state, action) => ({
    ...state,
    loading: false,
    loaded: true,
})), on(LIST_ITEM_ACTIONS.replaceProductInAList, LIST_ITEM_ACTIONS.replaceProductAcrossAllLists, (state, action) => {
    const updatedListItems = [];
    action.listItems?.forEach((item) => {
        updatedListItems.push({
            ...item,
            productNumber: action.product.replacementProduct,
        });
    });
    return listItemAdapter.upsertMany(updatedListItems, state);
}), on(LIST_ITEM_ACTIONS.create, (state, action) => listItemAdapter.upsertMany(action.listItems, {
    ...state,
    loaded: false,
    loading: true,
})), on(LIST_ITEM_ACTIONS.copy, (state, action) => listItemAdapter.upsertMany(action.tempItemsToInsert, state)), on(LIST_ITEM_ACTIONS.createFail, LIST_ITEM_ACTIONS.copyFail, (state, action) => {
    const keys = [];
    action.listItemsToRemove.forEach((tempItem) => {
        keys.push(tempItem.listKey.listTypeId +
            '-' +
            tempItem.listKey.listId +
            '-' +
            tempItem.listGroupKey.listGroupId +
            '-' +
            tempItem.listItemKey.listItemId);
    });
    return listItemAdapter.removeMany(keys, {
        ...state,
        loaded: true,
        loading: false,
    });
}), on(LIST_ITEM_ACTIONS.createSuccess, (state, action) => ({
    ...state,
    loading: false,
    loaded: true,
})), on(LIST_ITEM_ACTIONS.removeItems, (state, action) => listItemAdapter.removeMany(action.keys, state)), on(LIST_ITEM_ACTIONS.replaceProductFail, LIST_ITEM_ACTIONS.deleteFail, LIST_ITEM_ACTIONS.createTempItem, LIST_ITEM_ACTIONS.deleteFail, (state, action) => listItemAdapter.upsertMany(action.listItems, {
    ...state,
    loaded: true,
    loading: false,
})), on(LIST_ACTIONS.deleteGroupAndDelete, LIST_ACTIONS.deleteGroupAndMove, (state, action) => {
    const keysToRemove = [];
    action.listItemsToDelete.forEach((item) => {
        const key = createListItemKey(item.listKey, item.listGroupKey, item.listItemKey);
        keysToRemove.push(key);
    });
    return listItemAdapter.removeMany(keysToRemove, state);
}), on(LIST_ACTIONS.deleteGroupAndMove, (state, action) => listItemAdapter.upsertMany(action.listItemsToInsert, state)), on(LIST_ACTIONS.deleteGroupFail, (state, action) => {
    state = listItemAdapter.upsertMany(action.itemsToReinsert, state);
    const keysToRemove = [];
    action.itemsToRemove.forEach((item) => {
        const key = createListItemKey(item.listKey, item.listGroupKey, item.listItemKey);
        keysToRemove.push(key);
    });
    return listItemAdapter.removeMany(keysToRemove, state);
}), on(LIST_ITEM_ACTIONS.createNoteSuccess, LIST_ITEM_ACTIONS.updateNoteSuccess, LIST_ITEM_ACTIONS.deleteNoteSuccess, (state, action) => {
    const itemsToUpsert = [];
    for (const key in state.entities) {
        if (key && state.entities[key] !== undefined) {
            // eslint-disable-next-line prefer-const
            let item = { ...state.entities[key] };
            if (item.productNumber === action.productNumber &&
                item.listKey.listId === action.list.listKey.listId) {
                item.note = action.note;
                itemsToUpsert.push(item);
            }
        }
    }
    return listItemAdapter.upsertMany(itemsToUpsert, state);
}), on(LIST_ACTIONS.betterBuyAcceptSuccess, (state, action) => {
    const itemsToUpsert = [];
    const listKeys = action.lists.map((val) => val.listKey.listId);
    for (const key in state.entities) {
        if (key && state.entities[key] !== undefined) {
            // eslint-disable-next-line prefer-const
            let item = { ...state.entities[key] };
            const bb = action.betterBuyItems.find((item2) => item2.oldProdNbr === item.productNumber);
            if (bb !== undefined && listKeys.includes(item.listKey.listId)) {
                item.productNumber = bb.newProdNbr;
                itemsToUpsert.push(item);
            }
        }
    }
    return listItemAdapter.upsertMany(itemsToUpsert, state);
}), on(LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFromDragAndDrop, LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFromLineNumber, LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFromModal, (state, action) => {
    if (action.stateToChange.itemsToAdd.length > 0) {
        state = listItemAdapter.addMany(action.stateToChange.itemsToAdd, state);
    }
    const keysToRemove = action.stateToChange.itemsToRemove.map((value) => createListItemKey(value.listKey, value.listGroupKey, value.listItemKey));
    return listItemAdapter.removeMany(keysToRemove, {
        ...state,
    });
}), on(LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFail, (state, action) => {
    if (action.stateToRevert.itemsToRemove.length > 0) {
        const keysToRemove = action.stateToRevert.itemsToRemove.map((value) => createListItemKey(value.listKey, value.listGroupKey, value.listItemKey));
        state = listItemAdapter.removeMany(keysToRemove, state);
    }
    return listItemAdapter.addMany(action.stateToRevert.itemsToAdd, {
        ...state,
    });
}));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function listItemReducer(state, action) {
    return _listItemReducer(state, action);
}
const createListItemKey = (listKey, listGroupKey, listItemKey) => [
    listKey.listTypeId,
    listKey.listId,
    listGroupKey.listGroupId,
    listItemKey.listItemId,
].join('-');

const orderGuideItemAdapter = createEntityAdapter({
    selectId: (orderGuideItem) => orderGuideItem?.listKey?.listTypeId +
        '-' +
        orderGuideItem?.listKey?.listId +
        '-' +
        orderGuideItem?.listGroupKey?.listGroupId +
        '-' +
        orderGuideItem?.listItemKey?.listItemId,
});
const initialOrderGuideItemState = orderGuideItemAdapter.getInitialState({
    loaded: false,
    loading: false,
    error: null,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _orderGuideItemReducer = createReducer(initialOrderGuideItemState, on(ORDER_GUIDE_ITEM_ACTIONS.load, (state) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(ORDER_GUIDE_ITEM_ACTIONS.loadSuccess, (state, action) => orderGuideItemAdapter.upsertMany(action.orderGuideItems, {
    ...state,
    loaded: true,
    loading: false,
})), on(ORDER_GUIDE_ITEM_ACTIONS.loadFail, (state, action) => ({
    ...state,
    loading: false,
    loaded: true,
    error: action.error?.message,
})), on(ORDER_GUIDE_ITEM_ACTIONS.resetStore, (state, action) => initialOrderGuideItemState), on(ORDER_GUIDE_ITEM_ACTIONS.saveProductMap, (state, action) => ({
    ...state,
    productsMap: action.productsMap,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function orderGuideItemReducer(state, action) {
    return _orderGuideItemReducer(state, action);
}

const selectOrderGuideItemState = createFeatureSelector(ORDER_GUIDE_ITEM_KEY);
const orderGuideItemsLoaded = () => createSelector(selectOrderGuideItemState, (orderGuideItemState) => orderGuideItemState.loaded);
const { selectIds: selectOrderGuideItemIds, selectEntities: selectOrderGuideItemEntities, selectAll: selectAllOrderGuideItems, selectTotal: selectTotalOrderGuideItems, } = orderGuideItemAdapter.getSelectors(selectOrderGuideItemState);

/* eslint-disable no-shadow */
const sortByPurchaseDateTime = (e1, e2) => (e1.purchaseDtm1 < e2.purchaseDtm1 ? -1 : 1);
const recentPurchaseAdapter = createEntityAdapter({
    selectId: (product) => product.productNumber,
    sortComparer: sortByPurchaseDateTime,
});
const initialRecentPurchaseState = recentPurchaseAdapter.getInitialState({
    // add additional entity state properties here if needed
    loaded: false,
    isSelected: false,
    searchSortFilterOptions: {
        searchKey: '',
        sortType: ListConstants.date,
        filters: [],
        groupToSort: ListConstants.allGroups,
        week52Filter: true,
    },
    selections: {},
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _recentPurchaseReducer = createReducer(initialRecentPurchaseState, on(RECENT_PURCHASE_ACTIONS.loadRecentPurchase, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(RECENT_PURCHASE_ACTIONS.loadRecentPurchaseSuccess, (state, action) => {
    const recentPurchaseState = action.recentPurchase.map((product) => ({
        ...product,
        isSelected: false,
    }));
    if (recentPurchaseState.length > 0) {
        return recentPurchaseAdapter.upsertMany(recentPurchaseState, {
            ...state,
            loading: false,
            loaded: true,
        });
    }
    else {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    }
}), on(RECENT_PURCHASE_ACTIONS.loadFromStorageSuccess, (state, action) => ({
    ...state,
    ...action.recentPurchase,
    loading: false,
    loaded: true,
})), on(RECENT_PURCHASE_ACTIONS.resetStore, (state, action) => initialRecentPurchaseState), on(RECENT_PURCHASE_ACTIONS.loadRecentPurchaseFail, (state, action) => ({
    ...state,
    loading: false,
    loaded: false,
    error: action.error,
})), on(RECENT_PURCHASE_ACTIONS.sortAndFilterRecentPurchase, (state, action) => ({
    ...state,
    searchSortFilterOptions: {
        ...state.searchSortFilterOptions,
        sortType: action.sortType,
        groupToSort: action.groupToSort,
        filters: action.filters,
        week52Filter: action.week52Filter,
    },
})), on(RECENT_PURCHASE_ACTIONS.searchInRecentPurchase, (state, action) => {
    let newState = state;
    const startOfSearchOrSearchReset = action.tracking?.tracing.data.isStartOfTrace || !action.tracking;
    if (startOfSearchOrSearchReset) {
        newState = {
            ...state,
            searchSortFilterOptions: {
                ...state.searchSortFilterOptions,
                searchKey: action.searchKey,
            },
        };
    }
    return newState;
}), on(RECENT_PURCHASE_ACTIONS.setIsSelectedForARecentPurchase, (state, action) => ({
    ...state,
    selections: {
        ...state.selections,
        [action.key]: action.value,
    },
})), on(RECENT_PURCHASE_ACTIONS.setIsSelectedForAllRecentPurchases, (state, action) => {
    const selections = { ...state.selections };
    state.ids.forEach((key) => {
        const rpToBeUpdated = state.entities[key];
        selections[rpToBeUpdated.productNumber] = action.value;
    });
    return { ...state, selections };
}), on(RECENT_PURCHASE_ACTIONS.setIsSelectedForMultipleRecentPurchases, (state, action) => {
    const selections = { ...state.selections };
    action.keys.forEach((key) => {
        selections[key] = action.value;
    });
    return { ...state, selections };
}), on(RECENT_PURCHASE_ACTIONS.selectAList, (state, action) => ({
    ...state,
    isSelected: action.value,
})), on(RECENT_PURCHASE_ACTIONS.delete, (state, action) => {
    const productIds = action.products.map((product) => product.productNumber);
    state = recentPurchaseAdapter.removeMany(productIds, state);
    return {
        ...state,
    };
}), on(RECENT_PURCHASE_ACTIONS.deleteFail, (state, action) => {
    state = recentPurchaseAdapter.upsertMany(action.products, state);
    return {
        ...state,
    };
}));
const cacheActions$1 = {};
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function recentPurchaseReducer(state, action) {
    // persist list state here
    // determine if action is a list action
    const result = _recentPurchaseReducer(state, action);
    if (cacheActions$1[action.type]) {
    }
    return result;
}

const selectRecentPurchaseState = createFeatureSelector(RECENT_PURCHASE_KEY);
const recentPurchaseLoaded = () => createSelector(selectRecentPurchaseState, (products) => products.loaded);
const allRecentPurchases = () => createSelector(selectRecentPurchaseState, (products) => Object.values(products));
const recentPurchaseWatermark = () => createSelector(selectRecentPurchaseState, (recentPurchaseState) => recentPurchaseState.watermark);
const selectRecentPurchaseSearchSortFilterOptions = createSelector(selectRecentPurchaseState, (recentPurchaseState) => recentPurchaseState.searchSortFilterOptions);
const selectIsRecentPurchaseListSelected = createSelector(selectRecentPurchaseState, (recentPurchaseState) => recentPurchaseState.isSelected);
const { selectIds: selectRecentPurchaseIds, selectEntities: selectRecentPurchaseEntities, selectAll: selectAllLRecentlyPurchased, selectTotal: selectTotalRecentlyPurchased, } = recentPurchaseAdapter.getSelectors(selectRecentPurchaseState);

const sessionsHaveNotChanged = (previousSession, latestSession) => previousSession?.selectedCustomerId === latestSession?.selectedCustomerId &&
    previousSession?.division === latestSession?.division &&
    previousSession?.department === latestSession?.department &&
    previousSession?.ecomUserId === latestSession?.ecomUserId;
const textToCamelCase = (text) => text
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => index === 0 ? word.toLowerCase() : word.toUpperCase())
    .replace(/\s+/g, '');
const getListProductNumbersSeparated = (selectedListId, selectedListType, listEntities, groupEntities, itemEntities) => {
    const listKey = `${selectedListType}-${selectedListId}`;
    const list = listEntities?.[listKey];
    if (!list?.listGroupKeys || !Array.isArray(list.listGroupKeys)) {
        return { usfProductNumbers: [], nonUsfProductNumbers: [] };
    }
    const usfProductNumbersSet = new Set();
    const nonUsfProductNumbersSet = new Set();
    list.listGroupKeys.forEach((groupKey) => {
        if (!groupKey?.listGroupId) {
            return;
        }
        const groupEntityKey = `${listKey}-${groupKey.listGroupId}`;
        const group = groupEntities[groupEntityKey];
        if (group) {
            getProductNumbersFromGroupSeparated(groupEntityKey, group, itemEntities, usfProductNumbersSet, nonUsfProductNumbersSet);
        }
    });
    return {
        usfProductNumbers: Array.from(usfProductNumbersSet),
        nonUsfProductNumbers: Array.from(nonUsfProductNumbersSet),
    };
};
const getProductNumbersFromGroupSeparated = (groupEntityKey, group, itemEntities, usfProductNumbers, nonUsfProductNumbers) => {
    if (!group?.listItemKeys || !Array.isArray(group.listItemKeys)) {
        return;
    }
    group.listItemKeys.forEach((itemKey) => {
        if (!itemKey?.listItemId) {
            return;
        }
        const itemEntityKey = `${groupEntityKey}-${itemKey.listItemId}`;
        const item = itemEntities[itemEntityKey];
        if (item?.productNumber) {
            // TODO: Remove this once we have a proper way to determine if a product is non-USF
            if (item.nonUsfProduct || item.productNumber.toString().length === 10) {
                nonUsfProductNumbers.add(item.productNumber);
            }
            else {
                usfProductNumbers.add(item.productNumber);
            }
        }
    });
};
const getRecentPurchaseProductNumbersSeparated = (ids) => {
    if (!ids || !Array.isArray(ids)) {
        return { usfProductNumbers: [], nonUsfProductNumbers: [] };
    }
    const productNumbers = ids.map((id) => Number(id));
    return {
        usfProductNumbers: productNumbers,
        nonUsfProductNumbers: [],
    };
};
const createListName = (list) => list?.listKey?.listTypeId === ListConstants.orderGuideListType
    ? ListConstants.orderGuide + ' #' + list.listKey.listId
    : list?.listName;

const selectListItemsState = createFeatureSelector(LIST_ITEM_KEY);
const selectSortedListItems = createSelector(selectListItemsState, (listItemsState) => Object.values(listItemsState).sort((a, b) => {
    const diff = a.itemSequenceNumber - b.itemSequenceNumber;
    return diff / (Math.abs(diff) || 1);
}));
const selectListItemsInListAndGroup = createSelector(selectListEntities, (listItems, props) => Object.keys(listItems).reduce((filtered, key) => {
    if (listItems[key].listId === props.listId &&
        listItems[key].groupId === props.groupId) {
        filtered[key] = listItems[key];
    }
    return filtered;
}, {}));
const listItemsLoaded = () => createSelector(selectListItemsState, (items) => items.loaded);
const selectListItemProductMap = () => createSelector(selectListItemsState, shouldShowOrderGuides, selectListsState, selectRecentPurchaseState, (listItemState, showOG, listState, recentPurchaseState) => {
    if (listState.loaded && recentPurchaseState.loaded) {
        const productMap = {};
        listItemState.ids?.forEach((id) => {
            const listItem = listItemState.entities[id];
            if (listItem &&
                (listItem?.listKey?.listTypeId !==
                    ListConstants.orderGuideListType ||
                    showOG)) {
                const listForItem = listState.entities[createListKey(listItem.listKey)];
                const listName = createListName(listForItem);
                if (listName) {
                    const existingProductInMap = productMap[listItem.productNumber];
                    const customerProductNumber = listItem.customerProductNumber ?? null;
                    let listItemProduct;
                    if (existingProductInMap) {
                        const updatedLists = existingProductInMap.lists;
                        if (!updatedLists.includes(listName)) {
                            updatedLists.push(listName);
                        }
                        if (listForItem?.listKey?.listTypeId ===
                            ListConstants.orderGuideListType) {
                            listItemProduct = {
                                ...existingProductInMap,
                                isOnOrderGuide: true,
                                customerProductNumber,
                                lists: updatedLists,
                            };
                        }
                        else {
                            listItemProduct = {
                                ...existingProductInMap,
                                isOnList: true,
                                lists: updatedLists,
                            };
                        }
                    }
                    else {
                        let isOnOrderGuide = false;
                        if (listItem.listKey.listTypeId ===
                            ListConstants.orderGuideListType) {
                            isOnOrderGuide = true;
                        }
                        listItemProduct = {
                            isOnList: listItem.listKey.listTypeId ===
                                ListConstants.normalListTypeId
                                ? true
                                : false,
                            isOnOrderGuide,
                            lists: [listName],
                        };
                    }
                    if (listItemProduct.isOnOrderGuide && customerProductNumber) {
                        listItemProduct.customerProductNumber = customerProductNumber;
                    }
                    productMap[listItem.productNumber] = listItemProduct;
                }
            }
        });
        recentPurchaseState?.ids.forEach((id) => {
            const existingProductInMap = productMap[id];
            const listName = ListConstants.recentPurchase;
            if (existingProductInMap) {
                const updatedLists = existingProductInMap.lists;
                updatedLists.push(listName);
                const updatedProduct = {
                    ...existingProductInMap,
                    isOnList: true,
                    lists: updatedLists,
                };
                productMap[id] = updatedProduct;
            }
            else {
                const listItemProduct = {
                    isOnList: true,
                    isOnOrderGuide: false,
                    lists: [listName],
                };
                productMap[id] = listItemProduct;
            }
        });
        return productMap;
    }
});
const selectOrderGuideItemProductMap = () => createSelector(selectOrderGuideItemState, (orderGuideItemState) => orderGuideItemState.productsMap);
// Returns a combined version of the product map from item state and order guide state
const listItemProductMap = () => createSelector(selectListItemProductMap(), selectOrderGuideItemProductMap(), (listProductMap, orderGuideProductMap) => {
    const combinedMap = Object.assign({}, listProductMap);
    for (const key in orderGuideProductMap) {
        if (orderGuideProductMap.hasOwnProperty(key)) {
            const valueInOrderGuideItemMap = orderGuideProductMap[key];
            const valueInListItemMap = combinedMap[key];
            if (!valueInListItemMap) {
                combinedMap[key] = valueInOrderGuideItemMap;
            }
            else {
                const combinedLists = valueInListItemMap.lists.concat(valueInOrderGuideItemMap.lists);
                const newValue = {
                    isOnList: true,
                    isOnOrderGuide: true,
                    lists: combinedLists,
                    customerProductNumber: valueInOrderGuideItemMap.customerProductNumber,
                };
                combinedMap[key] = newValue;
            }
        }
    }
    return combinedMap;
});
const selectCombinedListAndOgItems = () => createSelector(listItemAdapter.getSelectors(selectListItemsState).selectEntities, orderGuideItemAdapter.getSelectors(selectOrderGuideItemState)
    .selectEntities, (listItems, orderGuideItems) => Object.assign({}, listItems, orderGuideItems));
const listItemsWatermark = () => createSelector(selectListItemsState, (items) => items?.watermark);
const productNumbersAccrossLists = () => createSelector(selectListItemsState, (items) => {
    const productNumbers = Object.values(items.entities).map((item) => item.productNumber);
    return productNumbers.filter((val, i) => productNumbers.indexOf(val) === i);
});
const { selectIds: selectListItemIds, selectEntities: selectListItemEntities, selectAll: selectAllListItems, selectTotal: selectTotalListItems, } = listItemAdapter.getSelectors(selectListItemsState);

class ListItemFacadeService {
    constructor(browserStorageService, store) {
        this.browserStorageService = browserStorageService;
        this.store = store;
        this.allListItems$ = this.store.select(selectAllListItems);
        this.listItemsLoaded$ = this.store.select(listItemsLoaded());
    }
    loadListItems(watermark) {
        this.store.dispatch(LIST_ITEM_ACTIONS.loadFromStorage());
    }
}
ListItemFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListItemFacadeService, deps: [{ token: i1.BrowserStorageService }, { token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ListItemFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListItemFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListItemFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.BrowserStorageService }, { type: i1$1.Store }]; } });

class RecentPurchaseFacadeService {
    constructor(store) {
        this.store = store;
        this.allRecentPurchase$ = this.store.select(selectAllLRecentlyPurchased);
        this.recentPurchaseState$ = this.store.select(selectRecentPurchaseState);
        this.recentPurchaseLoaded$ = this.store.select(recentPurchaseLoaded());
        this.recentPurchaseWatermark$ = this.store.select(recentPurchaseWatermark());
    }
    loadRecentPurchase() {
        this.store.dispatch(RECENT_PURCHASE_ACTIONS.loadRecentPurchase());
    }
    loadFromStorage() {
        this.store.dispatch(RECENT_PURCHASE_ACTIONS.loadFromStorage());
    }
}
RecentPurchaseFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RecentPurchaseFacadeService, deps: [{ token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
RecentPurchaseFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RecentPurchaseFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RecentPurchaseFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Store }]; } });

/* eslint-disable no-shadow */
const sellerRecentPurchaseAdapter = createEntityAdapter({
    selectId: (product) => product.productNumber,
});
const initialSellerRecentPurchaseState = sellerRecentPurchaseAdapter.getInitialState({
    // add additional entity state properties here if needed
    loaded: false,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _sellerRecentPurchaseReducer = createReducer(initialSellerRecentPurchaseState, on(SELLER_RECENT_PURCHASE_ACTIONS.loadRecentPurchase, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(SELLER_RECENT_PURCHASE_ACTIONS.loadRecentPurchaseSuccess, (state, action) => {
    const sellerRecentPurchaseState = action.recentPurchase.map((product) => ({
        ...product,
        loading: false,
        loaded: true,
        purchases: action.recentPurchase,
    }));
    if (sellerRecentPurchaseState.length > 0) {
        return sellerRecentPurchaseAdapter.upsertMany(sellerRecentPurchaseState, {
            ...state,
            loading: false,
            loaded: true,
            customerNumber: sellerRecentPurchaseState[0].customerNumber,
            divisionNumber: sellerRecentPurchaseState[0].divisionNumber,
            departmentNumber: sellerRecentPurchaseState[0].departmentNumber,
        });
    }
    else {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    }
}), on(SELLER_RECENT_PURCHASE_ACTIONS.loadRecentPurchaseFail, (state, action) => ({
    ...state,
    loading: false,
    loaded: false,
    error: action.error,
})));
const cacheActions = {};
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function sellerRecentPurchaseReducer(state, action) {
    // persist list state here
    // determine if action is a list action
    const result = _sellerRecentPurchaseReducer(state, action);
    if (cacheActions[action.type]) {
    }
    return result;
}

const selectSellerRecentPurchaseState = createFeatureSelector(SELLER_RECENT_PURCHASE_KEY);
const sellerRecentPurchaseLoaded = () => createSelector(selectSellerRecentPurchaseState, (products) => products.loaded);
const allSellerRecentPurchases = () => createSelector(selectSellerRecentPurchaseState, (products) => Object.values(products));
const sellerRecentPurchaseWatermark = () => createSelector(selectSellerRecentPurchaseState, (recentPurchaseState) => recentPurchaseState.watermark);
const { selectIds: selectSellerRecentPurchaseIds, selectEntities: selectSellerRecentPurchaseEntities, selectAll: selectAllSellerRecentlyPurchased, selectTotal: selectTotalSellerRecentlyPurchased, } = sellerRecentPurchaseAdapter.getSelectors(selectSellerRecentPurchaseState);

class SellerRecentPurchaseFacadeService {
    constructor(browserStorageService, store) {
        this.browserStorageService = browserStorageService;
        this.store = store;
        this.allSellerRecentPurchase$ = this.store.select(selectAllSellerRecentlyPurchased);
        this.sellerRecentPurchaseLoaded$ = this.store.select(sellerRecentPurchaseLoaded());
        this.sellerRecentPurchaseWatermark$ = this.store.select(sellerRecentPurchaseWatermark());
    }
    loadRecentPurchase(productNumber) {
        this.store.dispatch(SELLER_RECENT_PURCHASE_ACTIONS.loadRecentPurchase({ productNumber }));
        return Promise.resolve();
    }
}
SellerRecentPurchaseFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerRecentPurchaseFacadeService, deps: [{ token: i1.BrowserStorageService }, { token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
SellerRecentPurchaseFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerRecentPurchaseFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerRecentPurchaseFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.BrowserStorageService }, { type: i1$1.Store }]; } });

const ANALYTICS_ACTIONS = {
    trackListsPageLoad: createAction('[NgRxList - Analytics] - Track List Page Load', props()),
    trackListDetailPageLoad: createAction('[NgRxList - Analytics] - Track List Detail Page Load', props()),
    trackListManagementPageLoad: createAction('[NgRxList - Analytics] - Track List Management Page Load', props()),
    trackAddProductToOrder: createAction('[NgRxList - Analytics] - Track Add Product To Order', props()),
    track: createAction('[NgRxList - Tracking] - Track Action', props()),
    trackRequestCreditPageLoad: createAction('[NgRxList - Analytics] - Track Request Credit Page Load', props()),
    trackRequestCreditStart: createAction('[NgRxList - Analytics] - Track Request Service Request Start', props()),
    trackRequestCreditConfirmPageLoad: createAction('[NgRxList - Analytics] - Track Request Credit Confirmation Page Load', props()),
    trackListDetailsFilters: createAction('[NgRxList - Analytics] - Track List Details Filters Applied', props()),
    trackQuickViewClick: createAction('[NgRxList - Analytics] - Track Quick View Modal Open', props()),
    trackQuickViewClosed: createAction('[NgRxList - Analytics] - Track Quick View Modal Closed', props()),
    trackMasterListsPageLoaded: createAction('[NgRxList - Analytics] - Track Master Lists Page Loaded', props()),
    trackDownloadPrint: createAction('[NgRxList - Analytics] - Track Download/Print in Lists', props()),
    trackSearchSubs: createAction('[NgRxList - Analytics] - Track Search for Subs', props()),
    trackAddToList: createAction('[NgRxList - Analytics] - Track add to list', props()),
    trackSubmitPickupRequest: createAction('[NgRxList - Analytics] - Track submit pickup request', props()),
    trackCopyListSuccess: createAction('[NgRxList - Analytics] - Track Copy List Success', props()),
    trackSearchByProductNumberPageLoaded: createAction('[NgRxList - Analytics] - Track Search by Product Numbers Page Views', props()),
    trackSupportCenterPageLoaded: createAction('[NgRxList - Analytics] - Track Support Center Page Loaded', props()),
    trackGenerateAIListButton: createAction('[NgRxList - Analytics] - Track Users Click on the Generate AI List', props()),
    trackSupportCenterResubmitRequest: createAction('[NgRxList - Analytics] - Track Resubmitting Requests from the Support Center', props()),
    trackSubmitSupportRequest: createAction('[NgRxList - Analytics] - Track Support Request Submission', props()),
    trackGenerateAIListBuilder: createAction('[NgRxList - Analytics] - Track User Starting the AOG Flow', props()),
    trackSortFilterPricingMode: createAction('[NgRxList - Analytics] - Track User Filters Applied on Filter/Sort options from Pricing', props()),
};

/**
 * LISTS PAGE LOAD
 *
 * @param tracking
 * @returns Data Layer needed for tracking the Lists Page Load
 */
const listPageLoad = (tracking) => ({
    page: {
        pageName: tracking.data.pageName,
        numberOfLists: Object.values(tracking.resultState[LIST_KEY].ids).length,
    },
    event: tracking.data.event,
    capability: tracking.data.capability,
});
/**
 * LIST DETAILS PAGE LOAD
 *
 * @param tracking
 * @returns Data Layer needed for tracking the List Details Page Load
 */
const listDetailPageLoad = (tracking) => {
    let listTracking = {
        id: tracking.data.listId,
    };
    if (tracking.data.listId.includes('SL')) {
        listTracking = {
            ...listTracking,
            listType: tracking.resultState['lists'].entities[tracking.data.listId]?.listState,
        };
    }
    let pageTrackingData = {
        page: {
            pageName: tracking.data.pageName,
        },
        list: listTracking,
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    if (tracking.data.products?.length > 0) {
        pageTrackingData = {
            ...pageTrackingData,
            products: tracking.data.products,
        };
    }
    return pageTrackingData;
};
/**
 * LIST MANAGEMENT PAGE LOAD
 *
 * @param tracking
 * @returns Data Layer needed for tracking the List Details Page Load
 */
const listManagementPageLoad = (tracking) => ({
    page: {
        pageName: tracking.data.pageName,
    },
    list: {
        id: tracking.data.listId,
        listType: tracking.resultState['lists'].entities[tracking.data.listId]?.listState,
    },
    event: tracking.data.event,
    capability: tracking.data.capability,
});
const editListName = (tracking) => {
    const listState = tracking.previousState['lists'];
    const listId = tracking.data.listId;
    return {
        action: {
            type: tracking.action.type,
        },
        list: {
            id: tracking.data.listId,
            previousListName: listState.entities[listId].listName,
            listName: tracking.data.listName,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
};
const trackChangeQuantity = (tracking) => {
    const listState = tracking.resultState['lists'];
    const orderItemState = tracking.resultState['order-item'];
    const resultOrderItem = orderItemState.entities[tracking.data.productNumber];
    const productSummary = tracking.resultState['product-summaries'].entities[tracking.data.productNumber];
    let casesAdded = 0;
    if (orderItemState.hasOwnProperty('tallies')) {
        casesAdded =
            resultOrderItem.unitsOrdered.currentValue -
                resultOrderItem.unitsOrdered.previousValue;
    }
    else {
        casesAdded = resultOrderItem.unitsOrdered.currentValue;
    }
    const orderHeaderState = tracking.resultState['order-header'];
    return {
        products: [
            {
                productAPN: tracking.data.productNumber,
                divisionNumber: productSummary.divisionNumber,
                quantityUnits: 'CA',
                quantity: casesAdded,
                addMethod: 'My List',
            },
        ],
        list: {
            id: tracking.data.listId,
            listType: listState.entities[tracking.data.listId].listState,
        },
        order: {
            openMethod: 'LIST',
            id: orderHeaderState.entities[orderHeaderState.ids[0]].orderId,
        },
    };
};
/**
 * @param tracking
 * @returns Data Layer needed for tracking Page Loads
 */
const trackPageLoad = (tracking) => ({
    page: {
        pageName: tracking.data.pageName,
    },
    event: tracking.data.event,
    capability: tracking.data.capability,
});
/**
 * REQUEST CREDIT
 *
 * @param tracking
 * @returns Data Layer needed for tracking Request Credit Start
 */
const trackRequestCreditStart = (tracking) => ({
    event: tracking.data.event,
    capability: tracking.data.capability,
    order: {
        id: tracking.data.orderId,
    },
});
/**
 * @param tracking
 * @returns Data Layer needed for tracking Request Credit Confirmation Page Load
 */
const trackRequestCreditConfirmPageLoad = (tracking) => {
    const result = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        page: {
            pageName: tracking.data.pageName,
        },
        order: {
            id: tracking.data.orderId,
        },
        products: tracking.data.productData,
        file: {
            hasPhoto: 1,
            hasDocument: 1,
        },
    };
    if (!tracking.data.hasPhoto) {
        delete result.file.hasPhoto;
    }
    if (!tracking.data.hasDocument) {
        delete result.file.hasDocument;
    }
    return result;
};
/**
 * @param tracking
 * @returns Data Layer needed for tracking the display list details
 */
const filterListDetailsTransformer = (tracking) => ({
    event: tracking.data.event,
    filters: tracking.data?.filters ? tracking.data.filters : '',
    capability: tracking.data.capability,
});
const trackQuickViewClick = (tracking) => ({
    event: tracking.data?.event,
    capability: tracking.data.capability,
    modal: {
        name: tracking.data.modal,
    },
    products: tracking.data.products,
});
const trackQuickViewClosed = (tracking) => ({
    event: tracking.data?.event,
    capability: tracking.data.capability,
    modal: {
        name: tracking.data.modal,
    },
    products: tracking.data.products,
});
/**
 * @param tracking
 * @returns Data Layer needed for tracking Master lists page loaded
 */
const masterListsPageLoadedTransformer = (tracking) => {
    let mlTrackingData = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        page: {
            pageName: tracking.data.pageName,
        },
        list: {
            id: tracking.data.listId,
            listType: tracking.data.listType,
        },
    };
    if (tracking.data.products?.length > 0) {
        mlTrackingData = {
            ...mlTrackingData,
            products: tracking.data.products,
        };
    }
    return mlTrackingData;
};
/**
 * @param tracking
 * @returns Data Layer needed for tracking Download/Print
 */
const downloadPrintTransformer = (tracking) => ({
    event: tracking.data.event,
    capability: tracking.data.capability,
    list: {
        id: tracking.data.listId,
        listType: tracking.data.listType,
    },
    file: {
        type: tracking.data.entryPoint,
    },
});
const searchSubsTransformer = (tracking) => ({
    event: tracking.data?.event,
    capability: tracking.data.capability,
    search: {
        term: tracking.data.searchTerm,
        searchType: tracking.data.searchType,
    },
    products: tracking.data.products,
});
const addToListTransformer = (tracking) => ({
    event: tracking.data.event,
    capability: tracking.data.capability,
    ...(tracking.data.productData?.pageName && {
        page: {
            pageName: tracking.data.productData.pageName,
        },
    }),
    ...(!!tracking.data.productData.listID && {
        listID: tracking.data.productData.listID,
    }),
    ...(!!tracking.data.productData.listType && {
        listType: tracking.data.productData.listType,
    }),
    products: tracking.data.productData.products.map((product) => ({
        divisionApn: product.divisionApn,
        attributes: product.attributes,
        ...(!!product.merchFeatureName && {
            merchFeatureName: product.merchFeatureName,
        }),
        addMethod: tracking.data.productData?.addMethod,
        merchFeatureName: tracking.data.productData?.merchFeatureName,
    })),
    ...(tracking.data.productData.products.some((product) => !!product.resultRank) && {
        results: tracking.data.productData.products.map((product) => ({
            resultRank: product.resultRank,
        })),
    }),
});
/**
 * @param tracking
 * @returns Data Layer needed for tracking Pickup Request
 */
const pickUpRequestTransformer = (tracking) => ({
    event: tracking.data?.event,
    capability: tracking.data.capability,
});
const copyListSuccessTransformer = (tracking) => ({
    event: tracking.data.event,
    capability: tracking.data.capability,
    list: {
        id: tracking.data.copyListAnalytics.id,
        listType: tracking.data.copyListAnalytics.listType,
        copiedId: tracking.data.copyListAnalytics.copiedId,
        copyType: tracking.data.copyListAnalytics.copyType,
    },
    customerCount: tracking.data.copyListAnalytics.customerCount,
    marketCount: tracking.data.copyListAnalytics.marketCount,
});
const supportCenterPageLoadedTransformer = (tracking) => ({
    event: tracking.data.event,
    capability: tracking.data.capability,
    page: {
        pageName: tracking.data.pageName,
    },
    file: {
        fileCount: tracking.data.fileCount,
        failedCount: tracking.data.failedCount,
    },
});
const buttonClickTransformer = (tracking) => ({
    event: tracking.data.event,
    capability: tracking.data.capability,
    button: {
        buttonName: tracking.data.buttonName,
    },
});
const aiBuilderModalTransformer = (tracking) => ({
    event: tracking.data.event,
    capability: tracking.data.capability,
    modal: {
        name: tracking.data.modal,
    },
});
const supportCenterResubmitRequestTransformer = (tracking) => {
    let requestType = '';
    switch (tracking.data.serviceRequestType) {
        case ServiceRequestType.RECOVERY_REQUEST:
            requestType = AnalyticMessages.recoveryOrder;
            break;
        case ServiceRequestType.PICKUP_REQUEST:
            requestType = AnalyticMessages.pickUpRequest;
            break;
        case ServiceRequestType.REQUEST_CREDIT:
            requestType = AnalyticMessages.creditRequest;
            break;
    }
    const trackingData = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        file: {
            type: requestType,
        },
    };
    return trackingData;
};
const trackSubmitSupportRequestTransformer = (tracking) => {
    const trackingData = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        file: {
            type: `${AnalyticMessages.supportRequest}: ${tracking.data.supportRequestType}`,
        },
    };
    return trackingData;
};
const trackSortFilterPricingTransformer = (tracking) => {
    const { filters, sort } = tracking.data;
    const camelFilters = Array.isArray(filters)
        ? filters.map(textToCamelCase)
        : [];
    const filterBy = camelFilters.length
        ? `filterBy=${camelFilters.join(',')}`
        : '';
    const sortBy = sort ? `sortBy=${textToCamelCase(sort)}` : '';
    const filterName = [sortBy, filterBy].filter(Boolean).join(';');
    const trackingData = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        filters: filterName,
    };
    return trackingData;
};
const trackProductClickData = (tracking) => {
    const trackingData = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        products: [
            {
                divisionApn: `${tracking.data.divisionNumber}-${tracking.data.productNumber}`,
                attributes: tracking.data.attributes,
            },
        ],
    };
    return trackingData;
};
const trackFridayToggle = (tracking) => {
    const trackingData = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        button: {
            buttonName: tracking.data.toggleOn
                ? AnalyticMessages.fridayPricingToggleOn
                : AnalyticMessages.fridayPricingToggleOff,
        },
    };
    return trackingData;
};

class ListAnalyticsService {
    constructor(store) {
        this.store = store;
    }
    track(tracking) {
        this.store.dispatch(ANALYTICS_ACTIONS.track({ tracking }));
    }
    /**
     * LIST PAGE
     */
    trackListsPageLoad() {
        const tracking = {
            analytics: {
                data: {
                    pageName: AnalyticMessages.listOfListsPageName,
                    event: AnalyticMessages.pageLoadedEvent,
                    capability: AnalyticMessages.capability,
                },
                transformFunc: listPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackListsPageLoad({ tracking }));
    }
    /**
     * LIST DETAIL PAGE
     */
    trackListDetailPageLoad(listId, products) {
        const tracking = {
            analytics: {
                data: {
                    pageName: AnalyticMessages.listDetailsPageName,
                    event: AnalyticMessages.pageLoadedEvent,
                    capability: AnalyticMessages.capability,
                    listId,
                    products,
                },
                transformFunc: listDetailPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackListDetailPageLoad({ tracking }));
    }
    trackListDetailsFilters(filters) {
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.filtersAppliedEvent,
                    capability: AnalyticMessages.capability,
                    filters,
                },
                transformFunc: filterListDetailsTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackListDetailsFilters({ tracking }));
    }
    /**
     * LIST MANAGEMENT PAGE
     */
    trackListManagementPageLoad(listId) {
        const tracking = {
            analytics: {
                data: {
                    pageName: AnalyticMessages.listManagementPageName,
                    event: AnalyticMessages.pageLoadedEvent,
                    capability: AnalyticMessages.capability,
                    listId,
                },
                transformFunc: listManagementPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackListManagementPageLoad({ tracking }));
    }
    trackChangeQuantity(listId, productNumber) {
        const tracking = {
            analytics: {
                data: {
                    pageName: AnalyticMessages.listDetailsPageName,
                    event: AnalyticMessages.addToOrderEvent,
                    capability: AnalyticMessages.capability,
                    listId,
                    productNumber,
                },
                transformFunc: trackChangeQuantity,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackAddProductToOrder({ tracking }));
    }
    trackQuickViewOpen(products) {
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.modalOpen,
                    capability: AnalyticMessages.capabilityProduct,
                    modal: AnalyticMessages.quickView,
                    products,
                },
                transformFunc: trackQuickViewClick,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackQuickViewClick({ tracking }));
    }
    trackQuickViewClosed(products) {
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.modalClosed,
                    capability: AnalyticMessages.capabilityProduct,
                    modal: AnalyticMessages.quickView,
                    products,
                },
                transformFunc: trackQuickViewClosed,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackQuickViewClosed({ tracking }));
    }
    /**
     * MASTER LISTS
     */
    trackMasterListsPageLoaded(listId, products) {
        const tracking = {
            analytics: {
                data: {
                    pageName: AnalyticMessages.listDetailsPageName,
                    event: AnalyticMessages.pageLoadedEvent,
                    capability: AnalyticMessages.capability,
                    listType: AnalyticMessages.masterListType,
                    listId,
                    products,
                },
                transformFunc: masterListsPageLoadedTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackMasterListsPageLoaded({ tracking }));
    }
    /**
     * Download/Print
     */
    trackDownloadPrint(listId, listType, entryPoint) {
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.downloadPrint,
                    capability: AnalyticMessages.capability,
                    listType,
                    listId,
                    entryPoint,
                },
                transformFunc: downloadPrintTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackDownloadPrint({ tracking }));
    }
    /**
     * Search for Subs
     */
    trackSearchSubs(products, searchTerm) {
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.searchSubsEvent,
                    capability: AnalyticMessages.capabilityProduct,
                    searchType: AnalyticMessages.searchSubs,
                    searchTerm,
                    products,
                },
                transformFunc: searchSubsTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSearchSubs({ tracking }));
    }
    /**
     * Add to List
     */
    trackAddToList(productData) {
        const capability = productData?.capability ?? AnalyticMessages.capability;
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.addToList,
                    capability,
                    productData,
                },
                transformFunc: addToListTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackAddToList({ tracking }));
    }
    /**
     * Submit Pickup Request
     */
    trackSubmitPickupRequest() {
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.submitPickUpRequest,
                    capability: AnalyticMessages.capibilityServiceRequest,
                },
                transformFunc: pickUpRequestTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSubmitPickupRequest({ tracking }));
    }
    trackCopyListSuccess(copyListAnalytics) {
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.copyListEvent,
                    capability: AnalyticMessages.capability,
                    copyListAnalytics,
                },
                transformFunc: copyListSuccessTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackCopyListSuccess({ tracking }));
    }
    trackSearchByProductNumberPageLoaded() {
        const tracking = {
            analytics: {
                data: {
                    pageName: AnalyticMessages.searchByProductNumberPageName,
                    event: AnalyticMessages.pageLoadedEvent,
                    capability: AnalyticMessages.capability,
                },
                transformFunc: trackPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSearchByProductNumberPageLoaded({ tracking }));
    }
    /**
     * SUPPORT CENTER
     */
    trackSupportCenterPageLoaded(fileCount, failedCount) {
        const tracking = {
            analytics: {
                data: {
                    pageName: AnalyticMessages.supportCenterPageName,
                    event: AnalyticMessages.pageLoadedEvent,
                    capability: AnalyticMessages.capability,
                    fileCount,
                    failedCount,
                },
                transformFunc: supportCenterPageLoadedTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSupportCenterPageLoaded({ tracking }));
    }
    trackGenerateAIListButton() {
        const tracking = {
            analytics: {
                data: {
                    capability: AnalyticMessages.capability,
                    event: AnalyticMessages.buttonClickEvent,
                    buttonName: AnalyticMessages.generateAIList,
                },
                transformFunc: buttonClickTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackGenerateAIListButton({ tracking }));
    }
    trackSupportCenterResubmitRequest(serviceRequestType) {
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.resubmitRequestEvent,
                    capability: AnalyticMessages.capability,
                    serviceRequestType,
                },
                transformFunc: supportCenterResubmitRequestTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSupportCenterResubmitRequest({ tracking }));
    }
    trackSubmitSupportRequest(supportRequestType) {
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.submitSupportRequestEvent,
                    capability: AnalyticMessages.capability,
                    supportRequestType,
                },
                transformFunc: trackSubmitSupportRequestTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSubmitSupportRequest({ tracking }));
    }
    trackGenerateAIListBuilder() {
        const tracking = {
            analytics: {
                data: {
                    capability: AnalyticMessages.capability,
                    event: AnalyticMessages.modalOpen,
                    modal: AnalyticMessages.aiListBuilder,
                },
                transformFunc: aiBuilderModalTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackGenerateAIListBuilder({ tracking }));
    }
    trackSortFilterPricingMode(filters, sort) {
        const tracking = {
            analytics: {
                data: {
                    capability: AnalyticMessages.capability,
                    event: AnalyticMessages.filtersAppliedEvent,
                    filters,
                    sort,
                },
                transformFunc: trackSortFilterPricingTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSortFilterPricingMode({ tracking }));
    }
}
ListAnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListAnalyticsService, deps: [{ token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ListAnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListAnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListAnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Store }]; } });

const selectServiceRequestState = createFeatureSelector(SERVICE_REQUEST_KEY);
const serviceRequest = () => createSelector(selectServiceRequestState, (serviceReq) => serviceReq);
const requestCreditIssueOptions = () => createSelector(selectServiceRequestState, (serviceReq) => serviceReq.serviceRequest.requestCredit.issueOptions);
const freshnessGuaranteeIssueOptions = () => createSelector(selectServiceRequestState, (serviceReq) => serviceReq.serviceRequest.freshnessGuarantee.issueOptions);
const pickupRequestIssueOtions = () => createSelector(selectServiceRequestState, (serviceReq) => serviceReq.serviceRequest.pickUpRequest?.issueOptions ?? []);
const recoveryOrderIssueOptions = () => createSelector(selectServiceRequestState, (serviceReq) => serviceReq.serviceRequest.recoveryRequest?.issueOptions ?? []);
const supportRequestIssueOptions = () => createSelector(selectServiceRequestState, (serviceReq) => serviceReq.serviceRequest.supportRequest?.issueOptions ?? []);
const requestCreditLookup = () => createSelector(selectServiceRequestState, (serviceReq) => {
    const creditLookup = {};
    serviceReq.serviceRequest?.requestCredit?.issueOptions?.forEach((credit) => {
        credit?.issueDetails?.forEach((detail) => {
            creditLookup[detail.code + '-' + detail.reasonCode + '-' + detail.name] = {
                issue: credit.displayText,
                issueDetail: detail.displayText,
            };
        });
    });
    return creditLookup;
});
const pickupRequestLookup = () => createSelector(selectServiceRequestState, (serviceReq) => {
    const pickupLookup = {};
    serviceReq.serviceRequest?.pickUpRequest?.issueOptions?.forEach((pickup) => {
        pickup?.issueDetails?.forEach((detail) => {
            pickupLookup[detail.reasonCode] = {
                issue: pickup.displayText,
                issueDetail: detail.displayText,
            };
        });
    });
    return pickupLookup;
});
const recoveryOrderLookup = () => createSelector(selectServiceRequestState, (serviceReq) => {
    const recoveryLookup = {};
    serviceReq.serviceRequest?.recoveryRequest?.issueOptions?.forEach((recovery) => {
        recovery?.issueDetails?.forEach((detail) => {
            recoveryLookup[detail.reasonCode] = {
                issue: recovery.displayText,
                issueDetail: detail.displayText,
            };
        });
    });
    return recoveryLookup;
});
const supportRequestLookup = () => createSelector(selectServiceRequestState, (serviceReq) => {
    const supportLookup = {};
    serviceReq.serviceRequest?.supportRequest?.issueOptions?.forEach((issueOption) => {
        issueOption?.issueDetails?.forEach((detail) => {
            supportLookup[detail.reasonCode] = {
                issue: issueOption.displayText,
                issueDetail: detail.displayText,
            };
        });
    });
    return supportLookup;
});

class ServiceRequestFacadeService {
    constructor(store) {
        this.store = store;
        this.serviceRequest$ = () => this.store.select(serviceRequest());
        this.requestCreditIssueOptions$ = () => this.store.select(requestCreditIssueOptions());
        this.freshnessGuaranteeIssueOptions$ = () => this.store.select(freshnessGuaranteeIssueOptions());
        this.pickupRequestIssueOptions$ = () => this.store.select(pickupRequestIssueOtions());
        this.recoveryRequestIssueOptions$ = () => this.store.select(recoveryOrderIssueOptions());
        this.supportRequestIssueOptions$ = () => this.store.select(supportRequestIssueOptions());
    }
    loadServiceRequest() {
        this.store.dispatch(SERVICE_REQUEST_ACTIONS.load());
    }
}
ServiceRequestFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ServiceRequestFacadeService, deps: [{ token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ServiceRequestFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ServiceRequestFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ServiceRequestFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Store }]; } });

class ServiceRequestAnalyticsService {
    constructor(store) {
        this.store = store;
    }
    trackRequestCreditPageLoad() {
        const tracking = {
            analytics: {
                data: {
                    pageName: AnalyticMessages.requestCreditPageName,
                    event: AnalyticMessages.pageLoadedEvent,
                    capability: AnalyticMessages.capibilityServiceRequest,
                },
                transformFunc: trackPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackRequestCreditPageLoad({ tracking }));
    }
    trackRequestCreditStart(orderId) {
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.startRequestCreditEvent,
                    capability: AnalyticMessages.capibilityServiceRequest,
                    orderId,
                },
                transformFunc: trackRequestCreditStart,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackRequestCreditStart({ tracking }));
    }
    trackRequestCreditConfirmPageLoad(hasPhoto, productData, orderId, hasDocument) {
        const tracking = {
            analytics: {
                data: {
                    event: AnalyticMessages.pageLoadedEvent,
                    capability: AnalyticMessages.capibilityServiceRequest,
                    pageName: AnalyticMessages.requestCreditConfirmationPageName,
                    orderId,
                    hasPhoto,
                    productData,
                    hasDocument,
                },
                transformFunc: trackRequestCreditConfirmPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackRequestCreditConfirmPageLoad({ tracking }));
    }
}
ServiceRequestAnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ServiceRequestAnalyticsService, deps: [{ token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ServiceRequestAnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ServiceRequestAnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ServiceRequestAnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Store }]; } });

const masterListAdapter = createEntityAdapter({
    selectId: (masterList) => masterList.listId,
});
const initialMasterListState = masterListAdapter.getInitialState({
    loaded: false,
    searchSortFilterOptions: {
        searchKey: '',
        sortType: ListConstants.groupLine,
        filters: [],
        groupToSort: ListConstants.allGroups,
    },
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _masterListReducer = createReducer(initialMasterListState, on(MASTER_LIST_ACTIONS.resetStore, (state, action) => initialMasterListState), on(MASTER_LIST_ACTIONS.load, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(MASTER_LIST_ACTIONS.loadSuccess, (state, action) => masterListAdapter.upsertMany(action.masterLists, {
    ...state,
    loaded: true,
    loading: false,
})), on(MASTER_LIST_ACTIONS.loadFail, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
    error: action.error,
})), on(MASTER_LIST_ACTIONS.updateMany, (state, action) => masterListAdapter.upsertMany(action.masterLists, { ...state })), on(MASTER_LIST_ACTIONS.searchInMasterList, (state, action) => ({
    ...state,
    searchSortFilterOptions: {
        ...state.searchSortFilterOptions,
        searchKey: action.searchKey,
    },
})), on(MASTER_LIST_ACTIONS.sortAndFilterMasterList, (state, action) => ({
    ...state,
    searchSortFilterOptions: {
        ...state.searchSortFilterOptions,
        sortType: action.sortType,
        groupToSort: action.groupToSort,
        filters: action.filters,
    },
})), on(MASTER_LIST_ACTIONS.selectAList, (state, action) => {
    const updatedLists = [];
    state.ids.forEach((id) => {
        if (id === action.id) {
            const updatedList = { ...state.entities[id], isSelected: action.value };
            updatedLists.push(updatedList);
        }
        else {
            const updatedList = { ...state.entities[id], isSelected: false };
            updatedLists.push(updatedList);
        }
    });
    return masterListAdapter.upsertMany(updatedLists, state);
}));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function masterListReducer(state, action) {
    return _masterListReducer(state, action);
}

const masterListGroupAdapter = createEntityAdapter({
    selectId: (masterListGroup) => createMasterListGroupKey(masterListGroup.listId, masterListGroup.listGroupId),
});
const initialMasterListGroupState = masterListGroupAdapter.getInitialState({
    loaded: false,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _masterListGroupReducer = createReducer(initialMasterListGroupState, on(MASTER_LIST_GROUP_ACTIONS.resetStore, (state, action) => initialMasterListGroupState), on(MASTER_LIST_GROUP_ACTIONS.load, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(MASTER_LIST_GROUP_ACTIONS.loadSuccess, (state, action) => masterListGroupAdapter.upsertMany(action.masterListGroups, {
    ...state,
    loaded: true,
    loading: false,
})), on(MASTER_LIST_GROUP_ACTIONS.loadFail, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
    error: action.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function masterListGroupReducer(state, action) {
    return _masterListGroupReducer(state, action);
}
const createMasterListGroupKey = (masterListId, masterListGroupId) => masterListId + '-' + masterListGroupId;

const selectMasterListGroupState = createFeatureSelector(MASTER_LIST_GROUP_KEY);
const masterListGroupsLoaded = () => createSelector(selectMasterListGroupState, (masterListGroupState) => masterListGroupState.loaded);
const selectMasterListGroupByGroupId = (masterListId, masterListGroupId) => createSelector(selectMasterListGroupState, (masterListGroupState) => masterListGroupState.entities[createMasterListGroupKey(masterListId, masterListGroupId)]);
const selectMasterListGroupsFromList = (masterListId, masterListGroupIds) => createSelector(selectMasterListGroupState, (masterListGroupState) => {
    const masterListGroupsToReturn = [];
    masterListGroupIds?.forEach((masterListGroupId) => {
        const masterListGroup = masterListGroupState.entities[createMasterListGroupKey(masterListId, masterListGroupId)];
        if (masterListGroup) {
            masterListGroupsToReturn.push(masterListGroup);
        }
    });
    return masterListGroupsToReturn;
});
const { selectIds: selectMasterListGroupIds, selectEntities: selectMasterListGroupEntities, selectAll: selectAllMasterListGroups, selectTotal: selectTotalMasterListGroups, } = masterListGroupAdapter.getSelectors(selectMasterListGroupState);

const masterListItemAdapter = createEntityAdapter({
    selectId: (masterListItem) => createMasterListItemProductKey(masterListItem.listId, masterListItem.listGroupId, masterListItem.listItemId, masterListItem.listItemProductId),
});
const initialMasterListItemState = masterListItemAdapter.getInitialState({
    loaded: false,
    selections: {},
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _masterListItemReducer = createReducer(initialMasterListItemState, on(MASTER_LIST_ITEM_ACTIONS.resetStore, (state, action) => initialMasterListItemState), on(MASTER_LIST_ITEM_ACTIONS.load, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(MASTER_LIST_ITEM_ACTIONS.loadSuccess, (state, action) => masterListItemAdapter.upsertMany(action.masterListItems, {
    ...state,
    loaded: true,
    loading: false,
})), on(MASTER_LIST_ITEM_ACTIONS.loadFail, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
    error: action.error,
})), on(MASTER_LIST_ITEM_ACTIONS.setIsShowingAlternates, (state, action) => {
    const updatedListItemProducts = [];
    action.listItemProductKeys.forEach((listItemProductKey) => {
        const listItemProductInState = state.entities[listItemProductKey];
        const updatedListItemProduct = {
            ...listItemProductInState,
            isShowingAlternates: action.value,
        };
        updatedListItemProducts.push(updatedListItemProduct);
    });
    return masterListItemAdapter.upsertMany(updatedListItemProducts, state);
}), on(MASTER_LIST_ITEM_ACTIONS.cancelComparingWithAlternates, (state, action) => {
    const updatedItems = [];
    Object.keys(state.entities).forEach((key) => {
        const item = state.entities[key];
        const updatedItem = {
            ...item,
            isComparingWithAlternates: false,
        };
        updatedItems.push(updatedItem);
    });
    return masterListItemAdapter.upsertMany(updatedItems, state);
}), on(MASTER_LIST_ITEM_ACTIONS.setIsComparingWithAlternates, (state, action) => {
    const updatedItems = [];
    const updatedKey = action.listItemProductKey;
    const comparingModeEnabled = action.value;
    if (comparingModeEnabled) {
        // Only one item can be in comparing mode with its alternates in the master list at a time
        Object.keys(state.entities).forEach((key) => {
            const item = state.entities[key];
            const updatedItem = {
                ...item,
                isComparingWithAlternates: key === updatedKey,
            };
            updatedItems.push(updatedItem);
        });
        return masterListItemAdapter.upsertMany(updatedItems, state);
    }
    else {
        const item = state.entities[updatedKey];
        if (item) {
            const updatedItem = {
                ...item,
                isComparingWithAlternates: false,
            };
            return masterListItemAdapter.upsertOne(updatedItem, state);
        }
    }
    return state;
}), on(MASTER_LIST_ITEM_ACTIONS.updateItemKeyProductKeysDict, (state, action) => ({
    ...state,
    itemKeyProductKeysDict: action.itemKeyProductKeysDict,
})), on(MASTER_LIST_ITEM_ACTIONS.saveProductMap, (state, action) => ({
    ...state,
    productsMap: action.productsMap,
})), on(MASTER_LIST_ITEM_ACTIONS.setLoadedAndLoading, (state, action) => ({
    ...state,
    loaded: action.loaded,
    loading: action.loading,
})), on(MASTER_LIST_ITEM_ACTIONS.setIsSelected, (state, action) => ({
    ...state,
    selections: {
        ...state.selections,
        [action.listItemProductKey]: action.value,
    },
})), on(MASTER_LIST_ITEM_ACTIONS.resetSelections, (state, action) => ({
    ...state,
    selections: {},
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function masterListItemReducer(state, action) {
    return _masterListItemReducer(state, action);
}
const createMasterListItemKey = (masterListId, masterListGroupId, masterListItemId) => masterListId + '-' + masterListGroupId + '-' + masterListItemId;
const createMasterListItemProductKey = (masterListId, masterListGroupId, masterListItemId, masterListItemProductId) => masterListId +
    '-' +
    masterListGroupId +
    '-' +
    masterListItemId +
    '-' +
    masterListItemProductId;

const selectMasterListItemState = createFeatureSelector(MASTER_LIST_ITEM_KEY);
const masterListItemsLoaded = () => createSelector(selectMasterListItemState, (masterListItemState) => masterListItemState.loaded);
const masterListItemProductMap = () => createSelector(selectMasterListItemState, (itemState) => itemState.productsMap);
const selectItemKeyProductsDictionary = createSelector(selectMasterListItemState, (itemState) => itemState.itemKeyProductKeysDict);
const selectMasterListItemProductsById = (masterListId, masterListGroupId, masterListItemId) => createSelector(selectMasterListItemState, (masterListItemState) => {
    const masterListItemProductsToReturn = [];
    const masterListItemKey = createMasterListItemKey(masterListId, masterListGroupId, masterListItemId);
    masterListItemState.ids?.forEach((masterListItemProductKey) => {
        const masterListItemProduct = findMasterListItemProduct(String(masterListItemProductKey), masterListItemKey, masterListItemState.entities);
        if (masterListItemProduct) {
            masterListItemProductsToReturn.push(masterListItemProduct);
        }
    });
    return masterListItemProductsToReturn;
});
const findMasterListItemProduct = (masterListItemProductKey, masterListItemKey, masterListItemProductEntities) => {
    if (masterListItemProductKey.includes(masterListItemKey)) {
        return masterListItemProductEntities[masterListItemProductKey];
    }
};
const { selectIds: selectMasterListItemIds, selectEntities: selectMasterListItemEntities, selectAll: selectAllMasterListItems, selectTotal: selectTotalMasterListItems, } = masterListItemAdapter.getSelectors(selectMasterListItemState);

const selectMasterListsState = createFeatureSelector(MASTER_LIST_KEY);
const masterListsLoaded = () => createSelector(selectMasterListsState, (masterListState) => masterListState.loaded);
const selectMasterListById = (masterListId) => createSelector(selectMasterListsState, (masterListState) => masterListState.entities[masterListId]);
const selectMasterListSearchSortFilterOptions = createSelector(selectMasterListsState, (masterListState) => masterListState.searchSortFilterOptions);
const selectMasterListPrimaryCountsMap = createSelector(selectMasterListsState, selectMasterListGroupEntities, selectItemKeyProductsDictionary, (masterListState, masterListGroups, itemKeyProductKeysDict) => {
    const mapToReturn = new Map();
    if (!itemKeyProductKeysDict ||
        Object.keys(itemKeyProductKeysDict).length === 0) {
        return mapToReturn;
    }
    for (const id of masterListState.ids) {
        let count = 0;
        const masterList = masterListState.entities[id];
        if (!!masterList) {
            for (const listGroupId of masterList.listGroupIds) {
                const masterListGroupKey = createMasterListGroupKey(masterList.listId, listGroupId);
                const masterListGroup = masterListGroups[masterListGroupKey];
                if (!!masterListGroup?.listItemIds) {
                    for (const listItemId of masterListGroup.listItemIds) {
                        const masterListItemKey = createMasterListItemKey(masterList.listId, listGroupId, listItemId);
                        if (itemKeyProductKeysDict[masterListItemKey]) {
                            count++;
                        }
                    }
                }
            }
            mapToReturn.set(masterList.listId, count);
        }
    }
    return mapToReturn;
});
const { selectIds: selectMasterListIds, selectEntities: selectMasterListEntities, selectAll: selectAllMasterLists, selectTotal: selectTotalMasterLists, } = masterListAdapter.getSelectors(selectMasterListsState);

class MasterListFacadeService {
    constructor(store) {
        this.store = store;
        this.allMasterLists$ = this.store.select(selectAllMasterLists);
        this.allMasterListEntities$ = this.store.select(selectMasterListEntities);
        this.masterListState$ = this.store.select(selectMasterListsState);
        this.masterListsLoaded$ = this.store.select(masterListsLoaded());
    }
}
MasterListFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListFacadeService, deps: [{ token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
MasterListFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Store }]; } });

class MasterListGroupFacadeService {
    constructor(store) {
        this.store = store;
        this.allMasterListGroup$ = this.store.select(selectAllMasterListGroups);
        this.allMasterListGroupEntities$ = this.store.select(selectMasterListGroupEntities);
        this.masterListsLoaded$ = this.store.select(masterListGroupsLoaded());
    }
}
MasterListGroupFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListGroupFacadeService, deps: [{ token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
MasterListGroupFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListGroupFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListGroupFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Store }]; } });

class MasterListItemFacadeService {
    constructor(store) {
        this.store = store;
        this.allMasterListItems$ = this.store.select(selectAllMasterListItems);
        this.allMasterListItemEntities$ = this.store.select(selectMasterListItemEntities);
        this.masterListItemsLoaded$ = this.store.select(masterListItemsLoaded());
    }
}
MasterListItemFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListItemFacadeService, deps: [{ token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
MasterListItemFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListItemFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListItemFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Store }]; } });

const selectListViewState = createFeatureSelector(LIST_VIEW_KEY);
const selectSelectedListId = createSelector(selectListViewState, (state) => state.selectedListId);
const selectSelectedListType = createSelector(selectListViewState, (state) => state.selectedListType);
const selectSelectedListViewMode = createSelector(selectListViewState, (state) => state.selectedListViewMode);
const selectSelectedListItemKey = createSelector(selectListViewState, (state) => state.selectedListItemKey);
const selectSelectedListViewModel = createSelector(selectListViewState, recentPurchaseAdapter.getSelectors(selectRecentPurchaseState).selectIds, listAdapter.getSelectors(selectListsState).selectEntities, listGroupAdapter.getSelectors(selectListGroupsState).selectEntities, listItemAdapter.getSelectors(selectListItemsState).selectEntities, orderGuideAdapter.getSelectors(selectOrderGuideState).selectEntities, orderGuideGroupAdapter.getSelectors(selectOrderGuideGroupState)
    .selectEntities, orderGuideItemAdapter.getSelectors(selectOrderGuideItemState).selectEntities, (listViewState, recentPurchaseIds, listEntities, listGroupEntities, listItemEntities, orderGuideEntities, orderGuideGroupEntities, orderGuideItemEntities) => {
    const selectedListId = listViewState?.selectedListId;
    const selectedListType = listViewState?.selectedListType;
    const selectedListProductNumbers = {
        ...listViewState,
        separatedProductNumbers: {
            usfProductNumbers: [],
            nonUsfProductNumbers: [],
        },
        selectedListId: listViewState.selectedListId,
        selectedListType: listViewState.selectedListType,
        fridayToggleOn: listViewState.fridayToggleOn,
        selectedListViewMode: listViewState.selectedListViewMode,
    };
    if (!selectedListType) {
        return selectedListProductNumbers;
    }
    switch (selectedListType) {
        case ListType.recentPurchase:
            selectedListProductNumbers.separatedProductNumbers = getRecentPurchaseProductNumbersSeparated(recentPurchaseIds);
            break;
        case ListType.orderGuide:
            selectedListProductNumbers.separatedProductNumbers = getListProductNumbersSeparated(selectedListId, selectedListType, orderGuideEntities, orderGuideGroupEntities, orderGuideItemEntities);
            break;
        default:
            selectedListProductNumbers.separatedProductNumbers = getListProductNumbersSeparated(selectedListId, selectedListType, listEntities, listGroupEntities, listItemEntities);
            break;
    }
    return selectedListProductNumbers;
});
const selectPmeSortsAndFilters = createSelector(selectListViewState, (state) => state.pmeSortsAndFilters);

class ListViewService {
    constructor(store) {
        this.store = store;
        this.listViewState$ = this.store.select(selectListViewState);
        this.selectedListId$ = this.store.select(selectSelectedListId);
        this.selectedListType$ = this.store.select(selectSelectedListType);
        this.currentViewMode$ = this.store.select(selectSelectedListViewMode);
    }
    selectList(listId, listType, listViewMode) {
        this.store.dispatch(LIST_VIEW_ACTIONS.selectList({ listId, listType, listViewMode }));
    }
    changeViewMode(listViewMode, overrideCache) {
        this.store.dispatch(LIST_VIEW_ACTIONS.changeListViewMode({
            listViewMode,
            overrideCache: overrideCache ?? false,
            tracking: listViewMode === ListViewMode.pricing
                ? {
                    analytics: {
                        data: {
                            capability: AnalyticMessages.capability,
                            event: AnalyticMessages.pageLoadedEvent,
                            pageName: AnalyticMessages.pricingPageName,
                        },
                        transformFunc: trackPageLoad,
                    },
                }
                : {},
        }));
    }
    clearListSelection() {
        this.store.dispatch(LIST_VIEW_ACTIONS.resetListView());
    }
}
ListViewService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListViewService, deps: [{ token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ListViewService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListViewService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListViewService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Store }]; } });

const accessibleListAdapter = createEntityAdapter({
    selectId: (list) => list?.listKey?.listTypeId +
        '-' +
        list?.listKey?.listId +
        '-' +
        list.customerNumber,
});
const initialAccessibleListState = accessibleListAdapter.getInitialState({
    // add additional entity state properties here if needed
    loaded: false,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _AccessiblelistReducer = createReducer(initialAccessibleListState, on(ACCESSIBLE_LIST_ACTIONS.load, (state) => ({
    ...initialAccessibleListState,
    loading: true,
    loaded: false,
})), on(ACCESSIBLE_LIST_ACTIONS.loadSuccess, (state, action) => {
    const listState = action.lists;
    return accessibleListAdapter.upsertMany(listState, {
        ...state,
        loaded: true,
        loading: false,
    });
}), on(ACCESSIBLE_LIST_ACTIONS.loadFailure, (state, action) => ({
    ...state,
    loading: false,
    loaded: true,
    error: action.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function accessibleListReducer(state, action) {
    return _AccessiblelistReducer(state, action);
}

/* eslint-disable no-shadow */
const initialServiceRequestState = {
    serviceRequest: undefined,
    error: undefined,
};
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _serviceRequestReducer = createReducer(initialServiceRequestState, on(SERVICE_REQUEST_ACTIONS.load, (state, action) => ({
    ...state,
    serviceRequest: undefined,
})), on(SERVICE_REQUEST_ACTIONS.loadSuccess, (state, action) => ({
    ...state,
    serviceRequest: action.serviceRequest,
})), on(SERVICE_REQUEST_ACTIONS.loadFail, (state, action) => ({
    ...state,
    error: action.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function serviceRequestReducer(state, action) {
    return _serviceRequestReducer(state, action);
}

// eslint-disable-next-line max-len
const supportCenterAdapter = createEntityAdapter({
    selectId: (supportCenterContent) => supportCenterContent.mongoRecordId,
});
const initialSupportCenterState = supportCenterAdapter.getInitialState({
    loaded: false,
    loading: false,
    error: undefined,
    filter: undefined,
    supportCenterSort: initialSupportCenterSort,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _supportCenterReducer = createReducer(initialSupportCenterState, on(SUPPORT_CENTER_ACTIONS.load, (state, action) => ({
    ...state,
})), on(SUPPORT_CENTER_ACTIONS.loadSuccess, (state, action) => supportCenterAdapter.upsertMany(action.supportCenterContent, {
    ...state,
    loaded: true,
    loading: false,
})), on(SUPPORT_CENTER_ACTIONS.loadFail, (state, action) => ({
    ...state,
    error: action.error,
})), on(SUPPORT_CENTER_ACTIONS.updateFilter, (state, action) => ({
    ...state,
    filter: action.filter,
})), on(SUPPORT_CENTER_ACTIONS.resetStore, (state, action) => initialSupportCenterState), on(SERVICE_REQUEST_ACTIONS.resubmitSuccess, (state, action) => {
    const request = state.entities[action.primaryKey];
    request.isResubmitting = true;
    return supportCenterAdapter.upsertOne(request, state);
}), on(SUPPORT_CENTER_ACTIONS.updateSort, (state, action) => {
    // reset status sort to desc when other sort is selected, so it sets to asc when clicked next while still
    // defaulting to asc on page entry
    let updatedHeaders;
    if (action.selectedHeader === SupportCenterSortOptions.status) {
        updatedHeaders = {
            ...initialSupportCenterSort.headers,
            [action.selectedHeader]: {
                ...state.supportCenterSort.headers[action.selectedHeader],
                sortDirection: action.sortDirection,
            },
        };
    }
    else {
        updatedHeaders = {
            ...initialSupportCenterSort.headers,
            [action.selectedHeader]: {
                ...state.supportCenterSort.headers[action.selectedHeader],
                sortDirection: action.sortDirection,
            },
            [SupportCenterSortOptions.status]: {
                ...state.supportCenterSort.headers[SupportCenterSortOptions.status],
                sortDirection: SupportCenterConstants.desc,
            },
        };
    }
    return {
        ...state,
        supportCenterSort: {
            selectedHeader: action.selectedHeader,
            headers: updatedHeaders,
        },
    };
}));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function supportCenterReducer(state, action) {
    return _supportCenterReducer(state, action);
}

//Setting the initial state
const initialListUpdatesState = {
    listUpdates: [],
    loaded: false,
    loading: false,
    error: undefined,
};
// reducer function
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _listUpdatesReducer = createReducer(initialListUpdatesState, on(LIST_UPDATES_ACTIONS.load, (state, action) => ({
    ...state,
    listUpdates: [],
    loading: true,
    loaded: false,
})), on(LIST_UPDATES_ACTIONS.loadSuccess, (state, action) => ({
    ...state,
    listUpdates: action.listUpdates,
    loaded: true,
    loading: false,
})), on(LIST_UPDATES_ACTIONS.loadFail, (state, action) => ({
    ...state,
    error: action.error,
    loaded: true,
    loading: false,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function listUpdatesReducer(state, action) {
    return _listUpdatesReducer(state, action);
}

const initialListViewState = {
    selectedListId: null,
    selectedListType: null,
    fridayToggleOn: false,
    selectedListViewMode: ListViewMode.ordering,
    selectedListItemKey: null,
    pmeSortsAndFilters: {
        searchKey: '',
        sortType: ListConstants.groupLine,
        filters: [],
        groupToSort: ListConstants.allGroups,
    },
};

// eslint-disable-next-line no-underscore-dangle
const _listViewReducer = createReducer(initialListViewState, on(LIST_VIEW_ACTIONS.selectList, (state, action) => ({
    ...state,
    selectedListId: action.listId,
    selectedListType: action.listType,
    selectedListViewMode: action.listViewMode || ListViewMode.ordering,
    selectedListItemKey: null,
})), on(LIST_VIEW_ACTIONS.changeListViewMode, (state, action) => ({
    ...state,
    selectedListViewMode: action.listViewMode,
})), on(LIST_VIEW_ACTIONS.setSelectedListItemKey, (state, action) => ({
    ...state,
    selectedListItemKey: action.selectedListItemKey,
})), on(LIST_VIEW_ACTIONS.setFridayToggle, (state) => ({
    ...state,
    fridayToggleOn: !state.fridayToggleOn,
})), on(LIST_VIEW_ACTIONS.setPmeSortsAndFilters, (state, action) => ({
    ...state,
    pmeSortsAndFilters: action.pmeSortsAndFilters,
})), on(LIST_VIEW_ACTIONS.resetListView, () => initialListViewState));
const listViewReducer = (state, action) => _listViewReducer(state, action);

const selectListUpdateState = createFeatureSelector(LIST_UPDATES_KEY);
const listUpdates = createSelector(selectListUpdateState, (listUpdt) => listUpdt.listUpdates);
const hasListUpdates = createSelector(selectListUpdateState, (lt) => lt.listUpdates.length > 0);
const listUpdatesLoaded = createSelector(selectListUpdateState, (listUpdt) => listUpdt.loaded);
const selectListUpdatesByItemIdMap = createSelector(selectListUpdateState, (listUpdatesState) => {
    const mapToReturn = new Map();
    for (const forcedUpdate of listUpdatesState.listUpdates) {
        for (const updatedList of forcedUpdate?.updatedLists) {
            for (const list of updatedList.lists) {
                mapToReturn.set(list.listKey.listTypeId +
                    '-' +
                    list.listKey.listId +
                    '-' +
                    list.listItemId, forcedUpdate);
            }
        }
    }
    return mapToReturn;
});

const selectSupportCenterState = createFeatureSelector(SUPPORT_CENTER_KEY);
const selectSupportCenterById = (supportId) => createSelector(selectSupportCenterState, (supportState) => supportState.entities[supportId]);
const selectSupportCenterItemsAndCountsByFilter = createSelector(selectSupportCenterState, (supportState) => {
    const counts = {
        all: 0,
        failed: 0,
        new: 0,
        inProgress: 0,
        waitingForApproval: 0,
        approved: 0,
        closed: 0,
    };
    const supportData = [];
    counts.all = supportState.ids.length;
    supportState.ids?.forEach((id) => {
        const support = supportState.entities[id];
        const isNewSupportRequest = support.serviceRequestType === ServiceRequestType.SUPPORT_REQUEST &&
            support.status === SousStatus.NEW;
        switch (support.status) {
            case SousStatus.FAILED: {
                counts.failed++;
                break;
            }
            case SousStatus.NEW: {
                if (!isNewSupportRequest) {
                    counts.new++;
                }
                break;
            }
            case SousStatus.IN_PROGRESS: {
                counts.inProgress++;
                break;
            }
            case SousStatus.CREATED: {
                counts.waitingForApproval++;
                break;
            }
            case SousStatus.PENDING_CDMR_DECISION: {
                counts.waitingForApproval++;
                break;
            }
            case SousStatus.APPROVED: {
                counts.approved++;
                break;
            }
            case SousStatus.CLOSED: {
                counts.closed++;
                break;
            }
        }
        const filterInStateMapping = mapSousStatusToNumber(supportState.filter);
        const supportItemStatusMapping = mapSousStatusToNumber(support.status);
        if (!supportState.filter ||
            (supportItemStatusMapping === filterInStateMapping &&
                !isNewSupportRequest)) {
            supportData.push(support);
        }
    });
    return {
        supportItems: supportData,
        counts,
    };
});
const selectSupportCenterFilter = createSelector(selectSupportCenterState, (supportState) => supportState.filter);
const selectSupportCenterSort = createSelector(selectSupportCenterState, (supportState) => supportState.supportCenterSort);
const { selectIds: selectSupportCenterIds, selectEntities: selectSupportCenterEntities, selectAll: selectAllSupportCenter, selectTotal: selectTotalSupportCenter, } = supportCenterAdapter.getSelectors(selectSupportCenterState);

const selectAccessibleListState = createFeatureSelector(ACCESSIBLE_LIST_KEY);
const selectAllAccessibleListState = createSelector(selectAccessibleListState, (listState) => {
    const items = [];
    listState.ids.forEach((id) => {
        items.push(listState.entities[id]);
    });
    return items;
});
const selectIsAccessibleListStateLoaded = createSelector(selectAccessibleListState, (liststate) => liststate.loaded);
const { selectIds: selectAccessibleListIds, selectEntities: selectAccessibleListEntities, selectAll: selectAllAccessibleList, selectTotal: selectTotalAccessibleList, } = accessibleListAdapter.getSelectors(selectAccessibleListState);

/**
 * SEARCH IN LIST
 *
 * @param tracking
 * @returns Data Layer needed for tracking the search in list
 */
const searchInListTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    searchTerm: tracking.data.attributes.searchKey,
    searchType: tracking.data.attributes.searchType,
});
/**
 * DISPLAY LIST DETAILS
 *
 * @param tracking
 * @returns Data Layer needed for tracking the display list details
 */
const displayListDetailsTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    numberOfProducts: tracking.data.attributes.numberOfProducts,
});
/**
 * REQUEST CREDIT
 *
 * @param tracking
 * @returns Data Layer needed for tracking the request
 */
const submitRequestCreditTransformer = (tracking) => ({
    ...baseTransform(tracking),
    order: {
        id: tracking.data.attributes.orderId,
    },
    products: [{ productNumber: tracking.data.attributes.productNumber }],
});
/**
 * PICKUP REQUEST
 *
 * @param tracking
 * @returns Data Layer needed for tracking the request
 */
const submitPickupRequestTransformer = (tracking) => ({
    ...baseTransform(tracking),
    order: {
        id: tracking.data.attributes.orderId,
    },
    products: [{ productNumber: tracking.data.attributes.productNumber }],
});
/**
 * ADD PRODUCTS TO LISTS
 *
 * @param tracking
 * @returns Data Layer needed for tracking adding products to a list
 */
const addProductsToListsTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    list: {
        r4TempItemId: tracking.data.attributes.r4TempItemIds,
    },
    products: tracking.data.attributes.products,
});
/**
 * Create List
 *
 * @param tracking
 * @returns Data Layer needed for tracking creating a list
 */
const createListTransformer = (tracking) => ({
    ...baseListTransform(tracking),
});
/**
 * Delete List
 *
 * @param tracking
 * @returns Data Layer needed for tracking deleting a list
 */
const deleteListTransformer = (tracking) => ({
    ...baseListTransform(tracking),
});
/**
 * Create Group
 *
 * @param tracking
 * @returns Data Layer needed for tracking creating a group
 */
const createGroupTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    groupName: tracking.data.attributes.groupName,
});
/**
 * Edit List Name
 *
 * @param tracking
 * @returns Data Layer needed for tracking editing list name
 */
const editListNameTransformer = (tracking) => ({
    ...baseListTransform(tracking),
});
/**
 * Edit List Type
 *
 * @param tracking
 * @returns Data Layer needed for tracking editing list Type
 */
const editListTypeTransformer = (tracking) => ({
    ...baseListTransform(tracking),
});
/**
 * @param tracking
 * @returns Data Layer needed for Delete ListGroup
 */
const deleteListGroupTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    groupId: tracking.data.attributes.groupId,
});
/**
 * @param tracking
 * @returns Data Layer needed for copying items in a list
 */
const copyItemsTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    productNumbers: tracking.data.attributes.productNumbers,
    sourceGroupId: tracking.data.attributes.sourceGroupId,
    targetGroupIds: tracking.data.attributes.targetGroupIds,
});
/**
 * @param tracking
 * @returns Data Layer needed for tracking deleting items in a list
 */
const deleteItemsTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    productNumbers: tracking.data.attributes.productNumbers,
    groupId: tracking.data.attributes.groupId,
});
/**
 * Edit Group Name
 *
 * @param tracking
 * @returns Data Layer needed for tracking editing a group name
 */
const editGroupNameTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    groupId: tracking.data.attributes.groupId,
});
/**
 * Edit Group Sequence
 *
 * @param tracking
 * @returns Data Layer needed for tracking editing a group name
 */
const editGroupSequenceTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    groupId: tracking.data.attributes.groupId,
});
/**
 * Replace item on a single list
 *
 * @param tracking
 * @returns Data Layer needed for tracking replacing item on a list
 */
const replaceItemOnListTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    originalProductNumber: tracking.data.attributes.originalProductNumber,
    replacementProductNumber: tracking.data.attributes.replacementProductNumber,
});
/**
 * Replace item on all lists
 *
 * @param tracking
 * @returns Data Layer needed for tracking replacing item on all lists
 */
const replaceItemOnAllListsTransformer = (tracking) => ({
    ...baseTransform(tracking),
    listIds: tracking.data.attributes.listIds,
    originalProductNumber: tracking.data.attributes.originalProductNumber,
    replacementProductNumber: tracking.data.attributes.replacementProductNumber,
});
/**
 * @param tracking
 * @returns Data Layer needed for moving items within a group in a list
 */
const moveItemsWithinGroupTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    productNumbers: tracking.data.attributes.productNumbers,
    groupId: tracking.data.attributes.groupId,
});
/**
 * @param tracking
 * @returns Data Layer needed for moving items to another group in a list
 */
const moveItemsToAnotherGroupTransformer = (tracking) => ({
    ...baseListTransform(tracking),
    productNumbers: tracking.data.attributes.productNumbers,
    sourceGroupId: tracking.data.attributes.sourceGroupId,
    targetGroupId: tracking.data.attributes.targetGroupId,
});
const baseListTransform = (tracking) => ({
    ...baseTransform(tracking),
    listId: tracking.data.attributes.listId,
    listType: tracking.data.attributes.listType,
});
const baseTransform = (tracking) => {
    let result = {
        event: tracking.data.attributes.event,
        spanName: tracking.data.attributes.spanName,
        traceContext: tracking.data.traceContext,
        isStartOfTrace: tracking.data.isStartOfTrace,
        isEndOfTrace: tracking.data.isEndOfTrace,
    };
    if (tracking.data.attributes?.error) {
        result = {
            ...result,
            http: {
                statusCode: tracking.data.attributes.error.status,
                statusText: tracking.data.attributes.error.message,
            },
        };
    }
    return result;
};
const createSuccessTrackingObject = (tracking) => ({
    tracing: {
        data: {
            traceContext: tracking.tracing.data.traceContext,
            isEndOfTrace: true,
            isStartOfTrace: false,
            attributes: {
                ...tracking.tracing.data.attributes,
                event: tracking.tracing.data.attributes.event +
                    ListTracingConstants.eventSuccess,
                spanName: tracking.tracing.data.attributes.spanName +
                    ListTracingConstants.spanSuccess,
            },
        },
        transformFunc: tracking.tracing.transformFunc,
    },
});
const createFailureTrackingObject = (tracking, error) => ({
    tracing: {
        data: {
            traceContext: tracking.tracing.data.traceContext,
            isEndOfTrace: true,
            isStartOfTrace: false,
            attributes: {
                ...tracking.tracing.data.attributes,
                event: tracking.tracing.data.attributes.event +
                    ListTracingConstants.eventFailure,
                spanName: tracking.tracing.data.attributes.spanName +
                    ListTracingConstants.spanFailure,
                error: {
                    status: error.status,
                    message: error.message,
                },
            },
        },
        transformFunc: tracking.tracing.transformFunc,
    },
});

/* eslint-disable no-shadow */
var ListEndpoints;
(function (ListEndpoints) {
    ListEndpoints["betterBuy"] = "betterBuysAcceptDecline";
    ListEndpoints["copyList"] = "lists/copy";
    ListEndpoints["getModifiableListByName"] = "lists/getModifiableListByName";
    ListEndpoints["massDelete"] = "lists/massDeleteList";
    ListEndpoints["importList"] = "lists/import";
    ListEndpoints["listItems"] = "listItems";
    ListEndpoints["copyToMultipleLists"] = "listItems/copyItemsAcrossLists";
    ListEndpoints["replaceProducts"] = "replaceProducts";
    ListEndpoints["listItemsCopy"] = "listItems/copyItems";
    ListEndpoints["listItemsChanges"] = "listItems/myChanges";
    ListEndpoints["listGroups"] = "listGroups";
    ListEndpoints["listGroupsChanges"] = "listGroups/myChanges";
    ListEndpoints["lists"] = "lists";
    ListEndpoints["listsChanges"] = "lists/myChanges";
    ListEndpoints["masterLists"] = "masterLists";
    ListEndpoints["masterListGroups"] = "masterListGroups";
    ListEndpoints["masterListItems"] = "masterListItems";
    ListEndpoints["notes"] = "listItemNotes";
    ListEndpoints["orderGuideHeader"] = "orderGuides";
    ListEndpoints["orderGuideGroups"] = "orderGuideGroups";
    ListEndpoints["orderGuideItems"] = "orderGuideItems";
    ListEndpoints["supportCenterContent"] = "serviceRequest/supportCenterContent";
    ListEndpoints["listUpdates"] = "forcedUpdates/currentDepartment";
    ListEndpoints["resubmit"] = "serviceRequest/resubmitFailedServiceRequests";
    ListEndpoints["recoveryOrder"] = "recoveryRequest";
    ListEndpoints["accessible"] = "lists/getAccessibleList";
    ListEndpoints["supportRequest"] = "supportRequest";
})(ListEndpoints || (ListEndpoints = {}));

/* eslint-disable no-shadow */
var CopyListTypes;
(function (CopyListTypes) {
    CopyListTypes["massCopy"] = "mass copy";
    CopyListTypes["normalCopy"] = "copy (customer level)";
})(CopyListTypes || (CopyListTypes = {}));

/* eslint-disable no-shadow */
class ListHttpClientService {
    constructor(config, http, panAppState, store) {
        this.config = config;
        this.http = http;
        this.panAppState = panAppState;
        this.store = store;
        this.handleError = (error) => {
            if (error.status === 409) {
                // 409 is a version conflict error, so we need to load changes for all entities
                this.store.dispatch(LIST_ACTIONS.loadChanges());
                this.store.dispatch(LIST_GROUP_ACTIONS.loadChanges());
                this.store.dispatch(LIST_ITEM_ACTIONS.loadChanges());
            }
            return throwError(error);
        };
    }
    get(path, watermark) {
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url);
    }
    getItemWithParams(path, params) {
        const url = `${this.config.apiUrl}/${path}`;
        const requestOptions = this.generateSyncHeader();
        return this.http.get(url, { headers: requestOptions.headers, params });
    }
    getItemsWithParams(path, params) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http.get(url, { params });
    }
    getAsync(path, watermark, batchSize) {
        let url = `${this.config.apiUrl}/${path}?batchSize=${batchSize}`;
        if (watermark) {
            url += `&watermark=${watermark}`;
        }
        return this.http.get(url);
    }
    getObject(path, watermark) {
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url);
    }
    put(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        const requestOptions = this.generateSyncHeader();
        return this.http
            .put(url, body, { ...requestOptions })
            .pipe(catchError((error) => this.handleError(error)));
    }
    post(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        const requestOptions = this.generateSyncHeader();
        return this.http
            .post(url, body, { ...requestOptions })
            .pipe(catchError((error) => this.handleError(error)));
    }
    postWithRetry(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        const requestOptions = this.generateSyncHeader();
        return this.http
            .post(url, body, { ...requestOptions })
            .pipe(retryBackoff({
            initialInterval: 1000,
            maxRetries: 1,
            maxInterval: 1100,
            shouldRetry: () => true,
        }), catchError((error) => this.handleError(error)));
    }
    getWithRetry(path, watermark) {
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url).pipe(retryBackoff({
            initialInterval: 1000,
            maxRetries: 2,
            maxInterval: 2000,
            shouldRetry: () => true,
        }), catchError((error) => this.handleError(error)));
    }
    delete(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        const requestOptions = this.generateSyncHeader();
        return this.http
            .delete(url, { ...body, ...requestOptions })
            .pipe(catchError((error) => this.handleError(error)));
    }
    generateSyncHeader() {
        const requestOptions = {
            headers: new HttpHeaders({
                'content-type': 'application/json',
                'consumer-id': 'ecomr4',
            }),
        };
        return requestOptions;
    }
}
ListHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListHttpClientService, deps: [{ token: NGRX_LIST_LIB_CONFIG }, { token: i1$2.HttpClient }, { token: i3.PanAppState }, { token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ListHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_LIST_LIB_CONFIG]
                }] }, { type: i1$2.HttpClient }, { type: i3.PanAppState }, { type: i1$1.Store }]; } });

const WINDOW_TIMER = 1000;

class WindowingService {
    constructor(store, panAppState) {
        this.store = store;
        this.panAppState = panAppState;
        this.websocketQueue = {
            listEvents: [],
            groupEvents: [],
            itemEvents: [],
            externalProductEvents: [],
        };
        this.mapListAction = (currentAction) => ({
            [ListMessageStrings.editGroupNameInProgress]: () => ({
                message: ListMessageStrings.editListNameSuccess,
                messageType: ListMessageType.success,
            }),
            [ListMessageStrings.resequenceGroupsInProgress]: () => ({
                message: ListMessageStrings.resequenceGroupsSuccess,
                messageType: ListMessageType.success,
            }),
            [ListMessageStrings.addItemsInProgress]: () => ({
                message: ListMessageStrings.addItemsSuccess,
                messageType: ListMessageType.success,
                additionalInfo: currentAction?.additionalInfo,
            }),
            [ListMessageStrings.addGroupInProgress]: () => ({
                message: ListMessageStrings.addGroupSuccess,
                messageType: ListMessageType.success,
            }),
            [ListMessageStrings.copyItemsInProgress]: () => ({
                message: ListMessageStrings.copyItemsSuccess,
                messageType: ListMessageType.success,
            }),
            [ListMessageStrings.moveItemsInProgress]: () => ({
                message: ListMessageStrings.moveItemsSuccess,
                messageType: ListMessageType.success,
            }),
            [ListMessageStrings.deleteItemsInProgress]: () => ({
                message: ListMessageStrings.deleteItemsSuccess,
                messageType: ListMessageType.success,
            }),
            [ListMessageStrings.deleteGroupInProgress]: () => ({
                message: ListMessageStrings.deleteGroupSuccess,
                messageType: ListMessageType.success,
            }),
            [ListMessageStrings.editListNameInProgress]: () => ({
                message: ListMessageStrings.editListNameSuccess,
                messageType: ListMessageType.success,
            }),
            [ListMessageStrings.replaceProductsInProgress]: () => ({
                message: ListMessageStrings.replaceProductsSuccess,
                messageType: ListMessageType.success,
            }),
        }[currentAction.message]());
        /**
         * @remarks
         * This method maps the incoming websocket event action defined in @usf/list-types
         * to the appropriate NgRx action.  New events can be added to the map without impacting performance
         *
         * @param eventAction - The action attached to the websocket event
         * @param event - The data attached to the websocket event
         * @returns The NgRx action that will interact with the store
         */
        this.handleEvent = (eventAction, event) => ({
            [ListEventActions.ListCreated]: () => LIST_ACTIONS.createdAsync({
                lists: event.data.messageContent,
                watermark: event.data.watermark,
                contexts: event.data.contexts,
                version: event.version,
            }),
            [ListEventActions.ListUpdated]: () => LIST_ACTIONS.updatedAsync({
                lists: event.data.messageContent,
                watermark: event.data.watermark,
                contexts: event.data.contexts,
                version: event.version,
            }),
            [ListEventActions.ListDeleted]: () => LIST_ACTIONS.deletedAsync({
                lists: event.data.messageContent,
                watermark: event.data.watermark,
                contexts: event.data.contexts,
                version: event.version,
            }),
            [ListGroupEventActions.ListGroupCreated]: () => LIST_GROUP_ACTIONS.createdAsync({
                listGroups: event.data.messageContent,
                watermark: event.data.watermark,
                contexts: event.data.contexts,
                version: event.version,
            }),
            [ListGroupEventActions.ListGroupUpdated]: () => LIST_GROUP_ACTIONS.updatedAsync({
                listGroups: event.data.messageContent,
                watermark: event.data.watermark,
                contexts: event.data.contexts,
                version: event.version,
            }),
            [ListGroupEventActions.ListGroupDeleted]: () => LIST_GROUP_ACTIONS.deletedAsync({
                listGroups: event.data.messageContent,
                watermark: event.data.watermark,
                contexts: event.data.contexts,
                version: event.version,
            }),
            [ListItemEventActions.ListItemCreated]: () => LIST_ITEM_ACTIONS.createdAsync({
                listItems: event.data.messageContent,
                watermark: event.data.watermark,
                contexts: event.data.contexts,
                version: event.version,
            }),
            [ListItemEventActions.ListItemUpdated]: () => LIST_ITEM_ACTIONS.updatedAsync({
                listItems: event.data.messageContent,
                watermark: event.data.watermark,
                contexts: event.data.contexts,
                version: event.version,
            }),
            [ListItemEventActions.ListItemDeleted]: () => LIST_ITEM_ACTIONS.deletedAsync({
                listItems: event.data.messageContent,
                watermark: event.data.watermark,
                contexts: event.data.contexts,
                version: event.version,
            }),
        }[eventAction]());
    }
    processSynchronousWebsockets(events) {
        for (const event of events) {
            this.batchEvent(event);
        }
        this.dispatchWebsocketActions();
        this.resetEvents();
    }
    batchEvent(incomingEvent) {
        if (this.isCreateOrUpdateListEvent(incomingEvent)) {
            this.createUpdateListEvent = this.createNewEvent(incomingEvent, this.createUpdateListEvent);
        }
        else if (this.isDeleteListEvent(incomingEvent)) {
            this.deleteListEvent = this.createNewEvent(incomingEvent, this.deleteListEvent);
        }
        else if (this.isCreateOrUpdateGroupEvent(incomingEvent)) {
            this.createUpdateGroupEvent = this.createNewEvent(incomingEvent, this.createUpdateGroupEvent);
        }
        else if (this.isDeleteGroupEvent(incomingEvent)) {
            this.deleteGroupEvent = this.createNewEvent(incomingEvent, this.deleteGroupEvent);
        }
        else if (this.isCreateOrUpdateItemEvent(incomingEvent)) {
            this.createUpdateItemEvent = this.createNewEvent(incomingEvent, this.createUpdateItemEvent);
        }
        else if (this.isDeleteItemEvent(incomingEvent)) {
            this.deleteItemEvent = this.createNewEvent(incomingEvent, this.deleteItemEvent);
        }
    }
    createNewEvent(incomingEvent, currentEvent) {
        if (!currentEvent) {
            return incomingEvent;
        }
        const latestWatermark = this.getLatestWatermark(incomingEvent.data.watermark, currentEvent.data.watermark);
        const newMessageContent = this.getNewMessageContent(incomingEvent.data.messageContent, currentEvent.data.messageContent);
        const newBatchedEvent = {
            ...currentEvent,
            data: {
                ...currentEvent.data,
                messageContent: newMessageContent,
                watermark: latestWatermark,
            },
        };
        return newBatchedEvent;
    }
    getLatestWatermark(incomingWatermark, currentWatermark) {
        return new Date(incomingWatermark) > new Date(currentWatermark)
            ? incomingWatermark
            : currentWatermark;
    }
    getNewMessageContent(incomingMessageContent, currentMessageContent) {
        return incomingMessageContent.concat(currentMessageContent);
    }
    isCreateOrUpdateListEvent(event) {
        return (event.data.eventAction === ListEventActions.ListCreated ||
            event.data.eventAction === ListEventActions.ListUpdated);
    }
    isDeleteListEvent(event) {
        return event.data.eventAction === ListEventActions.ListDeleted;
    }
    isCreateOrUpdateGroupEvent(event) {
        return (event.data.eventAction === ListGroupEventActions.ListGroupCreated ||
            event.data.eventAction === ListGroupEventActions.ListGroupUpdated);
    }
    isDeleteGroupEvent(event) {
        return event.data.eventAction === ListGroupEventActions.ListGroupDeleted;
    }
    isCreateOrUpdateItemEvent(event) {
        return (event.data.eventAction === ListItemEventActions.ListItemCreated ||
            event.data.eventAction === ListItemEventActions.ListItemUpdated);
    }
    isDeleteItemEvent(event) {
        return event.data.eventAction === ListItemEventActions.ListItemDeleted;
    }
    dispatchWebsocketActions() {
        if (this.createUpdateListEvent) {
            this.dispatchWebsocketAction(this.createUpdateListEvent);
        }
        if (this.deleteListEvent) {
            this.dispatchWebsocketAction(this.deleteListEvent);
        }
        if (this.createUpdateGroupEvent) {
            this.dispatchWebsocketAction(this.createUpdateGroupEvent);
        }
        if (this.deleteGroupEvent) {
            this.dispatchWebsocketAction(this.deleteGroupEvent);
        }
        if (this.createUpdateItemEvent) {
            this.dispatchWebsocketAction(this.createUpdateItemEvent);
        }
        if (this.deleteItemEvent) {
            this.dispatchWebsocketAction(this.deleteItemEvent);
        }
    }
    dispatchWebsocketAction(event) {
        this.store.dispatch(this.handleEvent(event.data.eventAction, event));
    }
    resetEvents() {
        this.createUpdateListEvent = undefined;
        this.deleteListEvent = undefined;
        this.createUpdateGroupEvent = undefined;
        this.deleteGroupEvent = undefined;
        this.createUpdateItemEvent = undefined;
        this.deleteItemEvent = undefined;
    }
    processSynchronousResponse(events) {
        events?.forEach((event) => {
            this.enqueueSync(event);
        });
        this.flushSync();
    }
    process(websocketEvent) {
        // If event is batched (from my changes call) dont worry about windowing just execute or
        // if event is external product or vendor event, execute it
        if (websocketEvent?.data?.messageContent?.length > 1) {
            this.store.dispatch(this.handleEvent(websocketEvent.data.eventAction, websocketEvent));
            return;
        }
        // Select list from store so we can see if theres any current actions for it
        this.store
            .select(selectListById(websocketEvent?.data?.messageContent[0]?.listKey))
            .pipe(take(1))
            .subscribe((list) => {
            // If List does not exist in store and websocket has r4 negative id, action is for create new list
            if (!list) {
                // if temp list exists in store then map create list success action
                this.store
                    .select(selectListById({
                    listId: websocketEvent?.data?.messageContent[0]?.r4TempListId,
                    listTypeId: 'SL',
                }))
                    .pipe(take(1))
                    .subscribe((tempList) => {
                    if (!tempList) {
                        this.enqueue(websocketEvent);
                    }
                    else {
                        const newMessageContent = [
                            {
                                ...websocketEvent?.data?.messageContent[0],
                                currentAction: {
                                    message: ListMessageStrings.createListSuccess,
                                    messageType: ListMessageType.success,
                                },
                            },
                        ];
                        const newWebsocketEvent = {
                            ...websocketEvent,
                            data: {
                                ...websocketEvent.data,
                                messageContent: newMessageContent,
                            },
                        };
                        this.enqueue(newWebsocketEvent);
                    }
                    return;
                });
            }
            if (list?.currentAction) {
                if (list?.currentAction?.message ===
                    ListMessageStrings.addItemsInProgress ||
                    list?.currentAction?.message ===
                        ListMessageStrings.addGroupInProgress ||
                    list?.currentAction?.message ===
                        ListMessageStrings.deleteItemsInProgress ||
                    list?.currentAction?.message ===
                        ListMessageStrings.copyItemsInProgress ||
                    list?.currentAction?.message ===
                        ListMessageStrings.addItemsSuccess ||
                    list?.currentAction?.message ===
                        ListMessageStrings.addGroupSuccess ||
                    list?.currentAction?.message ===
                        ListMessageStrings.deleteItemsSuccess ||
                    list?.currentAction?.message === ListMessageStrings.copyItemsSuccess) {
                    // Need to process these events because we do not have this data in our store
                    // if list event, make current action for list of type success if its loading
                    if (websocketEvent?.data?.eventAction ===
                        ListEventActions.ListUpdated ||
                        websocketEvent?.data?.eventAction === ListEventActions.ListCreated) {
                        let newMessage = list?.currentAction;
                        if (list?.currentAction?.messageType === ListMessageType.loading) {
                            newMessage = this.mapListAction(list?.currentAction);
                        }
                        else {
                            newMessage = { messageType: null, message: null };
                        }
                        const newMessageContent = [
                            {
                                ...websocketEvent?.data?.messageContent[0],
                                currentAction: newMessage,
                            },
                        ];
                        const newWebsocketEvent = {
                            ...websocketEvent,
                            data: {
                                ...websocketEvent.data,
                                messageContent: newMessageContent,
                            },
                        };
                        this.enqueue(newWebsocketEvent);
                    }
                    else {
                        this.enqueue(websocketEvent);
                    }
                    return;
                }
                // This event is for a list that currently has an action pending
                // or for a list that an action has just been recently completed
                if (new Date(websocketEvent?.data?.messageContent[0]?.version) >
                    new Date(list?.version)) {
                    // This has to be a list event (group and items don't have a version) where the incoming event has a greater
                    // watermark than what we currently have in the store
                    if (list?.currentAction?.messageType === ListMessageType.loading) {
                        // We are currently waiting for an event to be completed (ListMessageType.loading)
                        // This is our list header event with our updated list version, so dispatch it so
                        // our store has the latest version number
                        // if list event, make current action for list of type success if its loading
                        const newMessage = this.mapListAction(list?.currentAction);
                        const newMessageContent = [
                            {
                                ...websocketEvent?.data?.messageContent[0],
                                currentAction: newMessage,
                            },
                        ];
                        const newWebsocketEvent = {
                            ...websocketEvent,
                            data: {
                                ...websocketEvent.data,
                                messageContent: newMessageContent,
                            },
                        };
                        this.store.dispatch(this.handleEvent(websocketEvent.data.eventAction, newWebsocketEvent));
                    }
                    else {
                        // We aren't waiting to process events (ListMessageType is not loading)
                        // This is the case where we did an action on r4 and someone on LMA just did an action right after
                        // Enqueue the action (we need these changes from LMA in our store)
                        // setting the current list version in queue, need to do this so that
                        // if we receive item and group events corresponding to this list
                        // we also process them and don't ignore them.
                        this.enqueue(websocketEvent);
                    }
                }
                else if (this.existsInQueue(websocketEvent.data?.messageContent[0]?.listKey)) {
                    // This is for item and group events coming from an LMA change after we had previously done a change on r4
                    // We know we currently have a list update event in the queue that is a newer version, and these events
                    // belong to that same list
                    this.enqueue(websocketEvent);
                }
                // This is the case where we did a change on r4 and we already got the list header back
                // So we can ignore these events because our state is already up to date
            }
            else {
                // list has no current action for it, queue it up
                this.enqueue(websocketEvent);
                return;
            }
        });
        // If external product event, queue it - DEPRECATED, REMOVED
    }
    /**
     * @remarks
     * This method takes in an incoming websocket event. Depending on the type of event, it will attempt to add it to one of the event queues
     */
    enqueue(websocketEvent) {
        if (websocketEvent.data.eventAction === ListEventActions.ListCreated ||
            websocketEvent.data.eventAction === ListEventActions.ListUpdated) {
            this.addToQueue(websocketEvent, this.websocketQueue.listEvents);
        }
        else if (websocketEvent.data.eventAction ===
            ListGroupEventActions.ListGroupCreated ||
            websocketEvent.data.eventAction ===
                ListGroupEventActions.ListGroupUpdated ||
            websocketEvent.data.eventAction === ListGroupEventActions.ListGroupDeleted) {
            this.addToQueue(websocketEvent, this.websocketQueue.groupEvents);
        }
        else if (websocketEvent.data.eventAction ===
            ListItemEventActions.ListItemCreated ||
            websocketEvent.data.eventAction ===
                ListItemEventActions.ListItemUpdated ||
            websocketEvent.data.eventAction === ListItemEventActions.ListItemDeleted) {
            this.addToQueue(websocketEvent, this.websocketQueue.itemEvents);
        }
        // enqueue external product event - DEPRECATED, REMOVED
        this.startTimer();
    }
    /**
     * @remarks
     * This method takes in an incoming websocket event. Depending on the type of event, it will attempt to add it to one of the event queues
     */
    enqueueSync(websocketEvent) {
        if (websocketEvent.data.eventAction === ListEventActions.ListCreated ||
            websocketEvent.data.eventAction === ListEventActions.ListUpdated ||
            websocketEvent.data.eventAction === ListEventActions.ListDeleted) {
            this.addToQueue(websocketEvent, this.websocketQueue.listEvents);
        }
        else if (websocketEvent.data.eventAction ===
            ListGroupEventActions.ListGroupCreated ||
            websocketEvent.data.eventAction ===
                ListGroupEventActions.ListGroupUpdated ||
            websocketEvent.data.eventAction === ListGroupEventActions.ListGroupDeleted) {
            this.addToQueue(websocketEvent, this.websocketQueue.groupEvents);
        }
        else if (websocketEvent.data.eventAction ===
            ListItemEventActions.ListItemCreated ||
            websocketEvent.data.eventAction ===
                ListItemEventActions.ListItemUpdated ||
            websocketEvent.data.eventAction === ListItemEventActions.ListItemDeleted) {
            this.addToQueue(websocketEvent, this.websocketQueue.itemEvents);
        }
        // enqueue external product event - DEPRECATED, REMOVED
    }
    /**
     * @remarks
     * This method takes in an incoming websocket event and queue and attempts to add it to the queue
     * if queue length is zero or an event for that same list/group/item doesnt already exist in the queue,
     * it pushes it to the end of the queue
     * if an event for that object already exists, it checks to see if the event is newer,
     * if it is, it overwrites it in the queue
     */
    addToQueue(websocketEvent, queue) {
        if (queue.length === 0) {
            queue.push(websocketEvent);
        }
        else {
            let updatedInQueue = false;
            for (let i = 0; i < queue.length; i++) {
                let existsInQueue = false;
                if (websocketEvent.data.eventType === 'ListEvent') {
                    if (JSON.stringify(queue[i].data.messageContent[0].listKey) ===
                        JSON.stringify(websocketEvent.data.messageContent[0].listKey)) {
                        existsInQueue = true;
                    }
                }
                else if (websocketEvent.data.eventType === 'ListGroupEvent') {
                    if (JSON.stringify(queue[i].data.messageContent[0].listGroupKey) ===
                        JSON.stringify(websocketEvent.data.messageContent[0].listGroupKey)) {
                        existsInQueue = true;
                    }
                }
                else if (websocketEvent.data.eventType === 'ListItemEvent') {
                    if (JSON.stringify(queue[i].data.messageContent[0].listItemKey) ===
                        JSON.stringify(websocketEvent.data.messageContent[0].listItemKey)) {
                        existsInQueue = true;
                    }
                }
                // external product event - DEPRECATED, REMOVED
                if (existsInQueue &&
                    new Date(websocketEvent.data.watermark) >
                        new Date(queue[i].data.watermark)) {
                    updatedInQueue = true;
                    queue.splice(i, 1, websocketEvent);
                }
                else if (existsInQueue) {
                    updatedInQueue = true;
                }
            }
            if (!updatedInQueue) {
                queue.push(websocketEvent);
            }
        }
    }
    /**
     * @remarks
     * This method dispathces the appropriate actions from the queue. It first batches all the list events into one and
     * dispatches an action for that. Then it does the same for group, list item and external products. It also will dispatch a loadChanges
     * call for items if we have a group event because we dont send item events in the api due to the volume of events being too high.
     * Making this call ensures we get the updated items since if an item gets updated so will the corresponding group its a part of.
     */
    async flush() {
        clearInterval(this.interval);
        this.createBatchAndExecute(this.websocketQueue.listEvents);
        this.createBatchAndExecute(this.websocketQueue.groupEvents);
        if (this.websocketQueue.groupEvents.length > 0) {
            this.store.dispatch(LIST_ITEM_ACTIONS.loadChanges());
        }
        this.createBatchAndExecute(this.websocketQueue.itemEvents);
        this.createBatchAndExecute(this.websocketQueue.externalProductEvents);
        this.resetQueue();
    }
    /**
     * @remarks
     * This method dispathces the appropriate actions from the queue. It first batches all the list events into one and
     * dispatches an action for that. Then it does the same for group, list item and external products. This is used when we
     * get a response from the domain api after doing a list management action
     */
    async flushSync() {
        this.createBatchAndExecute(this.websocketQueue.listEvents);
        this.createBatchAndExecute(this.websocketQueue.groupEvents);
        this.createBatchAndExecute(this.websocketQueue.itemEvents);
        this.createBatchAndExecute(this.websocketQueue.externalProductEvents);
        this.resetQueue();
    }
    /**
     * @remarks
     * This method resets the queue after we flush it
     */
    resetQueue() {
        this.websocketQueue.listEvents = [];
        this.websocketQueue.groupEvents = [];
        this.websocketQueue.itemEvents = [];
    }
    /**
     * @remarks
     * This method creates a single batched websocket event from the queue of events and executes it
     */
    createBatchAndExecute(websocketEvents) {
        if (websocketEvents.length > 0) {
            let batchedEvent = {
                ...websocketEvents[0],
            };
            const batchedContent = [];
            let latestWatermark = websocketEvents[0].data.watermark;
            for (const event of websocketEvents) {
                batchedContent.push(event.data.messageContent[0]);
                if (new Date(event.data.watermark) > new Date(latestWatermark)) {
                    latestWatermark = event.data.watermark;
                }
            }
            batchedEvent = {
                ...batchedEvent,
                data: {
                    ...batchedEvent.data,
                    messageContent: batchedContent,
                    watermark: latestWatermark,
                },
            };
            // If its a list event, need to force it as an update event to account for delete events
            // Logic exists downstream in update to check for deleteDtm
            if (batchedEvent.data.eventType === 'ListEvent') {
                batchedEvent.data.eventAction = ListEventActions.ListUpdated;
            }
            this.store.dispatch(this.handleEvent(batchedEvent.data.eventAction, batchedEvent));
        }
    }
    /**
     * @remarks
     * This method resets the timer once a new websocket event has been added to the queue
     */
    startTimer() {
        clearInterval(this.interval);
        this.interval = setInterval(() => {
            this.flush();
        }, WINDOW_TIMER);
    }
    /**
     * @remarks
     * This method checks the current queue of list events and decides whether or not
     * the list key passed in is included in this queue
     */
    existsInQueue(listKey) {
        for (const event of this.websocketQueue.listEvents) {
            if (JSON.stringify(event.data.messageContent[0]?.listKey) ===
                JSON.stringify(listKey)) {
                return true;
            }
        }
        return false;
    }
    isObject(obj) {
        return obj === Object(obj);
    }
}
WindowingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: WindowingService, deps: [{ token: i1$1.Store }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
WindowingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: WindowingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: WindowingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Store }, { type: i3.PanAppState }]; } });

class CopyListService {
    constructor(store) {
        this.store = store;
    }
    getSimplifiedListFromCopyListModal(copyListModalOutput) {
        const selectedListKey = createListKey(copyListModalOutput.selectedList.listKey);
        if (selectedListKey.includes(ListConstants.recentlyPurchased)) {
            return this.getSimplifiedListFromRecentPurchase(copyListModalOutput);
        }
        else if (selectedListKey.includes(ListConstants.masterListTypeId)) {
            return this.getSimplifiedListFromMasterList(copyListModalOutput);
        }
        else {
            return this.getSimplifiedListFromListOrOrderGuide(copyListModalOutput);
        }
    }
    getSimplifiedListFromRecentPurchase(copyListModalOutput) {
        return this.store.select(selectRecentPurchaseState).pipe(map((recentPurchaseState) => {
            const items = [];
            for (const key of recentPurchaseState.ids) {
                const recentPurchase = recentPurchaseState.entities[key];
                const simplifiedListItem = {
                    productNumber: recentPurchase.productNumber,
                };
                items.push(simplifiedListItem);
            }
            const simplifiedListGroups = [
                {
                    groupName: 'Miscellaneous',
                    items,
                },
                {
                    groupName: ListConstants.unassignedGroup,
                    items: [],
                },
            ];
            const simplifiedList = {
                listName: copyListModalOutput.listName,
                listState: copyListModalOutput.listType,
                groups: simplifiedListGroups,
            };
            return simplifiedList;
        }));
    }
    getSimplifiedListFromListOrOrderGuide(copyListModalOutput) {
        return combineLatest([
            this.store.select(selectCombinedListAndOgHeaders()),
            this.store.select(selectCombinedListAndOgGroups()),
            this.store.select(selectCombinedListAndOgItems()),
        ]).pipe(map(([listHeaders, listGroups, listItems]) => {
            const listKey = createListKey(copyListModalOutput.selectedList.listKey);
            const listHeader = listHeaders[listKey];
            const groups = [];
            if (listHeader) {
                let unassignedExists = false;
                for (const listGroupKey of listHeader.listGroupKeys) {
                    const listGroup = listGroups[createListGroupKey(listHeader.listKey, listGroupKey)];
                    const items = [];
                    if (listGroup) {
                        if (listGroup.groupName === ListConstants.unassignedGroup) {
                            unassignedExists = true;
                        }
                        if (listGroup.listItemKeys?.length > 0) {
                            for (const listItemKey of listGroup.listItemKeys) {
                                const listItem = listItems[createListItemKey(listHeader.listKey, listGroupKey, listItemKey)];
                                if (listItem) {
                                    const simplifiedListItem = {
                                        productNumber: listItem.productNumber,
                                        productNote: listItem.note,
                                    };
                                    items.push(simplifiedListItem);
                                }
                            }
                        }
                        const simplifiedListGroup = {
                            groupName: listGroup.groupName,
                            items,
                        };
                        groups.push(simplifiedListGroup);
                    }
                }
                if (!unassignedExists) {
                    const unassignedGroup = {
                        groupName: ListConstants.unassignedGroup,
                        items: [],
                    };
                    groups.push(unassignedGroup);
                }
            }
            const simplifiedList = {
                listName: copyListModalOutput.listName,
                listState: copyListModalOutput.listType,
                groups,
            };
            return simplifiedList;
        }));
    }
    getSimplifiedListFromMasterList(copyListModalOutput) {
        return combineLatest([
            this.store.select(selectMasterListEntities),
            this.store.select(selectMasterListGroupEntities),
            this.store.select(selectMasterListItemState),
        ]).pipe(map(([masterListHeaders, masterListGroups, masterListItemState]) => {
            const listId = copyListModalOutput.selectedList.listKey.listId;
            const listHeader = masterListHeaders[listId];
            const groups = [];
            if (listHeader) {
                for (const listGroupId of listHeader.listGroupIds) {
                    const items = [];
                    const listGroup = masterListGroups[createMasterListGroupKey(listHeader.listId, listGroupId)];
                    if (listGroup) {
                        for (const listItemId of listGroup.listItemIds) {
                            const masterListItemKey = createMasterListItemKey(listHeader.listId, listGroupId, listItemId);
                            if (masterListItemState.itemKeyProductKeysDict) {
                                const masterListItemProductKeys = masterListItemState
                                    .itemKeyProductKeysDict[masterListItemKey];
                                if (masterListItemProductKeys?.length > 0) {
                                    const primaryMasterListItemProduct = masterListItemState.entities[masterListItemProductKeys[0]];
                                    if (primaryMasterListItemProduct) {
                                        const simplifiedListItem = {
                                            productNumber: primaryMasterListItemProduct.productNumber,
                                            productNote: primaryMasterListItemProduct.referenceComment,
                                        };
                                        items.push(simplifiedListItem);
                                    }
                                }
                            }
                        }
                        const simplifiedListGroup = {
                            groupName: listGroup.groupName,
                            items,
                        };
                        groups.push(simplifiedListGroup);
                    }
                }
            }
            const simplifiedList = {
                listName: copyListModalOutput.listName,
                listState: copyListModalOutput.listType,
                groups,
            };
            return simplifiedList;
        }));
    }
}
CopyListService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CopyListService, deps: [{ token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
CopyListService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CopyListService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CopyListService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Store }]; } });

class ListEffects {
    constructor(actions$, httpService, panAppState, listState, store, windowingService, copyListService, listAnalyticsService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.panAppState = panAppState;
        this.listState = listState;
        this.store = store;
        this.windowingService = windowingService;
        this.copyListService = copyListService;
        this.listAnalyticsService = listAnalyticsService;
        /**
         * @Remarks
         * This effect responds to the MAIN initializer action
         * The effect will trigger actions to:
         * 1. Load each slice of state in the list domain from offline storage
         */
        this.initializeStorage$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.initializeStorage), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), filter(([_, user, customer]) => {
            if (!user || !customer) {
                console.log('LIST_ACTIONS.initializeStorage filter did not pass', user, customer);
            }
            return !!user && !!customer;
        }), concatMap(([_, user, customer]) => this.listState.initializeStorage(user, customer).pipe(mergeMap((lists) => {
            console.log('LIST_ACTIONS.initializeStorage succeeded:');
            return [
                LIST_ACTIONS.loadFromStorage(),
                LIST_GROUP_ACTIONS.loadFromStorage(),
                LIST_ITEM_ACTIONS.loadFromStorage(),
                RECENT_PURCHASE_ACTIONS.loadRecentPurchase(),
            ];
        }), catchError((error) => {
            console.log('LIST_ACTIONS.initializeStorage failed: try loading data from api', error);
            return [
                LIST_ACTIONS.load(),
                LIST_GROUP_ACTIONS.load(),
                LIST_ITEM_ACTIONS.load(),
                RECENT_PURCHASE_ACTIONS.loadRecentPurchase(),
            ];
        })))));
        /**
         * Returns actions to populate the store based on successful/error api response
         *
         * @remarks
         * Responsible for making api call to fetch list headers
         * assigned to a particular user/customer assoociation
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.load), withLatestFrom(this.store.select(listsWatermark()), this.panAppState.session$), switchMap(([action, watermark, previousSession]) => {
            const params = new HttpParams().set(ListConstants.watermark, watermark ? watermark : null);
            return this.httpService
                .getItemsWithParams(ListEndpoints.lists, params)
                .pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response, session]) => LIST_ACTIONS.loadSuccess({
                lists: response,
            })), catchError((error) => of(LIST_ACTIONS.loadFail({ error }))));
        })));
        /**
         * Loads slice of state from offline storage and dispatches success/fail
         *
         * @Remarks
         * This retrieves slice of state from offline storage
         * and dispatches action to populate the store
         */
        this.loadFromStorage$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.loadFromStorage), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), filter(([_, user, customer]) => !!user && !!customer), switchMap(([_, user, customer]) => {
            // DONT DELETE, needed for prod debugging
            console.log('LIST_ACTIONS.loadFromStorage: ');
            return this.listState
                .getFromOfflineStorage(user, customer, LIST_KEY)
                .pipe(map((lists) => {
                console.log('Length of ids from storage: ', lists?.ids?.length);
                return LIST_ACTIONS.loadFromStorageSuccess({ lists });
            }), catchError((error) => of(LIST_ACTIONS.loadFromStorageFail({ error }))));
        })));
        /**
         * Dispatches actions to load in list state
         *
         * @remarks
         * Determines whether or not to load data async/synchrously by checking for watermark
         */
        this.loadFromStorageSuccess$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.loadFromStorageSuccess), withLatestFrom(this.panAppState.feature$([ListConstants.splitMyChangesWatermarkFlag])), switchMap(([action, fetchAllData]) => {
            console.log('LIST_ACTIONS.loadFromStorageSuccess, evaluating splitMyChangesWatermarkFlag:', fetchAllData);
            if (action.lists.watermark &&
                action.lists.watermark !== ListConstants.beginningOfTimeWatermark &&
                !fetchAllData) {
                return [LIST_ACTIONS.loadChanges()];
            }
            else {
                return [LIST_ACTIONS.load()];
            }
        })));
        /**
         * Dispatches actions to load in list state
         *
         * @remarks
         * Load from storage failed for some reason make call to get all the users lists
         */
        this.loadFromStorageFail$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.loadFromStorageFail), concatMap((action) => [LIST_ACTIONS.load()])));
        this.saveToOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.loadSuccess, LIST_ACTIONS.updateInStore, LIST_ACTIONS.ecomR4UpdateListsInStore, LIST_ACTIONS.updateMany), withLatestFrom(this.panAppState.user$, this.panAppState.customer$, this.store.select(selectListsState)), distinctUntilChanged(), filter(([action, user, customer]) => action.lists?.length > 0 && !!user && !!customer), switchMap(([action, user, customer]) => [
            LIST_ACTIONS.writeToOfflineStorage(),
        ])));
        this.writeToOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.writeToOfflineStorage, LIST_ACTIONS.editGroupSequenceSuccess, LIST_ACTIONS.deleteSuccess, LIST_ACTIONS.massDeleteSuccess, LIST_ACTIONS.removeNegativeIdLists, LIST_ACTIONS.createSuccess, LIST_ACTIONS.editListTypeSuccess, LIST_ACTIONS.editListNameSuccess, LIST_ACTIONS.deleteGroupAndMoveSuccess, LIST_ACTIONS.deleteGroupAndDeleteSuccess, LIST_ACTIONS.editGroupSequenceSuccess, LIST_ACTIONS.removeImportError, LIST_GROUP_ACTIONS.writeToOfflineStorage, LIST_ITEM_ACTIONS.writeToOfflineStorage), withLatestFrom(this.panAppState.user$, this.panAppState.customer$, this.store.select(selectListsState)), distinctUntilChanged(), switchMap(([action, user, customer, listState]) => {
            // DONT DELETE NEED FOR PROD DEBUGGING
            // Dont write initial state to offline storage (temporary fix)
            if (listState?.watermark === ListConstants.beginningOfTimeWatermark) {
                return [];
            }
            console.log('LIST_ACTIONS.writeToOfflineStorage');
            console.log('Action that triggered this effect: ', action);
            console.log('Watermark: ', listState?.watermark);
            console.log('Length of ids: ', listState?.ids?.length);
            if (listState?.entities !== undefined &&
                listState?.entities !== null) {
                console.log('Length of entities: ', Object?.keys(listState?.entities)?.length);
            }
            return this.listState.writeToOfflineStorage(user, customer, LIST_KEY, listState);
        })), { dispatch: false });
        this.getChanges$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.loadChanges), withLatestFrom(this.store.select(listsWatermark()), this.panAppState.customer$), filter(([_, watermark, customer]) => !!customer), switchMap(([_, watermark, customer]) => {
            const params = new HttpParams()
                .append(ListConstants.listTypeId, ListConstants.normalListTypeId)
                .append(ListConstants.watermark, watermark ? watermark : null);
            return this.httpService
                .getItemWithParams(ListEndpoints.listsChanges, params)
                .pipe(map((response) => {
                this.windowingService.processSynchronousWebsockets(response);
                return LIST_ACTIONS.loadChangesSuccess();
            }), catchError((error) => of(LIST_ACTIONS.loadChangesFail({ error }))));
        })));
        /**
         * @Remarks
         * This effect makes api call to domain api to create a new list synchronously
         */
        this.create$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.create), concatMap((action) => {
            const requestBody = {
                listType: action.list.listKey.listTypeId,
                listId: action.list.listKey.listId.toString(),
                listName: action.list.listName,
                listStatusIndicator: action.list.listState,
                favorite: action.list.favorite,
            };
            return this.httpService
                .post(ListEndpoints.lists, requestBody)
                .pipe(map((response) => {
                this.windowingService.processSynchronousResponse(response);
                let newList;
                response.forEach((event) => {
                    if (event.data.eventAction === ListEventActions.ListUpdated) {
                        newList = event.data.messageContent[0];
                    }
                });
                return LIST_ACTIONS.createSuccess({
                    list: newList,
                    tracking: createSuccessTrackingObject(action.tracking),
                    favoriteProductNumber: action.favoriteProductNumber,
                });
            }), catchError((error) => [
                LIST_ACTIONS.createFail({
                    list: action.list,
                    error,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect makes an api call to list domain api to copy a list
         */
        this.copy$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.copy), mergeMap((action) => combineLatest([
            of(action),
            this.copyListService.getSimplifiedListFromCopyListModal(action.copyListModalOutput),
            this.panAppState.customer$,
        ]).pipe(take(1))), concatMap(([action, simplifiedList, customer]) => {
            let custDivDeptInfo;
            if (action.copyListModalOutput.custDivDeptInfo.length > 0) {
                custDivDeptInfo = action.copyListModalOutput.custDivDeptInfo;
            }
            else {
                custDivDeptInfo = [
                    {
                        customerNumber: customer.customerNumber,
                        divisionNumber: customer.divisionNumber,
                        departmentNumber: Number(customer.departmentNumber),
                    },
                ];
            }
            const requestBody = {
                simplifiedList,
                custDivDeptInfo,
            };
            const discontinuedCount = action.copyListModalOutput.selectedList
                ?.discontinuedCount
                ? action.copyListModalOutput.selectedList.discontinuedCount
                : 0;
            return this.httpService
                .post(ListEndpoints.copyList, requestBody)
                .pipe(mergeMap((response) => {
                if (!response.list) {
                    return [
                        LIST_ACTIONS.copySuccess({
                            list: response.list,
                            copyListModalOutput: action.copyListModalOutput,
                        }),
                    ];
                }
                return [
                    LIST_ACTIONS.copySuccess({
                        list: response.list,
                        copyListModalOutput: action.copyListModalOutput,
                    }),
                    LIST_ACTIONS.updateInStore({
                        lists: [{ ...response.list, discontinuedCount }],
                        watermark: response.list.updateDtmSourceSystem,
                        version: response.list.version,
                    }),
                    LIST_GROUP_ACTIONS.updateInStore({
                        listGroups: response.groups,
                        watermark: response.list.updateDtmSourceSystem,
                        version: response.list.version,
                    }),
                    LIST_ITEM_ACTIONS.updateInStore({
                        listItems: response.items,
                        watermark: response.list.updateDtmSourceSystem,
                        version: response.list.version,
                    }),
                ];
            }), catchError((error) => [
                LIST_ACTIONS.copyFail({
                    listName: action.copyListModalOutput.selectedList.listName,
                    error,
                }),
            ]));
        })));
        this.copySuccess$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.copySuccess), withLatestFrom(this.panAppState.customer$), map(([action, customer]) => {
            let copyType = CopyListTypes.normalCopy;
            for (const custDivDept of action.copyListModalOutput
                .custDivDeptInfo) {
                if (custDivDept.customerNumber !== customer.customerNumber ||
                    custDivDept.divisionNumber !== customer.divisionNumber ||
                    Number(custDivDept.departmentNumber) !==
                        Number(customer.departmentNumber)) {
                    copyType = CopyListTypes.massCopy;
                    break;
                }
            }
            const copyListAnalytics = {
                id: action.copyListModalOutput.selectedList.listKey.listId,
                listType: action.copyListModalOutput.selectedList.listKey.listTypeId,
                copiedId: action.list?.listKey?.listId,
                customerCount: action.copyListModalOutput.customerCount
                    ? action.copyListModalOutput.customerCount
                    : 1,
                marketCount: action.copyListModalOutput.marketCount
                    ? action.copyListModalOutput.marketCount
                    : 1,
                copyType,
            };
            this.listAnalyticsService.trackCopyListSuccess(copyListAnalytics);
        })), { dispatch: false });
        /**
         * @Remarks
         * This effect makes api call to domain api to edit a list type
         */
        this.editListType$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.editListType), concatMap((action) => {
            const requestBody = {
                listKey: {
                    listTypeId: action.list.listKey.listTypeId,
                    listId: Number(action.list.listKey.listId),
                },
                listName: action.list.listName,
                listStatusIndicator: action.newListType,
                version: action.list.version,
            };
            return this.httpService
                .put(ListEndpoints.lists, requestBody)
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_ACTIONS.editListTypeSuccess({
                        newListType: action.newListType,
                        listName: action.list.listName,
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                ];
            }), catchError((error) => [
                LIST_ACTIONS.editListTypeFail({
                    error,
                    list: action.list,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect makes api call to domain api to edit a list name synchronously
         */
        this.editListName$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.editListName), concatMap((action) => {
            const requestBody = {
                listKey: {
                    listTypeId: action.list.listKey.listTypeId,
                    listId: Number(action.list.listKey.listId),
                },
                listName: action.newListName,
                listStatusIndicator: action.list.listState,
                version: action.list.version,
            };
            return this.httpService
                .put(ListEndpoints.lists, requestBody)
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_ACTIONS.editListNameSuccess({
                        response,
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                ];
            }), catchError((error) => [
                LIST_ACTIONS.editListNameFail({
                    error,
                    list: action.list,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * Deletes negative list ids from store
         *
         * @Remarks
         * UI must first insert a negative list id in state when creating a new list
         * Once the CDC event notifys UI of via websocket created/update event,
         * this effect will delete old negative list from store.
         */
        this.ecomR4UpdateListsInStore$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.ecomR4UpdateListsInStore), concatMap((action) => {
            const deleteBuffer = [];
            action.lists.forEach((list) => {
                const key = list?.listKey?.listTypeId + '-' + list?.r4TempListId;
                if (list?.r4TempListId) {
                    deleteBuffer.push(key);
                }
            });
            if (deleteBuffer.length > 0) {
                return [LIST_ACTIONS.removeNegativeIdLists({ keys: deleteBuffer })];
            }
            return [];
        })));
        /**
         * @Remarks
         * This effect responds to create or update actions received through
         * the websocket event handler.  When the create/update event is related
         * to a new list created on R4, a separate action is dispatched in order
         * to delete the old negative list id from the store
         */
        this.createOrUpdateEvent$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.updatedAsync, LIST_ACTIONS.createdAsync), withLatestFrom(this.panAppState.session$), filter(([action, session]) => !!action.contexts && !!session), concatMap(([action, session]) => {
            const contexts = action.contexts[0];
            if (Number(session.division) === Number(contexts.divisionNumber) &&
                Number(session.selectedCustomerId) ===
                    Number(contexts.customerNumber) &&
                Number(session.department) === Number(contexts.departmentNumber)) {
                const actionBuffer = [];
                const r4Lists = [];
                const nonR4Lists = [];
                const listsToDelete = [];
                action.lists?.forEach((list) => {
                    if (list.deleteDtm && list.deleteDtm !== 'N') {
                        listsToDelete.push(list);
                    }
                    else if (list.r4TempListId) {
                        r4Lists.push(list);
                    }
                    else if (!list.r4TempListId) {
                        nonR4Lists.push(list);
                    }
                });
                if (nonR4Lists?.length > 0) {
                    actionBuffer.push(LIST_ACTIONS.updateInStore({
                        lists: nonR4Lists,
                        watermark: action.watermark,
                        version: action.version,
                    }));
                }
                if (r4Lists?.length > 0) {
                    actionBuffer.push(LIST_ACTIONS.ecomR4UpdateListsInStore({
                        lists: r4Lists,
                        watermark: action.watermark,
                        version: action.version,
                    }));
                }
                if (listsToDelete?.length > 0) {
                    actionBuffer.push(LIST_ACTIONS.deletedAsync({
                        lists: listsToDelete,
                        watermark: action.watermark,
                        contexts: action.contexts,
                        version: action.version,
                    }));
                }
                return actionBuffer;
            }
            else {
                return [
                    LIST_ACTIONS.updateInStorage({
                        lists: action.lists,
                        watermark: action.watermark,
                        contexts: action.contexts,
                        version: action.version,
                    }),
                ];
            }
        })));
        this.deletedAsync$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.deletedAsync), withLatestFrom(this.panAppState.session$), filter(([action]) => !!action.contexts), concatMap(([action, session]) => {
            const contexts = action.contexts[0];
            if (Number(session.division) === Number(contexts.divisionNumber) &&
                Number(session.selectedCustomerId) ===
                    Number(contexts.customerNumber) &&
                Number(session.department) === Number(contexts.departmentNumber)) {
                const keys = action.lists.map((list) => list.listKey.listTypeId + '-' + list.listKey.listId);
                return [
                    LIST_ACTIONS.deletedAsyncSuccess({
                        keys,
                        watermark: action.watermark,
                        version: action.version,
                        tracking: {
                            tracing: {
                                data: {
                                    traceContext: ListTracingConstants.listMaintenance,
                                    isEndOfTrace: true,
                                    isStartOfTrace: false,
                                },
                            },
                        },
                    }),
                ];
            }
            else {
                return [
                    LIST_ACTIONS.deletedAsyncFail({
                        error: 'Delete List event is out of sync',
                        tracking: {
                            tracing: {
                                data: {
                                    traceContext: ListTracingConstants.listMaintenance,
                                    isEndOfTrace: true,
                                    isStartOfTrace: false,
                                },
                            },
                        },
                    }),
                ];
            }
        })));
        /**
         * @Remarks
         * This effect makes api call to domain api to resequence groups synchronously
         */
        this.editGroupSequence$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.editGroupSequence), concatMap((action) => {
            const requestBody = {
                listKey: action.list.listKey,
                groupKeys: action.groupKeys,
                groupId: action.group.listGroupKey.listGroupId,
                groupSequenceNumber: action.groupSequenceNumber,
                groupName: action.group.groupName,
                version: action.list.version,
            };
            return this.httpService
                .put(ListEndpoints.listGroups, requestBody)
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_ACTIONS.editGroupSequenceSuccess({
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                ];
            }), catchError((error) => [
                LIST_ACTIONS.editGroupSequenceFail({
                    error,
                    list: action.list,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect makes api call to domain api to delete group and move items to unassigned group
         */
        this.deleteGroupAndMove$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.deleteGroupAndMove), concatMap((action) => {
            const requestBody = {
                list: { ...action.list, listGroupKeys: action.updatedGroupKeys },
                listGroups: [
                    action.groupToDelete,
                    {
                        ...action.unassignedGroup,
                        listItemKeys: action.updatedListItemKeys,
                    },
                ],
                listItems: action.listItemsToInsert,
                isMoveItem: true,
                slmDeleteListGroupRequestBody: {
                    listId: action.list.listKey.listId,
                    groupId: Number(action.groupToDelete.listGroupKey.listGroupId),
                    version: action.list.version,
                },
            };
            return this.httpService
                .delete(ListEndpoints.listGroups, {
                body: requestBody,
            })
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_ACTIONS.changeSelectedGroup({
                        key: action.list.listKey.listTypeId +
                            '-' +
                            action.list.listKey.listId,
                        newSelectedGroup: ListConstants.unassignedGroup,
                    }),
                    LIST_ACTIONS.deleteGroupAndMoveSuccess({
                        groupName: action.groupToDelete.groupName,
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                ];
            }), catchError((error) => [
                LIST_ACTIONS.deleteGroupFail({
                    list: action.list,
                    error,
                    groupsToReinsert: [
                        action.groupToDelete,
                        action.unassignedGroup,
                    ],
                    itemsToReinsert: action.listItemsToDelete,
                    itemsToRemove: action.listItemsToInsert,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect makes api call to domain api to delete group and all the items in it
         */
        this.deleteGroupAndDelete$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.deleteGroupAndDelete), concatMap((action) => {
            const requestBody = {
                list: { ...action.list, listGroupKeys: action.updatedGroupKeys },
                listGroups: [action.groupToDelete],
                listItems: action.listItemsToDelete,
                isMoveItem: false,
                slmDeleteListGroupRequestBody: {
                    listId: action.list.listKey.listId,
                    groupId: Number(action.groupToDelete.listGroupKey.listGroupId),
                    version: action.list.version,
                },
            };
            return this.httpService
                .delete(ListEndpoints.listGroups, {
                body: requestBody,
            })
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_ACTIONS.changeSelectedGroup({
                        key: action.list.listKey.listTypeId +
                            '-' +
                            action.list.listKey.listId,
                        newSelectedGroup: ListConstants.unassignedGroup,
                    }),
                    LIST_ACTIONS.deleteGroupAndDeleteSuccess({
                        groupName: action.groupToDelete.groupName,
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                ];
            }), catchError((error) => [
                LIST_ACTIONS.deleteGroupFail({
                    list: action.list,
                    error,
                    itemsToReinsert: action.listItemsToDelete,
                    groupsToReinsert: [action.groupToDelete],
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect makes api call to domain api to delete a list
         */
        this.delete$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.delete), withLatestFrom(this.panAppState.user$), concatMap(([action, user]) => {
            const privateDeletedByNonOwner = action.list.listState === ListConstants.private &&
                user.userId !== action.list.addUserIdSourceSystem;
            if (privateDeletedByNonOwner) {
                return [LIST_ACTIONS.deleteFail({ error: null, list: action.list })];
            }
            const requestBody = {
                listKey: {
                    listTypeId: action.list.listKey.listTypeId,
                    listId: Number(action.list.listKey.listId),
                },
            };
            return this.httpService
                .delete(ListEndpoints.lists, { body: requestBody })
                .pipe(mergeMap((response) => [
                LIST_ACTIONS.deleteSuccess({
                    listName: action.list.listName,
                    tracking: createSuccessTrackingObject(action.tracking),
                }),
            ]), catchError((error) => [
                LIST_ACTIONS.deleteFail({
                    error,
                    list: action.list,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect makes api call to domain api to accept better buy and replace products in lists that match the better buy suggestion
         */
        this.acceptBetterBuy$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.betterBuyAccept), withLatestFrom(this.panAppState.user$), concatMap(([action, user]) => {
            const products = action.betterBuyItems.map((bb) => ({
                originalProduct: bb.oldProdNbr,
                replacementProduct: bb.newProdNbr,
            }));
            const listIds = [];
            action.listItems.forEach((item) => {
                if (!listIds.includes(item.listKey.listId)) {
                    listIds.push(item.listKey.listId);
                }
            });
            const requestBody = {
                acceptDeclineMuleRequest: {
                    acceptDeclineList: action.betterBuyItems,
                },
                replaceItemsRequestBody: {
                    listItems: action.listItems,
                    slmRepalceItemsRequestBody: {
                        products,
                        listIds,
                        replaceAll: true,
                        listStatusIndicator: 'PUBLIC',
                    },
                },
            };
            return this.httpService
                .post(ListEndpoints.betterBuy, requestBody)
                .pipe(mergeMap((response) => [
                LIST_ACTIONS.betterBuyAcceptSuccess({
                    lists: response.updatedLists,
                    groups: response.updatedListGroups,
                    betterBuyItems: action.betterBuyItems,
                    muleErrorNote: response?.muleErrorNote,
                }),
            ]), catchError((error) => [
                LIST_ACTIONS.betterBuyAcceptFail({ error }),
            ]));
        })));
        /**
         * @Remarks
         * This effect makes api call to domain api to decline a better buy suggestion
         */
        this.declineBetterBuy$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.betterBuyDecline), withLatestFrom(this.panAppState.user$), concatMap(([action, user]) => {
            const requestBody = {
                acceptDeclineMuleRequest: {
                    acceptDeclineList: action.betterBuyItems,
                },
                replaceItemsRequestBody: {
                    listItems: [],
                },
            };
            return this.httpService
                .post(ListEndpoints.betterBuy, requestBody)
                .pipe(mergeMap((response) => [
                LIST_ACTIONS.betterBuyDeclineSuccess({
                    betterBuyItems: action.betterBuyItems,
                }),
            ]), catchError((error) => [
                LIST_ACTIONS.betterBuyDeclineFail({ error }),
            ]));
        })));
        /**
         * This effect makes api call to domain api to import a list
         * updateDtmSourceSystem === watermark
         */
        this.importList$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.importList), concatMap((action) => {
            const requestBody = {
                csvRows: action.csvRows,
                listName: action.listName,
                listState: action.listState,
            };
            return this.httpService
                .post(ListEndpoints.importList, requestBody)
                .pipe(mergeMap((response) => [
                LIST_ACTIONS.importListSuccess({
                    importedListContents: response.importedListContents,
                    errorDetails: response.errorDetails,
                }),
                LIST_ACTIONS.updateInStore({
                    lists: [response.importedListContents.list],
                    watermark: response.importedListContents.list
                        .updateDtmSourceSystem,
                    version: response.importedListContents.list.version,
                }),
                LIST_GROUP_ACTIONS.updateInStore({
                    listGroups: response.importedListContents.groups,
                    watermark: response.importedListContents.list
                        .updateDtmSourceSystem,
                    version: response.importedListContents.list.version,
                }),
                LIST_ITEM_ACTIONS.updateInStore({
                    listItems: response.importedListContents.items,
                    watermark: response.importedListContents.list
                        .updateDtmSourceSystem,
                    version: response.importedListContents.list.version,
                }),
            ]), catchError((error) => [
                LIST_ACTIONS.importListFail({ error, listName: action.listName }),
            ]));
        })));
        /**
         * This effect makes api call to the list domain api to find other customers
         * that have the same list (checking list name)
         */
        this.getCustomersWithListName$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.getCustomersWithListName), concatMap((action) => {
            const requestBody = {
                custDivDeptInfo: action.custDivDeptInfo,
                listName: action.listName,
            };
            return this.httpService
                .post(ListEndpoints.getModifiableListByName, requestBody)
                .pipe(mergeMap((response) => [
                LIST_ACTIONS.getCustomersWithListNameSuccess({
                    listName: action.listName,
                    customerList: response,
                }),
            ]), catchError((error) => [
                LIST_ACTIONS.getCustomersWithListNameFail({ error }),
            ]));
        })));
        /**
         * This effect makes api call to the list domain api to delete all list keys that were passed
         */
        this.massDelete$ = createEffect(() => this.actions$.pipe(ofType(LIST_ACTIONS.massDelete), concatMap((action) => {
            const requestBody = {
                listKeys: action.listKeys,
            };
            return this.httpService
                .delete(ListEndpoints.massDelete, { body: requestBody })
                .pipe(mergeMap(() => [
                LIST_ACTIONS.massDeleteSuccess({
                    listName: action.listName,
                    listKeys: action.listKeys,
                    customerList: action.customerList,
                }),
            ]), catchError((error) => [
                LIST_ACTIONS.massDeleteFail({ error }),
            ]));
        })));
    }
}
ListEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: i3.PanAppState }, { token: ListStateService }, { token: i1$1.Store }, { token: WindowingService }, { token: CopyListService }, { token: ListAnalyticsService }], target: i0.ɵɵFactoryTarget.Injectable });
ListEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: i3.PanAppState }, { type: ListStateService }, { type: i1$1.Store }, { type: WindowingService }, { type: CopyListService }, { type: ListAnalyticsService }]; } });

class ListGroupEffects {
    constructor(actions$, store, panAppState, listState, listFacadeService, listGroupFacadeService, httpService, windowingService) {
        this.actions$ = actions$;
        this.store = store;
        this.panAppState = panAppState;
        this.listState = listState;
        this.listFacadeService = listFacadeService;
        this.listGroupFacadeService = listGroupFacadeService;
        this.httpService = httpService;
        this.windowingService = windowingService;
        this.load$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.load), withLatestFrom(this.store.select(listGroupsWatermark()), this.panAppState.session$), switchMap(([action, watermark, previousSession]) => {
            const params = new HttpParams().set('watermark', watermark ? watermark : null);
            return this.httpService
                .getItemsWithParams(ListEndpoints.listGroups, params)
                .pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response, session]) => LIST_GROUP_ACTIONS.loadSuccess({ listGroups: response })), catchError((error) => of(LIST_GROUP_ACTIONS.loadFail({ error }))));
        })));
        this.loadFromStorage$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.loadFromStorage), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), filter(([_, user, customer]) => !!user && !!customer), switchMap(([_, user, customer]) => this.listState
            .getFromOfflineStorage(user, customer, LIST_GROUP_KEY)
            .pipe(map((listGroups) => LIST_GROUP_ACTIONS.loadFromStorageSuccess({ listGroups })), catchError((error) => of(LIST_GROUP_ACTIONS.loadFromStorageFail({ error })))))));
        this.loadFromStorageSuccess$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.loadFromStorageSuccess), withLatestFrom(this.panAppState.feature$([ListConstants.splitMyChangesWatermarkFlag])), switchMap(([action, fetchAllData]) => {
            if (action.listGroups.watermark &&
                action.listGroups.watermark !==
                    ListConstants.beginningOfTimeWatermark &&
                !fetchAllData) {
                return [LIST_GROUP_ACTIONS.loadChanges()];
            }
            else {
                return [LIST_GROUP_ACTIONS.load()];
            }
        })));
        /**
         * Dispatches actions to load in list group state
         *
         * @remarks
         * Load from storage failed for some reason make call to get all the users groups
         */
        this.loadFromStorageFail$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.loadFromStorageFail), concatMap((action) => [LIST_GROUP_ACTIONS.load()])));
        this.getChanges$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.loadChanges), withLatestFrom(this.store.select(listGroupsWatermark()), this.panAppState.customer$), filter(([_, watermark, customer]) => !!customer), switchMap(([_, watermark, customer]) => {
            const params = new HttpParams()
                .append(ListConstants.listTypeId, ListConstants.normalListTypeId)
                .append(ListConstants.watermark, watermark ? watermark : null);
            return this.httpService
                .getItemWithParams(ListEndpoints.listGroupsChanges, params)
                .pipe(map((response) => {
                this.windowingService.processSynchronousWebsockets(response);
                return LIST_GROUP_ACTIONS.loadChangesSuccess();
            }), catchError((error) => of(LIST_GROUP_ACTIONS.loadChangesFail({ error }))));
        })));
        this.saveToOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.loadSuccess, LIST_GROUP_ACTIONS.updateInStore), filter((action) => action.listGroups.length > 0), map(() => LIST_GROUP_ACTIONS.writeToOfflineStorage())));
        this.writeToOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.writeToOfflineStorage, LIST_GROUP_ACTIONS.update, LIST_GROUP_ACTIONS.delete, LIST_GROUP_ACTIONS.moveItemsToAnotherGroupSuccess, LIST_GROUP_ACTIONS.editGroupNameSuccess, LIST_GROUP_ACTIONS.removeListItemKeys, LIST_GROUP_ACTIONS.removeNegativeIdGroups, LIST_GROUP_ACTIONS.updateMany, LIST_GROUP_ACTIONS.createSuccess, LIST_GROUP_ACTIONS.moveItemsWithinGroupSuccess, LIST_ACTIONS.writeToOfflineStorage, LIST_ITEM_ACTIONS.writeToOfflineStorage), withLatestFrom(this.panAppState.user$, this.panAppState.customer$, this.store.select(selectListGroupsState)), distinctUntilChanged(), switchMap(([_, user, customer, listGroupState]) => {
            // Dont write initial state to offline storage (temporary fix)
            if (listGroupState?.watermark === ListConstants.beginningOfTimeWatermark) {
                return [];
            }
            return this.listState.writeToOfflineStorage(user, customer, LIST_GROUP_KEY, listGroupState);
        })), { dispatch: false });
        /**
         * @Remarks
         * This effect responds when list groups were updated in the store from a websocket event
         * Checks to see if a list group has a negative id attached to it
         * If so, dispatches an action to remove it from the store
         */
        this.updateInStore$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.updateInStore), concatMap((action) => {
            const keys = [];
            action.listGroups?.forEach((group) => {
                if (group?.r4TempGroupId) {
                    keys.push(group.listKey.listTypeId +
                        '-' +
                        group.listKey.listId +
                        '-' +
                        group.r4TempGroupId);
                }
            });
            if (keys.length > 0) {
                return [LIST_GROUP_ACTIONS.removeNegativeIdGroups({ keys })];
            }
            return [];
        })));
        /**
         * @Remarks
         * This effect calls the domain api to move items around within a group
         */
        this.moveItemsWithinGroup$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.moveItemsWithinGroupFromDragAndDrop, LIST_GROUP_ACTIONS.moveItemsWithinGroupFromLineNumber), concatMap((action) => {
            const requestBody = {
                listKey: action.list.listKey,
                listGroups: [{ ...action.group, listItemKeys: action.listItemKeys }],
                listItems: null,
                slmUpdateItemsRequestBody: {
                    listId: action.list?.listKey.listId,
                    groupId: action.group.listGroupKey.listGroupId,
                    itemNumbers: action.productNumbers,
                    toItemSequenceNumber: action.toItemSequenceNumber + 1,
                    toGroupId: null,
                    version: action.list?.version,
                    isLineItemNumberMove: action.isLineItemMove,
                },
            };
            return this.httpService
                .put(ListEndpoints.listItems, requestBody)
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_GROUP_ACTIONS.moveItemsWithinGroupSuccess({
                        numberOfItemsMoved: action.productNumbers.length,
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                ];
            }), catchError((error) => [
                LIST_GROUP_ACTIONS.moveItemsWithinGroupFail({
                    error,
                    group: action.group,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect calls the domain api to move items to another group
         */
        this.moveItemsToAnotherGroup$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFromDragAndDrop, LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFromLineNumber, LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFromModal), concatMap((action) => {
            const requestBody = {
                listKey: action.stateToChange.list.listKey,
                listGroups: [
                    {
                        ...action.stateToChange.targetGroup,
                        listItemKeys: action.stateToChange.updatedTargetGroupItemKeys,
                    },
                    {
                        ...action.stateToChange.sourceGroup,
                        listItemKeys: action.stateToChange.updatedSourceGroupItemKeys,
                    },
                ],
                listItems: action.stateToChange.itemsToAdd,
                slmUpdateItemsRequestBody: {
                    listId: action.stateToChange.list.listKey.listId,
                    groupId: action.stateToChange.sourceGroup.listGroupKey.listGroupId,
                    itemNumbers: action.stateToChange.itemsToRemove.map((val) => val.productNumber),
                    toItemSequenceNumber: !!action.stateToChange.toItemSequenceNumber
                        ? action.stateToChange.toItemSequenceNumber
                        : null,
                    toGroupId: Number(action.stateToChange.targetGroup.listGroupKey.listGroupId),
                    version: action.stateToChange.list.version,
                    isLineItemNumberMove: !!action.stateToChange.toItemSequenceNumber,
                },
            };
            return this.httpService
                .put(ListEndpoints.listItems, requestBody)
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_ITEM_ACTIONS.loadChanges(),
                    LIST_GROUP_ACTIONS.moveItemsToAnotherGroupSuccess({
                        numberOfItemsMoved: action.stateToChange.itemsToRemove.length,
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                ];
            }), catchError((error) => [
                LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFail({
                    error,
                    stateToRevert: {
                        sourceGroup: action.stateToChange.sourceGroup,
                        targetGroup: action.stateToChange.targetGroup,
                        itemsToAdd: action.stateToChange.itemsToRemove,
                        itemsToRemove: action.stateToChange.itemsToAdd,
                    },
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect calls the domain api to create a new group for a list
         */
        this.create$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.create), concatMap((action) => {
            const requestBody = {
                listId: action.listGroup.listKey.listId,
                r4TempListGrpId: action.listGroup.r4TempGroupId,
                groupName: action.listGroup.groupName,
                versionNumber: action.version,
            };
            const newGroupKeys = action.list.listGroupKeys.slice();
            newGroupKeys.push(action.listGroup.listGroupKey);
            return this.httpService
                .post(ListEndpoints.listGroups, requestBody)
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_GROUP_ACTIONS.createSuccess({
                        newGroupNumber: action.list.listGroupKeys.length,
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                ];
            }), catchError((error) => [
                LIST_GROUP_ACTIONS.createFail({
                    error,
                    listGroup: action.listGroup,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect calls the domain api to change the group name
         */
        this.editGroupName$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.editGroupName), concatMap((action) => {
            const requestBody = {
                listKey: action.list.listKey,
                groupKeys: action.list.listGroupKeys,
                groupId: action.group.listGroupKey.listGroupId,
                groupSequenceNumber: null,
                groupName: action.newGroupName,
                version: action.list.version,
            };
            return this.httpService
                .put(ListEndpoints.listGroups, requestBody)
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_GROUP_ACTIONS.editGroupNameSuccess({
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                    LIST_ACTIONS.changeSelectedGroup({
                        key: action.list.listKey.listTypeId +
                            '-' +
                            action.list.listKey.listId,
                        newSelectedGroup: action.newGroupName,
                    }),
                ];
            }), catchError((error) => [
                LIST_GROUP_ACTIONS.editGroupNameFail({
                    error,
                    group: action.group,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect responds to create or update actions received through
         * the websocket event handler
         */
        this.createOrUpdateEvent$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.updatedAsync, LIST_GROUP_ACTIONS.createdAsync), withLatestFrom(this.panAppState.session$, this.store.select(listGroupsWatermark())), filter(([action, session, watermark]) => !!action.contexts && !!session), concatMap(([action, session, watermark]) => {
            const contexts = action.contexts[0];
            if (Number(session.division) === Number(contexts.divisionNumber) &&
                Number(session.selectedCustomerId) ===
                    Number(contexts.customerNumber) &&
                Number(session.department) === Number(contexts.departmentNumber)) {
                return [
                    LIST_GROUP_ACTIONS.updateInStore({
                        listGroups: action.listGroups,
                        watermark: action.watermark,
                        version: action.version,
                    }),
                ];
            }
            else {
                return [
                    LIST_GROUP_ACTIONS.updateInStorage({
                        listGroups: action.listGroups,
                        watermark: action.watermark,
                        contexts: action.contexts,
                        version: action.version,
                    }),
                ];
            }
        })));
        this.deleteEvent$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.deletedAsync), withLatestFrom(this.panAppState.session$, this.store.select(listGroupsWatermark())), filter(([action, session, watermark]) => !!action.contexts && !!session), concatMap(([action, session, watermark]) => {
            const contexts = action.contexts[0];
            if (Number(session.division) === Number(contexts.divisionNumber) &&
                Number(session.selectedCustomerId) ===
                    Number(contexts.customerNumber) &&
                Number(session.department) === Number(contexts.departmentNumber)) {
                const keys = action.listGroups.map((list) => list.listKey.listTypeId +
                    '-' +
                    list.listKey.listId +
                    '-' +
                    list.listGroupKey.listGroupId);
                const version = action.version;
                return [
                    LIST_GROUP_ACTIONS.deletedAsyncSuccess({
                        keys,
                        watermark,
                        version,
                        tracking: {
                            tracing: {
                                data: {
                                    traceContext: ListTracingConstants.listMaintenance,
                                    isEndOfTrace: true,
                                    isStartOfTrace: false,
                                },
                            },
                        },
                    }),
                ];
            }
            else {
                return [
                    LIST_GROUP_ACTIONS.deletedAsyncFail({
                        error: 'Delete List event is out of sync',
                        tracking: {
                            tracing: {
                                data: {
                                    traceContext: ListTracingConstants.listMaintenance,
                                    isEndOfTrace: true,
                                    isStartOfTrace: false,
                                },
                            },
                        },
                    }),
                ];
            }
        })));
    }
}
ListGroupEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListGroupEffects, deps: [{ token: i1$3.Actions }, { token: i1$1.Store }, { token: i3.PanAppState }, { token: ListStateService }, { token: ListFacadeService }, { token: ListGroupFacadeService }, { token: ListHttpClientService }, { token: WindowingService }], target: i0.ɵɵFactoryTarget.Injectable });
ListGroupEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListGroupEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListGroupEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: i1$1.Store }, { type: i3.PanAppState }, { type: ListStateService }, { type: ListFacadeService }, { type: ListGroupFacadeService }, { type: ListHttpClientService }, { type: WindowingService }]; } });

class ListItemEffects {
    constructor(actions$, httpService, listState, store, panAppState, windowingService, listFacadeService, masterListFacadeService, recentPurchaseFacadeService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.listState = listState;
        this.store = store;
        this.panAppState = panAppState;
        this.windowingService = windowingService;
        this.listFacadeService = listFacadeService;
        this.masterListFacadeService = masterListFacadeService;
        this.recentPurchaseFacadeService = recentPurchaseFacadeService;
        /**
         * Returns actions to populate the store based on successful/error api response
         *
         * @remarks
         * Responsible for making api call to fetch list items
         * assigned to a particular user/customer association
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.load), withLatestFrom(this.store.select(listItemsWatermark()), this.panAppState.session$), switchMap(([action, watermark, previousSession]) => {
            const params = new HttpParams().set(ListConstants.watermark, watermark ? watermark : null);
            return this.httpService
                .getItemsWithParams(ListEndpoints.listItems, params)
                .pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response, session]) => LIST_ITEM_ACTIONS.loadSuccess({ listItems: response })), catchError((error) => of(LIST_ITEM_ACTIONS.loadFail({ error }))));
        })));
        /**
         * Loads slice of state from offline storage and dispatches success/fail
         *
         * @Remarks
         * This retrieves slice of state from offline storage
         * and dispatches action to populate the store
         */
        this.loadFromStorage$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.loadFromStorage), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), filter(([_, user, customer]) => !!user && !!customer), switchMap(([_, user, customer]) => this.listState
            .getFromOfflineStorage(user, customer, LIST_ITEM_KEY)
            .pipe(map((listItems) => LIST_ITEM_ACTIONS.loadFromStorageSuccess({ listItems })), catchError((error) => of(LIST_ITEM_ACTIONS.loadFromStorageFail({ error })))))));
        /**
         * Dispatches actions to load in list state
         *
         * @remarks
         * Determines whether or not to load data async/synchrously by checking for watermark
         */
        this.loadFromStorageSuccess$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.loadFromStorageSuccess), withLatestFrom(this.panAppState.feature$([ListConstants.splitMyChangesWatermarkFlag])), switchMap(([action, fetchAllData]) => {
            if (action.listItems.watermark &&
                action.listItems.watermark !==
                    ListConstants.beginningOfTimeWatermark &&
                !fetchAllData) {
                return [LIST_ITEM_ACTIONS.loadChanges()];
            }
            else {
                return [LIST_ITEM_ACTIONS.load()];
            }
        })));
        /**
         * Dispatches actions to load in list item state
         *
         * @remarks
         * Load from storage failed for some reason make call to get all the users items
         */
        this.loadFromStorageFail$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.loadFromStorageFail), concatMap((action) => [LIST_ITEM_ACTIONS.load()])));
        this.saveToOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.loadSuccess, LIST_ITEM_ACTIONS.updateInStore), withLatestFrom(this.panAppState.user$, this.panAppState.customer$, this.store.select(selectListItemsState)), distinctUntilChanged(), filter(([action, user, customer]) => action.listItems?.length > 0 && !!user && !!customer), switchMap(([action, user, customer]) => [
            LIST_ITEM_ACTIONS.writeToOfflineStorage(),
        ])));
        this.writeToOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.writeToOfflineStorage, LIST_ITEM_ACTIONS.update, LIST_ITEM_ACTIONS.deleteInStore, LIST_ITEM_ACTIONS.replaceProductSuccess, LIST_ITEM_ACTIONS.deleteSuccess, LIST_ITEM_ACTIONS.copySuccess, LIST_ITEM_ACTIONS.createSuccess, LIST_ITEM_ACTIONS.removeItems, LIST_ITEM_ACTIONS.createNoteSuccess, LIST_ITEM_ACTIONS.deleteNoteSuccess, LIST_ITEM_ACTIONS.updateNoteSuccess, LIST_ACTIONS.writeToOfflineStorage, LIST_GROUP_ACTIONS.writeToOfflineStorage, LIST_ITEM_ACTIONS.saveProductMap), withLatestFrom(this.panAppState.user$, this.panAppState.customer$, this.store.select(selectListItemsState)), distinctUntilChanged(), switchMap(([_, user, customer, listItemState]) => {
            // Dont write initial state to offline storage (temporary fix)
            if (listItemState?.watermark === ListConstants.beginningOfTimeWatermark) {
                return [];
            }
            return this.listState.writeToOfflineStorage(user, customer, LIST_ITEM_KEY, listItemState);
        })), { dispatch: false });
        this.getChanges$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.loadChanges), withLatestFrom(this.store.select(listItemsWatermark()), this.panAppState.customer$), filter(([_, watermark, customer]) => !!customer), switchMap(([_, watermark, customer]) => {
            const params = new HttpParams()
                .append(ListConstants.listTypeId, ListConstants.normalListTypeId)
                .append(ListConstants.watermark, watermark ? watermark : null);
            return this.httpService
                .getItemWithParams(ListEndpoints.listItemsChanges, params)
                .pipe(map((response) => {
                this.windowingService.processSynchronousWebsockets(response);
                return LIST_ITEM_ACTIONS.loadChangesSuccess();
            }), catchError((error) => of(LIST_ITEM_ACTIONS.loadChangesFail({ error }))));
        })));
        /**
         * @Remarks
         * This effect responds when list items were updated in the store from a websocket event
         * Checks to see if a list item has a negative id attached to it
         * If so, dispatches an action to remove it from the store
         */
        this.updateInStore$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.updateInStore), concatMap((action) => {
            const actionBuffer = [];
            const itemKeys = [];
            action.listItems?.forEach((item) => {
                if (item.r4TempListItemId) {
                    itemKeys.push(item.listKey.listTypeId +
                        '-' +
                        item.listKey.listId +
                        '-' +
                        item.listGroupKey.listGroupId +
                        '-' +
                        item.r4TempListItemId);
                }
            });
            if (itemKeys.length > 0) {
                actionBuffer.push(LIST_ITEM_ACTIONS.removeItems({ keys: itemKeys }));
            }
            return actionBuffer;
        })));
        /**
         * @Remarks
         * This effect responds to create or update actions received through
         * the websocket event handler
         */
        this.createOrUpdateEvent$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.updatedAsync, LIST_ITEM_ACTIONS.createdAsync), withLatestFrom(this.panAppState.session$, this.store.select(listItemsWatermark())), filter(([action, session, watermark]) => !!action.contexts && !!session), concatMap(([action, session, watermark]) => {
            const contexts = action.contexts[0];
            if (Number(session.division) === Number(contexts.divisionNumber) &&
                Number(session.selectedCustomerId) ===
                    Number(contexts.customerNumber) &&
                Number(session.department) === Number(contexts.departmentNumber)) {
                return [
                    LIST_ITEM_ACTIONS.updateInStore({
                        listItems: action.listItems,
                        watermark: action.watermark,
                        version: action.version,
                        tracking: {
                            tracing: {
                                data: {
                                    traceContext: ListTracingConstants.listMaintenance,
                                    isEndOfTrace: true,
                                    isStartOfTrace: false,
                                },
                            },
                        },
                    }),
                ];
            }
            else {
                return [
                    LIST_ITEM_ACTIONS.updateInStorage({
                        listItems: action.listItems,
                        watermark: action.watermark,
                        contexts: action.contexts,
                        version: action.version,
                        tracking: {
                            tracing: {
                                data: {
                                    traceContext: ListTracingConstants.listMaintenance,
                                    isEndOfTrace: true,
                                    isStartOfTrace: false,
                                },
                            },
                        },
                    }),
                ];
            }
        })));
        /**
         * @Remarks
         * This effect is called when a list management action calls the domain api and the round trip is in progress
         * Will deselect all items on a list
         */
        this.resetIsSelected$ = createEffect(() => this.actions$.pipe(ofType(LIST_GROUP_ACTIONS.moveItemsToAnotherGroupSuccess, LIST_GROUP_ACTIONS.moveItemsWithinGroupSuccess, LIST_GROUP_ACTIONS.moveItemsWithinGroupFail, LIST_GROUP_ACTIONS.moveItemsToAnotherGroupFail), concatMap(() => [
            LIST_ITEM_ACTIONS.setIsSelected({
                key: null,
                value: false,
                listGroupKey: null,
            }),
        ])));
        /**
         * @Remarks
         * This effect will make a post call to domain api to create new list item(s) synchronously
         */
        this.create$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.create), concatMap((action) => {
            const listItemTempKeyList = [];
            action.listItems.forEach((item) => {
                const itemTempKey = {
                    productNumber: item.productNumber,
                    r4TempListItemId: item.r4TempListItemId,
                };
                listItemTempKeyList.push(itemTempKey);
            });
            const requestBody = {
                listKey: action.list.listKey,
                listGroupKey: action.updatedGroup.listGroupKey,
                items: listItemTempKeyList,
                atGroupLocation: action.atGroupLocation,
                versionNumber: action.version,
            };
            return this.httpService
                .post(ListEndpoints.listItems, requestBody)
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_ITEM_ACTIONS.createSuccess({
                        listId: action.list.listKey.listId,
                        listName: action.list.listName,
                        productQuantity: requestBody.items.length,
                        listItems: action.listItems,
                        showAlternativeToast: action.showAlternativeToast,
                        addMultipleEntryPoint: action.addMultipleEntryPoint,
                        isNonUsf: action.isNonUsf,
                        tracking: createSuccessTrackingObject(action.tracking),
                        displaySpinner: action.displaySpinner,
                    }),
                ];
            }), catchError((error) => [
                LIST_ITEM_ACTIONS.createFail({
                    listItemsToRemove: action.listItems,
                    error,
                    isNonUsf: action.isNonUsf,
                    tracking: createFailureTrackingObject(action.tracking, error),
                    displaySpinner: action.displaySpinner,
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect will make a post call to domain api to copy item(s)
         * on a list
         */
        this.copy$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.copy), concatMap((action) => {
            const requestBody = {
                listKey: action.list.listKey,
                listGroupKey: action.sourceGroupKey,
                itemNumbers: action.tempItemKeys,
                versionNumber: action.list.version,
                listStatusIndicator: action.list.listState,
                listIds: [action.list.listKey.listId],
                groupIds: action.groupIds,
            };
            return this.httpService
                .put(ListEndpoints.listItemsCopy, requestBody)
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_ITEM_ACTIONS.copySuccess({
                        numberOfItemsCopied: action.tempItemKeys.length,
                        numberOfGroupsCopiedTo: action.groupIds.length,
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                ];
            }), catchError((error) => [
                LIST_ITEM_ACTIONS.copyFail({
                    error,
                    listItemsToRemove: action.tempItemsToInsert,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect will make a put call on domain api to replace a discontinued product
         * within a list synchronously
         */
        this.replaceProductInAList$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.replaceProductInAList), concatMap((action) => {
            const requestBody = {
                listItems: action.listItems,
                slmRepalceItemsRequestBody: {
                    products: [action.product],
                    listIds: [action.list.listKey.listId],
                    replaceAll: false,
                    listStatusIndicator: action.list.listState,
                },
            };
            return this.httpService
                .put(ListEndpoints.replaceProducts, requestBody)
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_ITEM_ACTIONS.replaceProductSuccess({
                        originalNum: action.product.originalProduct,
                        replacementNum: action.product.replacementProduct,
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                ];
            }), catchError((error) => [
                LIST_ITEM_ACTIONS.replaceProductFail({
                    listItems: action.listItems,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * this effect will call the domain api to create a note
         * note will apply to all product numbers in a list
         */
        this.createNote$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.createNote), concatMap((action) => {
            const requestBody = {
                listKey: action.list.listKey,
                productNumber: action.productNumber,
                note: action.note,
                versionNumber: action.list.version,
            };
            return this.httpService
                .put(ListEndpoints.notes, requestBody)
                .pipe(switchMap((response) => [
                LIST_ITEM_ACTIONS.createNoteSuccess({
                    list: action.list,
                    productNumber: action.productNumber,
                    note: action.note,
                }),
                LIST_ACTIONS.ecomR4UpdateListsInStore({
                    lists: [response],
                    version: response.version.toString(),
                    watermark: response.updateDtmSourceSystem.toString(),
                }),
            ]), catchError((error) => [
                LIST_ITEM_ACTIONS.createNoteFail({
                    error,
                }),
            ]));
        })));
        /**
         * @Remarks
         * this effect will call the domain api to update a note
         * note will apply to all product numbers in a list
         */
        this.updateNote$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.updateNote), concatMap((action) => {
            const requestBody = {
                listKey: action.list.listKey,
                productNumber: action.productNumber,
                note: action.note,
                versionNumber: action.list.version,
            };
            return this.httpService
                .put(ListEndpoints.notes, requestBody)
                .pipe(switchMap((response) => [
                LIST_ITEM_ACTIONS.updateNoteSuccess({
                    list: action.list,
                    productNumber: action.productNumber,
                    note: action.note,
                }),
                LIST_ACTIONS.ecomR4UpdateListsInStore({
                    lists: [response],
                    version: response.version.toString(),
                    watermark: response.updateDtmSourceSystem.toString(),
                }),
            ]), catchError((error) => [
                LIST_ITEM_ACTIONS.updateNoteFail({
                    error,
                }),
            ]));
        })));
        /**
         * @Remarks
         * this effect will call the domain api to update a note
         * note will apply to all product numbers in a list
         */
        this.deleteNote$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.deleteNote), concatMap((action) => {
            const requestBody = {
                listKey: action.list.listKey,
                productNumber: action.productNumber,
                note: null,
                versionNumber: action.list.version,
            };
            return this.httpService
                .put(ListEndpoints.notes, requestBody)
                .pipe(switchMap((response) => [
                LIST_ITEM_ACTIONS.deleteNoteSuccess({
                    list: action.list,
                    productNumber: action.productNumber,
                    note: null,
                }),
                LIST_ACTIONS.ecomR4UpdateListsInStore({
                    lists: [response],
                    version: response.version.toString(),
                    watermark: response.updateDtmSourceSystem.toString(),
                }),
            ]), catchError((error) => [
                LIST_ITEM_ACTIONS.deleteNoteFail({
                    error,
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect will make a put call on domain api to replace a discontinued product
         * across all lists
         */
        this.replaceProductAcrossAllLists$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.replaceProductAcrossAllLists), concatMap((action) => {
            const requestBody = {
                listItems: action.listItems,
                slmRepalceItemsRequestBody: {
                    products: [action.product],
                    listIds: action.listIds,
                    replaceAll: true,
                    listStatusIndicator: action.list.listState,
                },
            };
            return this.httpService
                .put(ListEndpoints.replaceProducts, requestBody)
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_ITEM_ACTIONS.replaceProductSuccess({
                        originalNum: action.product.originalProduct,
                        replacementNum: action.product.replacementProduct,
                        tracking: createSuccessTrackingObject(action.tracking),
                    }),
                ];
            }), catchError((error) => [
                LIST_ITEM_ACTIONS.replaceProductFail({
                    listItems: action.listItems,
                    tracking: createFailureTrackingObject(action.tracking, error),
                }),
            ]));
        })));
        /**
         * @Remarks
         * This effect will make a delete call to the domain api
         * to delete item(s) within a list
         */
        this.delete$ = createEffect(() => this.actions$.pipe(ofType(LIST_ITEM_ACTIONS.delete), concatMap((action) => {
            const itemNumbers = [];
            action.listItems.forEach((item) => {
                itemNumbers.push(item.productNumber);
            });
            const requestBody = {
                listKey: action.list.listKey,
                listGroups: action.updatedGroups,
                listItems: action.listItems,
                version: action.list.version,
                slmDeleteItemsRequestBody: {
                    listIds: Array.of(action.list.listKey.listId),
                    groupIds: action.updatedGroups.map((group) => Number(group.listGroupKey.listGroupId)),
                    itemNumbers,
                },
            };
            return this.httpService
                .delete(ListEndpoints.listItems, {
                body: requestBody,
            })
                .pipe(switchMap((response) => {
                this.windowingService.processSynchronousResponse(response);
                return [
                    LIST_ITEM_ACTIONS.deleteSuccess({
                        tracking: createSuccessTrackingObject(action.tracking),
                        displaySpinner: action.displaySpinner,
                    }),
                ];
            }), catchError((error) => [
                LIST_ITEM_ACTIONS.deleteFail({
                    listItems: action.listItems,
                    listGroups: action.originalGroups,
                    tracking: createFailureTrackingObject(action.tracking, error),
                    displaySpinner: action.displaySpinner,
                }),
            ]));
        })));
    }
}
ListItemEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListItemEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: ListStateService }, { token: i1$1.Store }, { token: i3.PanAppState }, { token: WindowingService }, { token: ListFacadeService }, { token: MasterListFacadeService }, { token: RecentPurchaseFacadeService }], target: i0.ɵɵFactoryTarget.Injectable });
ListItemEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListItemEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListItemEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: ListStateService }, { type: i1$1.Store }, { type: i3.PanAppState }, { type: WindowingService }, { type: ListFacadeService }, { type: MasterListFacadeService }, { type: RecentPurchaseFacadeService }]; } });

const NG_RX_LIST_EVENTS = {
    namespace: 'List',
    eventType: [
        'ListEvent',
        'ListGroupEvent',
        'ListItemEvent',
        'ExternalVendorEvent',
        'ExternalProductEvent',
    ],
};

class ListClientWebSocketService {
    constructor(webSocketService, store, windowingService) {
        this.webSocketService = webSocketService;
        this.store = store;
        this.windowingService = windowingService;
        this.subscriptions = [];
    }
    initialize() {
        // if there is no websocket service injected, return out
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!this.webSocketService['baseUrl']) {
            // eslint-disable-next-line no-console
            console.info('%c** no websocket injected for list domain **', 'color: black; font-weight:bold;');
            return;
        }
        // subscribe to the websocket events scoped to the list domain
        // panamax websocket service lib will handle keeping the websocket connection
        // alive and will update the isConnected$ observable accordingly
        this.subscriptions.push(this.webSocketService?.isConnected$.subscribe((connected) => {
            if (connected) {
                // Register websocket namespace and event types to subscribe to
                this.webSocketService.register([NG_RX_LIST_EVENTS]);
                this.subscriptions.push(this.webSocketService?.getTopicData().subscribe((event) => {
                    let eventData;
                    try {
                        if (this.isObject(JSON.parse(event?.data))) {
                            eventData = JSON.parse(event?.data);
                            // dispatch appropriate NgRx action based on incoming websocket event action
                            if (eventData?.data?.namespace === 'List' &&
                                eventData?.data?.eventAction) {
                                if (eventData?.data?.messageContent) {
                                    // eslint-disable-next-line no-console
                                    console.debug('WEBSOCKET EVENT: ', eventData);
                                    console.log('List received websocket change');
                                    this.store.dispatch(LIST_ACTIONS.websocketMessageReceivedAction({
                                        tracking: {
                                            tracing: {
                                                data: {
                                                    traceContext: 'listMaintenance',
                                                    isEndOfTrace: false,
                                                    isStartOfTrace: false,
                                                },
                                            },
                                        },
                                    }));
                                    this.windowingService.process(eventData);
                                }
                            }
                        }
                        else {
                            eventData = null;
                        }
                    }
                    catch (e) { }
                }));
            }
            else {
                // eslint-disable-next-line no-console
                console.info('%c** list domain web socket disconnected. **', 'color: red; font-weight:bold;');
            }
        }));
    }
    isObject(obj) {
        return obj === Object(obj);
    }
}
ListClientWebSocketService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListClientWebSocketService, deps: [{ token: i1$4.WebSocketService }, { token: i1$1.Store }, { token: WindowingService }], target: i0.ɵɵFactoryTarget.Injectable });
ListClientWebSocketService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListClientWebSocketService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListClientWebSocketService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$4.WebSocketService }, { type: i1$1.Store }, { type: WindowingService }]; } });

class RecentPurchaseEffects {
    constructor(actions$, recentPurchaseService, panAppState, httpService, listState, store) {
        this.actions$ = actions$;
        this.recentPurchaseService = recentPurchaseService;
        this.panAppState = panAppState;
        this.httpService = httpService;
        this.listState = listState;
        this.store = store;
        this.loadRecentPurchase$ = createEffect(() => this.actions$.pipe(ofType(RECENT_PURCHASE_ACTIONS.loadRecentPurchase), withLatestFrom(this.store.select(recentPurchaseWatermark()), this.panAppState.session$), distinctUntilChanged(), switchMap(([action, watermark, previousSession]) => {
            const params = new HttpParams().set('watermark', watermark ? watermark : null);
            return this.httpService
                .getItemsWithParams('recentPurchase', params)
                .pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response, session]) => RECENT_PURCHASE_ACTIONS.loadRecentPurchaseSuccess({
                recentPurchase: response,
            })), catchError((error) => of(RECENT_PURCHASE_ACTIONS.loadRecentPurchaseFail({ error }))));
        })));
        this.loadRecentPurchaseSuccess$ = createEffect(() => this.actions$.pipe(ofType(RECENT_PURCHASE_ACTIONS.loadRecentPurchaseSuccess), filter((action) => action.recentPurchase.length > 0), map((action) => RECENT_PURCHASE_ACTIONS.writeToOfflineStorage())));
        this.loadFromStorage$ = createEffect(() => this.actions$.pipe(ofType(RECENT_PURCHASE_ACTIONS.loadFromStorage), this.waitFor(this.panAppState.user$), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), filter(([_, user, customer]) => user !== null &&
            customer !== null &&
            user !== undefined &&
            customer !== undefined), switchMap(([_, user, customer]) => this.listState
            .getFromOfflineStorage(user, customer, RECENT_PURCHASE_KEY)
            .pipe(map((recentPurchase) => {
            let newIds = recentPurchase.ids;
            for (const key in recentPurchase?.entities) {
                if (!this.dateIsLessThan(180, 'days', dayjs(recentPurchase?.entities[key].purchaseDtm1))) {
                    delete recentPurchase?.entities[key];
                    newIds = newIds.filter((obj) => obj !== Number(key));
                }
            }
            if (newIds) {
                recentPurchase.ids = newIds;
            }
            return RECENT_PURCHASE_ACTIONS.loadFromStorageSuccess({
                recentPurchase,
            });
        }), catchError((error) => of(RECENT_PURCHASE_ACTIONS.loadFromStorageFail({ error })))))));
        this.loadRecentPurchaseFromStorageSuccess$ = createEffect(() => this.actions$.pipe(ofType(RECENT_PURCHASE_ACTIONS.loadFromStorageSuccess), switchMap((recentPurchase) => [
            RECENT_PURCHASE_ACTIONS.loadRecentPurchase(),
        ])));
        this.writeToOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(RECENT_PURCHASE_ACTIONS.writeToOfflineStorage), withLatestFrom(this.panAppState.user$, this.panAppState.customer$, this.store.select(selectRecentPurchaseState)), distinctUntilChanged(), switchMap(([_, user, customer, recentPurchase]) => this.listState.writeToOfflineStorage(user, customer, 'recent-purchase', recentPurchase))), { dispatch: false });
        this.delete$ = createEffect(() => this.actions$.pipe(ofType(RECENT_PURCHASE_ACTIONS.delete), concatMap((action) => {
            const productIds = action.products.map((product) => product.productNumber);
            const requestBody = {
                productIds,
            };
            return this.httpService
                .post('recentPurchase', requestBody)
                .pipe(mergeMap((response) => [
                RECENT_PURCHASE_ACTIONS.deleteSuccess({
                    products: action.products,
                }),
            ]), catchError((error) => [
                RECENT_PURCHASE_ACTIONS.deleteFail({
                    error,
                    products: action.products,
                }),
            ]));
        })));
        this.dateIsLessThan = (lapse, type, date) => dayjs().diff(date, type) <= lapse;
    }
    waitFor(signal$) {
        return (source$) => new Observable((observer) => {
            // combineLatest emits the first value only when
            // both source and signal emitted at least once
            combineLatest([source$, signal$.pipe(first())]).subscribe(([v]) => observer.next(v));
        });
    }
}
RecentPurchaseEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RecentPurchaseEffects, deps: [{ token: i1$3.Actions }, { token: RecentPurchaseFacadeService }, { token: i3.PanAppState }, { token: ListHttpClientService }, { token: ListStateService }, { token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
RecentPurchaseEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RecentPurchaseEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RecentPurchaseEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: RecentPurchaseFacadeService }, { type: i3.PanAppState }, { type: ListHttpClientService }, { type: ListStateService }, { type: i1$1.Store }]; } });

class SellerRecentPurchaseEffects {
    constructor(actions$, httpService, store, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        this.panAppState = panAppState;
        this.loadSellerRecentPurchase$ = createEffect(() => this.actions$.pipe(ofType(SELLER_RECENT_PURCHASE_ACTIONS.loadRecentPurchase), withLatestFrom(this.store.select(sellerRecentPurchaseWatermark()), this.panAppState.session$), distinctUntilChanged(), switchMap(([action, watermark, previousSession]) => {
            const params = new HttpParams();
            return this.httpService
                .getItemsWithParams(`recentPurchase/${action.productNumber.toString()}`, params)
                .pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response, session]) => SELLER_RECENT_PURCHASE_ACTIONS.loadRecentPurchaseSuccess({
                recentPurchase: response,
            })), catchError((error) => of(SELLER_RECENT_PURCHASE_ACTIONS.loadRecentPurchaseFail({ error }))));
        })));
    }
}
SellerRecentPurchaseEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerRecentPurchaseEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: i1$1.Store }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
SellerRecentPurchaseEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerRecentPurchaseEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerRecentPurchaseEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: i1$1.Store }, { type: i3.PanAppState }]; } });

class ServiceRequestEffects {
    constructor(actions$, httpService, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.panAppState = panAppState;
        /**
         * Returns actions to populate the store based on successful/error api response
         *
         * @remarks
         * Responsible for making api call to fetch service request data
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(SERVICE_REQUEST_ACTIONS.load), withLatestFrom(this.panAppState.session$), switchMap(([action, previousSession]) => this.httpService.getObject('serviceRequest', null).pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response, session]) => SERVICE_REQUEST_ACTIONS.loadSuccess({ serviceRequest: response })), catchError((error) => of(SERVICE_REQUEST_ACTIONS.loadFail(error)))))));
        this.submitRequestCredit$ = createEffect(() => this.actions$.pipe(ofType(SERVICE_REQUEST_ACTIONS.submitRequestCredit), concatMap((action) => this.httpService
            .post('requestCredit', action.requestCredit)
            .pipe(concatMap((requestCredit) => [
            SERVICE_REQUEST_ACTIONS.submitRequestCreditSuccess({
                requestCredit,
                trackingData: action.trackingData,
                tracking: {
                    tracing: {
                        data: {
                            traceContext: ListTracingConstants.requestCreditContext,
                            isEndOfTrace: true,
                            isStartOfTrace: false,
                            attributes: {
                                ...action.tracking.tracing.data.attributes,
                                event: requestCredit?.desc ===
                                    ListTracingConstants.requestCreditSuccessWithUploadFailureDesc
                                    ? ListTracingConstants.submitRequestCreditEventWithUploadFailureEvent +
                                        'success'
                                    : ListTracingConstants.submitRequestCreditEvent +
                                        ' success',
                                spanName: requestCredit?.desc ===
                                    ListTracingConstants.requestCreditSuccessWithUploadFailureDesc
                                    ? ListTracingConstants.requestCreditSubmitEventsWithUploadFailureSpan +
                                        'Success'
                                    : ListTracingConstants.submitRequestCreditSpan +
                                        ' Success',
                            },
                        },
                        transformFunc: submitRequestCreditTransformer,
                    },
                },
            }),
        ]), catchError((error) => {
            console.log('dispatching submitRequestCreditFailure with error: ', error);
            return [
                SERVICE_REQUEST_ACTIONS.submitRequestCreditFailure({
                    error,
                    tracking: {
                        tracing: {
                            data: {
                                traceContext: ListTracingConstants.requestCreditContext,
                                isEndOfTrace: true,
                                isStartOfTrace: false,
                                attributes: {
                                    ...action.tracking.tracing.data.attributes,
                                    event: ListTracingConstants.submitRequestCreditEvent +
                                        ' failure',
                                    spanName: ListTracingConstants.submitRequestCreditSpan +
                                        ' Failure',
                                },
                            },
                            transformFunc: submitRequestCreditTransformer,
                        },
                    },
                }),
            ];
        })))));
        this.submitPickupRequest$ = createEffect(() => this.actions$.pipe(ofType(SERVICE_REQUEST_ACTIONS.submitPickupRequest), concatMap((action) => this.httpService
            .post('pickupRequest', action.pickupRequest)
            .pipe(concatMap((pickupRequest) => [
            SERVICE_REQUEST_ACTIONS.submitPickupRequestSuccess({
                pickupRequest,
                trackingData: action.trackingData,
                tracking: {
                    tracing: {
                        data: {
                            traceContext: ListTracingConstants.pickupRequestContext,
                            isEndOfTrace: true,
                            isStartOfTrace: false,
                            attributes: {
                                ...action.tracking.tracing.data.attributes,
                                event: pickupRequest?.desc ===
                                    ListTracingConstants.pickupRequestSubmitEventsWithUploadFailureSpan
                                    ? ListTracingConstants.submitPickupRequestEventWithUploadFailureEvent +
                                        'success'
                                    : ListTracingConstants.submitPickupRequestEvent +
                                        ' success',
                                spanName: pickupRequest?.desc ===
                                    ListTracingConstants.pickupRequestSubmitEventsWithUploadFailureSpan
                                    ? ListTracingConstants.pickupRequestSubmitEventsWithUploadFailureSpan +
                                        'Success'
                                    : ListTracingConstants.submitPickupRequestSpan +
                                        ' Success',
                            },
                        },
                        transformFunc: submitPickupRequestTransformer,
                    },
                },
            }),
        ]), catchError((error) => {
            console.log('dispatching submitPickupRequestFailure with error: ', error);
            return [
                SERVICE_REQUEST_ACTIONS.submitPickupRequestFailure({
                    error,
                    tracking: {
                        tracing: {
                            data: {
                                traceContext: ListTracingConstants.pickupRequestContext,
                                isEndOfTrace: true,
                                isStartOfTrace: false,
                                attributes: {
                                    ...action.tracking.tracing.data.attributes,
                                    event: ListTracingConstants.submitPickupRequestEvent +
                                        ' failure',
                                    spanName: ListTracingConstants.submitPickupRequestSpan +
                                        ' Failure',
                                },
                            },
                            transformFunc: submitPickupRequestTransformer,
                        },
                    },
                }),
            ];
        })))));
        this.resubmitServiceRequest$ = createEffect(() => this.actions$.pipe(ofType(SERVICE_REQUEST_ACTIONS.resubmit), concatMap((action) => this.httpService
            .post(ListEndpoints.resubmit + '?primaryKey=' + action.primaryKey, {})
            .pipe(concatMap(() => [
            SERVICE_REQUEST_ACTIONS.resubmitSuccess({
                primaryKey: action.primaryKey,
            }),
        ]), catchError((error) => of(SERVICE_REQUEST_ACTIONS.resubmitFail(error)))))));
        this.submitRecoveryOrder$ = createEffect(() => this.actions$.pipe(ofType(SERVICE_REQUEST_ACTIONS.submitRecoveryOrder), concatMap((action) => this.httpService
            .post(ListEndpoints.recoveryOrder, action.recoveryRequest)
            .pipe(concatMap(() => [
            SERVICE_REQUEST_ACTIONS.submitRecoveryOrderSuccess({
                recoveryRequest: action.recoveryRequest,
            }),
        ]), catchError((error) => of(SERVICE_REQUEST_ACTIONS.submitRecoveryOrderFailure(error)))))));
    }
}
ServiceRequestEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ServiceRequestEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
ServiceRequestEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ServiceRequestEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ServiceRequestEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: i3.PanAppState }]; } });

class MasterListEffects {
    constructor(actions$, httpService, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.panAppState = panAppState;
        /**
         * @remarks
         * Responsible for making api call to fetch master lists
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(MASTER_LIST_ACTIONS.load), withLatestFrom(this.panAppState.session$), switchMap(([action, previousSession]) => this.httpService
            .get(ListEndpoints.masterLists)
            .pipe(withLatestFrom(this.panAppState.customer$, this.panAppState.session$), filter(([response, selectedCustomer, session]) => sessionsHaveNotChanged(previousSession, session)), map(([masterLists, selectedCustomer]) => {
            const masterListStates = this.setMasterListNames(masterLists, selectedCustomer?.restrictToOG);
            return MASTER_LIST_ACTIONS.loadSuccess({
                masterLists: masterListStates,
            });
        }), catchError((error) => of(MASTER_LIST_ACTIONS.loadFail({ error })))))));
    }
    setMasterListNames(masterLists, restrictToOg) {
        const sortedMasterLists = masterLists.sort((a, b) => a?.listName?.toUpperCase() > b?.listName?.toUpperCase() ? 1 : -1);
        const defaultNameCount = sortedMasterLists.filter((masterList) => Number(masterList?.customerListName) === 0).length;
        let defaultNameIndex = 1;
        let newName;
        const renamedMasterLists = sortedMasterLists.map((masterList) => {
            newName = masterList?.listName;
            // customerListName indicates whether or not to show a default name for the list
            if (masterList?.customerListName === 0) {
                if (defaultNameCount > 1) {
                    newName = `Master List (${defaultNameIndex})`;
                    defaultNameIndex++;
                }
                else {
                    newName = 'Master List';
                }
            }
            newName =
                restrictToOg === 'M' ? `${newName} (R-ML)` : `${newName} (ML)`;
            return {
                ...masterList,
                listName: newName,
            };
        });
        return renamedMasterLists;
    }
}
MasterListEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
MasterListEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: i3.PanAppState }]; } });

class MasterListGroupEffects {
    constructor(actions$, httpService, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.panAppState = panAppState;
        /**
         * @remarks
         * Responsible for making api call to fetch master list groups
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(MASTER_LIST_GROUP_ACTIONS.load), withLatestFrom(this.panAppState.session$), switchMap(([action, previousSession]) => this.httpService
            .get(ListEndpoints.masterListGroups)
            .pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response, session]) => MASTER_LIST_GROUP_ACTIONS.loadSuccess({
            masterListGroups: response,
        })), catchError((error) => of(MASTER_LIST_GROUP_ACTIONS.loadFail({ error })))))));
    }
}
MasterListGroupEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListGroupEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
MasterListGroupEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListGroupEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListGroupEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: i3.PanAppState }]; } });

class MasterListItemEffects {
    constructor(actions$, httpService, store, masterListFacadeService, masterListGroupFacadeService, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        this.masterListFacadeService = masterListFacadeService;
        this.masterListGroupFacadeService = masterListGroupFacadeService;
        this.panAppState = panAppState;
        /**
         * @remarks
         * Responsible for making api call to fetch master list groups
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(MASTER_LIST_ITEM_ACTIONS.load), withLatestFrom(this.panAppState.session$), switchMap(([action, previousSession]) => this.httpService
            .get(ListEndpoints.masterListItems)
            .pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response, session]) => MASTER_LIST_ITEM_ACTIONS.loadSuccess({
            masterListItems: response,
        })), catchError((error) => of(MASTER_LIST_ITEM_ACTIONS.loadFail({ error })))))));
        /**
         * @Remarks
         * This Effect will fire after the itemKeyProduct dictionary is established.
         * The primary products will be counted based on the new dictionary and appended to each list.
         */
        this.calculatePrimaryCount$ = createEffect(() => this.actions$.pipe(ofType(MASTER_LIST_ITEM_ACTIONS.updateItemKeyProductKeysDict), tap((action) => {
            combineLatest([
                this.masterListFacadeService.allMasterLists$,
                this.masterListGroupFacadeService.allMasterListGroup$,
            ])
                .pipe(filter(([lists, listGroups]) => Object.keys(lists).length > 0 &&
                Object.keys(listGroups).length > 0), tap(([lists, listGroups]) => {
                this.calcMasterPrimaryItems(lists, listGroups, action.itemKeyProductKeysDict);
            }), take(1), finalize(() => { }))
                .subscribe();
        })), { dispatch: false });
        /**
         * @Remarks
         * This effect is fired when an update in the list item state takes place.
         * We calculate a dictionary to provide more information about certain products on lists.
         * Key is product number and value is of type ListItemProduct.
         */
        this.calculateProductMap$ = createEffect(() => this.actions$.pipe(ofType(MASTER_LIST_ITEM_ACTIONS.loadSuccess), withLatestFrom(this.store.select(selectMasterListItemState)), tap(([action, masterListItemState]) => {
            combineLatest([this.masterListFacadeService.masterListState$])
                .pipe(filter(([masterListState]) => masterListState.loaded), tap(([masterListState]) => {
                const productMap = {};
                masterListItemState.ids?.forEach((id) => {
                    const masterListItem = masterListItemState.entities[id];
                    if (masterListItem) {
                        const masterListName = masterListState.entities[masterListItem.listId]?.listName;
                        if (masterListName) {
                            const existingProductInMap = productMap[masterListItem.productNumber];
                            let listItemProduct;
                            if (existingProductInMap) {
                                const updatedMasterLists = existingProductInMap.masterLists ?? [];
                                if (!updatedMasterLists.includes(masterListName)) {
                                    updatedMasterLists.push(masterListName);
                                }
                                listItemProduct = {
                                    ...existingProductInMap,
                                    masterLists: updatedMasterLists,
                                };
                            }
                            else {
                                listItemProduct = {
                                    masterLists: [masterListName],
                                };
                            }
                            productMap[masterListItem.productNumber] = listItemProduct;
                        }
                    }
                });
                this.store.dispatch(MASTER_LIST_ITEM_ACTIONS.saveProductMap({
                    productsMap: productMap,
                }));
            }), take(1), finalize(() => { }))
                .subscribe();
        })), { dispatch: false });
    }
    calcMasterPrimaryItems(lists, listGroups, itemKeyProductKeysDict) {
        const updatedLists = [];
        lists.forEach((list) => {
            let count = 0;
            const listToUpdate = list;
            list.listGroupIds.forEach((groupId) => {
                const value = listGroups.find((item) => item.listGroupId === groupId);
                if (value && value.listItemIds) {
                    value.listItemIds.forEach((listItemId) => {
                        const keyForLookup = value.listId + '-' + value.listGroupId + '-' + listItemId;
                        const keyLookUpValue = itemKeyProductKeysDict[keyForLookup];
                        if (keyLookUpValue) {
                            count++;
                        }
                    });
                }
            });
            updatedLists.push({
                ...listToUpdate,
                primaryCount: count,
            });
        });
        this.store.dispatch(MASTER_LIST_ACTIONS.updateMany({ masterLists: updatedLists }));
    }
}
MasterListItemEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListItemEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: i1$1.Store }, { token: MasterListFacadeService }, { token: MasterListGroupFacadeService }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
MasterListItemEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListItemEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MasterListItemEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: i1$1.Store }, { type: MasterListFacadeService }, { type: MasterListGroupFacadeService }, { type: i3.PanAppState }]; } });

class OrderGuideEffects {
    constructor(actions$, httpService, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.panAppState = panAppState;
        /**
         *API call for OrderGuide Headers
         */
        this.loadOrderGuide$ = createEffect(() => this.actions$.pipe(ofType(ORDER_GUIDE_ACTIONS.load), withLatestFrom(this.panAppState.session$), switchMap(([action, previousSession]) => this.httpService.get(ListEndpoints.orderGuideHeader).pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response, session]) => ORDER_GUIDE_ACTIONS.loadSuccess({
            orderGuides: response,
        })), catchError((error) => of(ORDER_GUIDE_ACTIONS.loadFail({ error })))))));
    }
}
OrderGuideEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderGuideEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
OrderGuideEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderGuideEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderGuideEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: i3.PanAppState }]; } });

class OrderGuideGroupEffects {
    constructor(actions$, httpService, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.panAppState = panAppState;
        //API call for OrderGuide Groups
        this.loadOrderGuideGroup$ = createEffect(() => this.actions$.pipe(ofType(ORDER_GUIDE_GROUP_ACTIONS.load), withLatestFrom(this.panAppState.session$), switchMap(([action, previousSession]) => this.httpService.get(ListEndpoints.orderGuideGroups).pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response, session]) => ORDER_GUIDE_GROUP_ACTIONS.loadSuccess({
            orderGuideGroups: response,
        })), catchError((error) => of(ORDER_GUIDE_GROUP_ACTIONS.loadFail({ error })))))));
    }
}
OrderGuideGroupEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderGuideGroupEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
OrderGuideGroupEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderGuideGroupEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderGuideGroupEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: i3.PanAppState }]; } });

class OrderGuideItemEffects {
    constructor(actions$, httpService, store, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        this.panAppState = panAppState;
        //API call for OrderGuideItems
        this.loadOrderGuideItem$ = createEffect(() => this.actions$.pipe(ofType(ORDER_GUIDE_ITEM_ACTIONS.load), withLatestFrom(this.panAppState.session$), switchMap(([action, previousSession]) => this.httpService.get(ListEndpoints.orderGuideItems).pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response, session]) => ORDER_GUIDE_ITEM_ACTIONS.loadSuccess({ orderGuideItems: response })), catchError((error) => of(ORDER_GUIDE_ITEM_ACTIONS.loadFail({ error })))))));
        // Fires after order guide items get loaded into state. Creates a dictionary with key being
        // product number and value being of type ListItemProduct
        this.orderGuideItemsLoadSuccess$ = createEffect(() => this.actions$.pipe(ofType(ORDER_GUIDE_ITEM_ACTIONS.loadSuccess), tap((action) => {
            const newProductMap = {};
            // Loop through order guide items and modify product map with order guide data
            action.orderGuideItems?.forEach((orderGuideItem) => {
                const orderGuideName = ListConstants.orderGuide + ' #' + orderGuideItem.listKey.listId;
                let existingItemInProductMap;
                if (orderGuideItem.productNumber) {
                    existingItemInProductMap =
                        newProductMap[orderGuideItem.productNumber];
                }
                if (existingItemInProductMap) {
                    if (!existingItemInProductMap.lists.includes(orderGuideName)) {
                        // Add list name to array and set isOnOrderGuide and customerProductNumber
                        const newLists = existingItemInProductMap.lists.slice();
                        newLists.push(orderGuideName);
                        const newItemInProductMap = {
                            ...existingItemInProductMap,
                            isOnOrderGuide: true,
                            lists: newLists,
                            customerProductNumber: orderGuideItem.customerProductNumber,
                        };
                        // Save value back to map
                        newProductMap[orderGuideItem.productNumber] = newItemInProductMap;
                    }
                }
                else {
                    // Initialize lists array with og name and set isOnOrderGuide, isOnList and customerProductNumber
                    const newItemInProductMap = {
                        isOnOrderGuide: true,
                        isOnList: false,
                        lists: [orderGuideName],
                        customerProductNumber: orderGuideItem.customerProductNumber,
                    };
                    // Save value back to map
                    if (orderGuideItem.productNumber) {
                        newProductMap[orderGuideItem.productNumber] = newItemInProductMap;
                    }
                }
            });
            // Save map to state
            this.store.dispatch(ORDER_GUIDE_ITEM_ACTIONS.saveProductMap({
                productsMap: newProductMap,
            }));
        })), { dispatch: false });
    }
    selectListItemProductMap() {
        return this.store.select(listItemProductMap());
    }
}
OrderGuideItemEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderGuideItemEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: i1$1.Store }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
OrderGuideItemEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderGuideItemEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderGuideItemEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: i1$1.Store }, { type: i3.PanAppState }]; } });

class SupportCenterEffects {
    constructor(actions$, httpService, store) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        /**
         * Returns actions to populate the store based on successful/error api response
         *
         * @remarks
         * Responsible for making api call to fetch support center content
         * assigned to a particular user/customer association
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(SUPPORT_CENTER_ACTIONS.load), switchMap((action) => this.httpService
            .getWithRetry(ListEndpoints.supportCenterContent)
            .pipe(switchMap((response) => [
            SUPPORT_CENTER_ACTIONS.loadSuccess({
                supportCenterContent: response,
            }),
        ]), catchError((error) => of(SUPPORT_CENTER_ACTIONS.loadFail({ error })))))));
        this.submitSupportRequest$ = createEffect(() => this.actions$.pipe(ofType(SUPPORT_CENTER_ACTIONS.submitSupportRequest), concatMap((action) => this.httpService
            .post('supportRequest', action.supportRequest)
            .pipe(concatMap((supportRequest) => [
            SUPPORT_CENTER_ACTIONS.submitSupportRequestSuccess({
                supportRequest,
            }),
        ]), catchError((error) => of(SUPPORT_CENTER_ACTIONS.submitSupportRequestFailure({ error })))))));
    }
}
SupportCenterEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SupportCenterEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
SupportCenterEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SupportCenterEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SupportCenterEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: i1$1.Store }]; } });

class ListUpdatesEffects {
    constructor(actions$, httpService, store) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        this.load$ = createEffect(() => this.actions$.pipe(ofType(LIST_UPDATES_ACTIONS.load), switchMap((action) => this.httpService
            .getObject(ListEndpoints.listUpdates)
            .pipe(switchMap((response) => [
            LIST_UPDATES_ACTIONS.loadSuccess({
                listUpdates: response.forcedUpdates,
            }),
        ]), catchError((error) => of(LIST_UPDATES_ACTIONS.loadFail({ error })))))));
    }
}
ListUpdatesEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListUpdatesEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }, { token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ListUpdatesEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListUpdatesEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ListUpdatesEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }, { type: i1$1.Store }]; } });

class AccessibleListEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        /**
         * Returns actions to populate the store based on successful/error api response
         *
         * @remarks
         * Responsible for making api call to fetch list headers
         * assigned to a particular user assoociation
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(ACCESSIBLE_LIST_ACTIONS.load), concatMap((action) => this.httpService
            .post(ListEndpoints.accessible, {
            custDivDeptInfo: action.custDivDeptInfo,
        })
            .pipe(concatMap((response) => [
            ACCESSIBLE_LIST_ACTIONS.loadSuccess({
                lists: response,
            }),
        ]), catchError((error) => of(ACCESSIBLE_LIST_ACTIONS.loadFailure({ error })))))));
        this.copyToMultipleLists$ = createEffect(() => this.actions$.pipe(ofType(ACCESSIBLE_LIST_ACTIONS.copyToMultiple), concatMap((action) => this.httpService
            .post(ListEndpoints.copyToMultipleLists, {
            targetListKeys: action.targetListKeys,
            atGroupLocation: action.atGroupLocation,
            productNumbers: action.productNumbers,
        })
            .pipe(concatMap((response) => [
            ACCESSIBLE_LIST_ACTIONS.copyToMultipleSuccess({
                response,
                lists: action.targetListKeys.length,
                products: action.productNumbers.length,
            }),
            LIST_ACTIONS.updateMany({ lists: response.listChanges }),
            LIST_ITEM_ACTIONS.update({ listItems: response.itemChanges }),
            LIST_GROUP_ACTIONS.updateMany({ groups: response.groupChanges }),
        ]), catchError((error) => of(ACCESSIBLE_LIST_ACTIONS.copyToMultipleFailure({ error })))))));
    }
}
AccessibleListEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AccessibleListEffects, deps: [{ token: i1$3.Actions }, { token: ListHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
AccessibleListEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AccessibleListEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AccessibleListEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ListHttpClientService }]; } });

/* eslint-disable prefer-arrow/prefer-arrow-functions */
class NgRxListModule {
    constructor(ngRxListLibConfig, store, panAppState, webSocketService) {
        this.ngRxListLibConfig = ngRxListLibConfig;
        this.store = store;
        this.panAppState = panAppState;
        this.webSocketService = webSocketService;
    }
    static forRoot(ngRxListLibConfig, store, panAppState, webSocketService) {
        return {
            ngModule: NgRxListModule,
            providers: [
                {
                    provide: NGRX_LIST_LIB_CONFIG,
                    useValue: ngRxListLibConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeWebSocket,
                    deps: [ListClientWebSocketService],
                    multi: true,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeListState,
                    deps: [ListStateService],
                    multi: true,
                },
            ],
        };
    }
}
NgRxListModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxListModule, deps: [{ token: NGRX_LIST_LIB_CONFIG }, { token: i1$1.Store }, { token: i3.PanAppState }, { token: i1$4.WebSocketService, optional: true }], target: i0.ɵɵFactoryTarget.NgModule });
NgRxListModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: NgRxListModule, imports: [i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$1.StoreFeatureModule, i1$3.EffectsFeatureModule] });
NgRxListModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxListModule, providers: [
        ListHttpClientService,
        ListEffects,
        ListGroupEffects,
        ListItemEffects,
        ListStateService,
        SellerRecentPurchaseFacadeService,
        ServiceRequestEffects,
        MasterListEffects,
        MasterListGroupEffects,
        MasterListItemEffects,
        OrderGuideEffects,
        OrderGuideGroupEffects,
        OrderGuideItemEffects,
        SupportCenterEffects,
        AccessibleListEffects,
        ListViewService,
    ], imports: [StoreModule.forFeature(LIST_KEY, listReducer),
        StoreModule.forFeature(LIST_GROUP_KEY, listGroupReducer),
        StoreModule.forFeature(LIST_ITEM_KEY, listItemReducer),
        StoreModule.forFeature(ORDER_GUIDE_KEY, orderGuideReducer),
        StoreModule.forFeature(ORDER_GUIDE_GROUP_KEY, orderGuideGroupReducer),
        StoreModule.forFeature(ORDER_GUIDE_ITEM_KEY, orderGuideItemReducer),
        StoreModule.forFeature(RECENT_PURCHASE_KEY, recentPurchaseReducer),
        StoreModule.forFeature(SELLER_RECENT_PURCHASE_KEY, sellerRecentPurchaseReducer),
        StoreModule.forFeature(SERVICE_REQUEST_KEY, serviceRequestReducer),
        StoreModule.forFeature(MASTER_LIST_KEY, masterListReducer),
        StoreModule.forFeature(MASTER_LIST_GROUP_KEY, masterListGroupReducer),
        StoreModule.forFeature(MASTER_LIST_ITEM_KEY, masterListItemReducer),
        StoreModule.forFeature(SUPPORT_CENTER_KEY, supportCenterReducer),
        StoreModule.forFeature(LIST_UPDATES_KEY, listUpdatesReducer),
        StoreModule.forFeature(ACCESSIBLE_LIST_KEY, accessibleListReducer),
        StoreModule.forFeature(LIST_VIEW_KEY, listViewReducer),
        EffectsModule.forFeature([
            ListEffects,
            ListGroupEffects,
            ListItemEffects,
            RecentPurchaseEffects,
            SellerRecentPurchaseEffects,
            ServiceRequestEffects,
            MasterListEffects,
            MasterListGroupEffects,
            MasterListItemEffects,
            OrderGuideEffects,
            OrderGuideGroupEffects,
            OrderGuideItemEffects,
            SupportCenterEffects,
            ListUpdatesEffects,
            AccessibleListEffects,
        ])] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxListModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        StoreModule.forFeature(LIST_KEY, listReducer),
                        StoreModule.forFeature(LIST_GROUP_KEY, listGroupReducer),
                        StoreModule.forFeature(LIST_ITEM_KEY, listItemReducer),
                        StoreModule.forFeature(ORDER_GUIDE_KEY, orderGuideReducer),
                        StoreModule.forFeature(ORDER_GUIDE_GROUP_KEY, orderGuideGroupReducer),
                        StoreModule.forFeature(ORDER_GUIDE_ITEM_KEY, orderGuideItemReducer),
                        StoreModule.forFeature(RECENT_PURCHASE_KEY, recentPurchaseReducer),
                        StoreModule.forFeature(SELLER_RECENT_PURCHASE_KEY, sellerRecentPurchaseReducer),
                        StoreModule.forFeature(SERVICE_REQUEST_KEY, serviceRequestReducer),
                        StoreModule.forFeature(MASTER_LIST_KEY, masterListReducer),
                        StoreModule.forFeature(MASTER_LIST_GROUP_KEY, masterListGroupReducer),
                        StoreModule.forFeature(MASTER_LIST_ITEM_KEY, masterListItemReducer),
                        StoreModule.forFeature(SUPPORT_CENTER_KEY, supportCenterReducer),
                        StoreModule.forFeature(LIST_UPDATES_KEY, listUpdatesReducer),
                        StoreModule.forFeature(ACCESSIBLE_LIST_KEY, accessibleListReducer),
                        StoreModule.forFeature(LIST_VIEW_KEY, listViewReducer),
                        EffectsModule.forFeature([
                            ListEffects,
                            ListGroupEffects,
                            ListItemEffects,
                            RecentPurchaseEffects,
                            SellerRecentPurchaseEffects,
                            ServiceRequestEffects,
                            MasterListEffects,
                            MasterListGroupEffects,
                            MasterListItemEffects,
                            OrderGuideEffects,
                            OrderGuideGroupEffects,
                            OrderGuideItemEffects,
                            SupportCenterEffects,
                            ListUpdatesEffects,
                            AccessibleListEffects,
                        ]),
                    ],
                    providers: [
                        ListHttpClientService,
                        ListEffects,
                        ListGroupEffects,
                        ListItemEffects,
                        ListStateService,
                        SellerRecentPurchaseFacadeService,
                        ServiceRequestEffects,
                        MasterListEffects,
                        MasterListGroupEffects,
                        MasterListItemEffects,
                        OrderGuideEffects,
                        OrderGuideGroupEffects,
                        OrderGuideItemEffects,
                        SupportCenterEffects,
                        AccessibleListEffects,
                        ListViewService,
                    ],
                    exports: [],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_LIST_LIB_CONFIG]
                }] }, { type: i1$1.Store }, { type: i3.PanAppState }, { type: i1$4.WebSocketService, decorators: [{
                    type: Optional
                }] }]; } });
function initializeListState(listState) {
    const state = () => listState.initialize();
    return state;
}
function initializeWebSocket(webSocketService) {
    const websocket = () => webSocketService.initialize();
    return websocket;
}

/*
 * Public API Surface of ngrx-list
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ACCESSIBLE_LIST_ACTIONS, ACCESSIBLE_LIST_KEY, AnalyticMessages, LIST_ACTIONS, LIST_DOMAIN_NAME, LIST_GROUP_ACTIONS, LIST_GROUP_KEY, LIST_ITEM_ACTIONS, LIST_ITEM_KEY, LIST_KEY, LIST_STORAGE_KEYS, LIST_UPDATES_ACTIONS, LIST_UPDATES_KEY, LIST_VIEW_ACTIONS, LIST_VIEW_KEY, ListAnalyticsService, ListConstants, ListFacadeService, ListGroupFacadeService, ListItemFacadeService, ListState, ListStateService, ListTracingConstants, ListType, ListViewMode, ListViewService, MASTER_LIST_ACTIONS, MASTER_LIST_GROUP_ACTIONS, MASTER_LIST_GROUP_KEY, MASTER_LIST_ITEM_ACTIONS, MASTER_LIST_ITEM_KEY, MASTER_LIST_KEY, MasterListFacadeService, MasterListGroupFacadeService, MasterListItemFacadeService, NgRxListModule, ORDER_GUIDE_ACTIONS, ORDER_GUIDE_GROUP_ACTIONS, ORDER_GUIDE_GROUP_KEY, ORDER_GUIDE_ITEM_ACTIONS, ORDER_GUIDE_ITEM_KEY, ORDER_GUIDE_KEY, PmeFilterOptions, PmeSortOptions, RECENT_PURCHASE_ACTIONS, RECENT_PURCHASE_KEY, RecentPurchaseFacadeService, SELLER_RECENT_PURCHASE_ACTIONS, SELLER_RECENT_PURCHASE_KEY, SERVICE_REQUEST_ACTIONS, SERVICE_REQUEST_KEY, SUPPORT_CENTER_ACTIONS, SUPPORT_CENTER_KEY, SellerRecentPurchaseFacadeService, ServiceRequestAnalyticsService, ServiceRequestFacadeService, SousStatusLookUp, SupportCenterConstants, SupportCenterSortOptions, USER_STATE_KEY, accessibleListAdapter, accessibleListReducer, addProductsToListsTransformer, addToListTransformer, aiBuilderModalTransformer, buttonClickTransformer, copyItemsTransformer, copyListSuccessTransformer, createFailureTrackingObject, createGroupTransformer, createListTransformer, createSuccessTrackingObject, deleteItemsTransformer, deleteListGroupTransformer, deleteListTransformer, displayListDetailsTransformer, downloadPrintTransformer, editGroupNameTransformer, editGroupSequenceTransformer, editListName, editListNameTransformer, editListTypeTransformer, filterListDetailsTransformer, hasListUpdates, initialCustomersPerList, initialImportErrors, initialListOfListsSort, initialSupportCenterSort, listAdapter, listDetailPageLoad, listGroupAdapter, listGroupReducer, listGroupsLoaded, listItemAdapter, listItemProductMap, listItemReducer, listItemsLoaded, listManagementPageLoad, listOfListsHeaders, listPageLoad, listReducer, listUpdates, listUpdatesLoaded, listUpdatesReducer, listViewReducer, listsLoaded, mapSousStatusToNumber, masterListGroupReducer, masterListGroupsLoaded, masterListItemProductMap, masterListItemReducer, masterListItemsLoaded, masterListReducer, masterListsLoaded, masterListsPageLoadedTransformer, moveItemsToAnotherGroupTransformer, moveItemsWithinGroupTransformer, orderGuideAdapter, orderGuideGroupAdapter, orderGuideGroupReducer, orderGuideGroupsLoaded, orderGuideItemAdapter, orderGuideItemReducer, orderGuideItemsLoaded, orderGuideReducer, orderGuidesLoaded, pickUpRequestTransformer, pickupRequestLookup, recentPurchaseAdapter, recentPurchaseLoaded, recentPurchaseReducer, recoveryOrderLookup, replaceItemOnAllListsTransformer, replaceItemOnListTransformer, requestCreditLookup, searchInListTransformer, searchSubsTransformer, selectAllAccessibleListState, selectAllSupportCenter, selectCustomersWithListName, selectFavoritesList, selectIsAccessibleListStateLoaded, selectIsRecentPurchaseListSelected, selectItemKeyProductsDictionary, selectListGroupsState, selectListImportErrors, selectListItemsState, selectListSearchSortFilterOptions, selectListUpdateState, selectListUpdatesByItemIdMap, selectListViewState, selectListsState, selectListsWithUpdatedCounts, selectMassDeleteLoading, selectMasterListGroupState, selectMasterListItemState, selectMasterListPrimaryCountsMap, selectMasterListSearchSortFilterOptions, selectMasterListsState, selectOrderGuideGroupState, selectOrderGuideItemState, selectOrderGuideState, selectPmeSortsAndFilters, selectRecentPurchaseSearchSortFilterOptions, selectRecentPurchaseState, selectSelectedListId, selectSelectedListItemKey, selectSelectedListType, selectSelectedListViewMode, selectSelectedListViewModel, selectSellerRecentPurchaseState, selectServiceRequestState, selectSupportCenterById, selectSupportCenterEntities, selectSupportCenterFilter, selectSupportCenterIds, selectSupportCenterItemsAndCountsByFilter, selectSupportCenterSort, selectSupportCenterState, selectTotalSupportCenter, sellerRecentPurchaseAdapter, sellerRecentPurchaseLoaded, sellerRecentPurchaseReducer, serviceRequest, serviceRequestReducer, submitPickupRequestTransformer, submitRequestCreditTransformer, supportCenterHeaders, supportCenterPageLoadedTransformer, supportCenterReducer, supportCenterResubmitRequestTransformer, supportRequestLookup, trackChangeQuantity, trackFridayToggle, trackPageLoad, trackProductClickData, trackQuickViewClick, trackQuickViewClosed, trackRequestCreditConfirmPageLoad, trackRequestCreditStart, trackSortFilterPricingTransformer, trackSubmitSupportRequestTransformer };
